import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Play, Pause, Info, BookOpen, AlertTriangle, Scale, Users, ClipboardCheck, Euro, GraduationCap, FileText, Target } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// ===================== MODULE-SPECIFIC VISUAL CONTENT =====================

const moduleVisualContent = {
  // MODUL 1: Wundarten - Behalten wir die medizinischen Wundbilder
  1: {
    title: "Wundarten erkennen",
    icon: "🔬",
    description: "Lernen Sie die wichtigsten chronischen Wundarten zu erkennen und zu klassifizieren.",
    slides: [
      {
        id: "dekubitus_grad_1",
        title: "Dekubitus Grad 1",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/4b3c9df709049144d0c0a31eb806b0730ec035f86ac7066c20ff52ffad297924.png",
        explanation: "Hier sehen Sie einen Dekubitus Grad 1. Das Hauptmerkmal ist eine nicht-wegdrückbare Rötung auf intakter Haut, typischerweise über einem Knochenvorsprung wie dem Kreuzbein oder der Ferse. Die Haut ist noch nicht verletzt, aber die Rötung bleibt bestehen, auch wenn der Druck entfernt wird. Wichtig: In diesem Stadium ist die Wunde noch reversibel!",
        keyPoints: [
          "Nicht-wegdrückbare Rötung auf intakter Haut",
          "Über Knochenvorsprüngen lokalisiert",
          "Vollständig reversibel bei sofortiger Intervention",
          "Umlagerungsintervall sofort auf 2 Stunden reduzieren"
        ]
      },
      {
        id: "dekubitus_grad_2",
        title: "Dekubitus Grad 2",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/fdab483027453448fe56600cbde5a48c49ba82959e92835bdaa8b3b29ab399fb.png",
        explanation: "Dies ist ein Dekubitus Grad 2. Hier sehen Sie einen Teilverlust der Haut mit freiliegender Dermis. Das Wundbett ist rosa bis rot und feucht. Wichtig: Es kann auch als intakte oder rupturierte Blase erscheinen! Die Heilungschancen sind bei korrekter Behandlung noch sehr gut.",
        keyPoints: [
          "Teilverlust der Haut - Epidermis und Teile der Dermis betroffen",
          "Rosa bis rotes, feuchtes Wundbett",
          "Kann als intakte oder geplatzte Blase erscheinen",
          "Feuchte Wundbehandlung empfohlen (z.B. Hydrokolloid)"
        ]
      },
      {
        id: "dekubitus_grad_3",
        title: "Dekubitus Grad 3",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/7873db8e64211aceeee9baca92170e8bb3fd527055ac2c974331e6dae257083f.png",
        explanation: "Bei Grad 3 sehen wir einen Vollverlust der Haut. Das subkutane Fettgewebe ist sichtbar, aber Knochen, Sehnen und Muskeln liegen noch nicht frei. Achten Sie auf die Wundränder - sie können unterminiert sein!",
        keyPoints: [
          "Vollverlust der Haut - alle Hautschichten betroffen",
          "Subkutanes Fettgewebe kann sichtbar sein",
          "Belag möglich, aber Tiefe erkennbar",
          "Oft unterminierte Wundränder - immer sondieren!"
        ]
      },
      {
        id: "dekubitus_grad_4",
        title: "Dekubitus Grad 4",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/52a8341adfa7d6263e948cf7b1465d9f7cfa5f4be03c4a343ac97543c9ca2d26.png",
        explanation: "Grad 4 ist die schwerste Form des Dekubitus. Hier liegt ein vollständiger Gewebeverlust vor mit freiliegenden Knochen, Sehnen oder Muskeln. Diese Wunden erfordern interdisziplinäre Zusammenarbeit und oft chirurgische Intervention!",
        keyPoints: [
          "Vollständiger Gewebeverlust - tiefste Strukturen betroffen",
          "Knochen, Sehnen oder Muskeln können freiliegen",
          "Oft Nekrose und Belag vorhanden",
          "Chirurgisches Débridement oft erforderlich"
        ]
      },
      {
        id: "ulcus_venosum",
        title: "Ulcus cruris venosum",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/c9af3962029ce4c0b2ed5e01180043f3e4e0224fb4d9994edba9108998b1b053.png",
        explanation: "Das venöse Unterschenkelgeschwür ist die häufigste Form des Ulcus cruris. Typische Merkmale sind: unregelmäßige Wundränder, flaches Wundbett, Lokalisation meist am Innenknöchel. Kompressionstherapie ist das A und O!",
        keyPoints: [
          "Häufigste Ursache: Chronische venöse Insuffizienz",
          "Typische Lokalisation: Innenknöchel",
          "Umgebungshaut: Braune Pigmentierung, Stauungsekzem",
          "Therapie: Kompressionstherapie ist essentiell!"
        ]
      },
      {
        id: "diabetischer_fuss",
        title: "Diabetisches Fußulkus",
        image: "https://static.prod-images.emergentagent.com/jobs/3744b2c0-dfed-4136-b75f-3aa3d3db9a94/images/d3c1a9697fb771941072aaa1b598abb3b780e3d192cf942b3a171efab0ab808b.png",
        explanation: "Das diabetische Fußulkus entsteht durch Neuropathie und Angiopathie. Beachten Sie den Kallus am Wundrand - ein Warnsignal! Patienten spüren oft keine Schmerzen, was die Wunde besonders gefährlich macht.",
        keyPoints: [
          "Entstehung: Neuropathie + Angiopathie + Druck",
          "Lokalisation: Druckpunkte - Großzehenballen, Ferse",
          "Warnsignal: Kallus am Wundrand",
          "Therapie: Druckentlastung + Blutzuckereinstellung"
        ]
      }
    ]
  },

  // MODUL 2: Rechtliche Grundlagen und Haftung
  2: {
    title: "Rechtliche Grundlagen",
    icon: "⚖️",
    description: "Die wichtigsten rechtlichen Aspekte der Wundversorgung für Leitungskräfte.",
    slides: [
      {
        id: "recht_grundlagen",
        title: "Gesetzliche Grundlagen",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/51f32cf221af80c83b3c221b3295e4c82d8bfa1fa46054770b269f94150edb0b.png",
        explanation: "Hier sehen Sie die wichtigsten rechtlichen Grundlagen für das Wundmanagement: Das Bürgerliche Gesetzbuch (BGB) mit den §§ 630a-h zum Behandlungsvertrag, die Waage der Gerechtigkeit symbolisiert die Abwägung zwischen Patientenrechten und Pflichten der Einrichtung. Als Leitungskraft müssen Sie diese Paragraphen kennen!",
        keyPoints: [
          "§ 630a BGB: Behandlungsvertrag - Ihre vertragliche Grundlage",
          "§ 630f BGB: Dokumentationspflicht - Was nicht dokumentiert ist, hat nicht stattgefunden!",
          "§ 630h BGB: Beweislastumkehr bei Dokumentationsmängeln",
          "10 Jahre Aufbewahrungspflicht für alle Dokumente"
        ]
      },
      {
        id: "recht_haftung",
        title: "Haftungsrisiken verstehen",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/51f32cf221af80c83b3c221b3295e4c82d8bfa1fa46054770b269f94150edb0b.png",
        explanation: "Die Waage und der Richterhammer zeigen: Bei Fehlern in der Wundversorgung drohen Konsequenzen! Die häufigsten Haftungsfälle entstehen durch: mangelhafte Dokumentation, verspätete Reaktion auf Wundverschlechterung, und fehlende Expertenhinzuziehung. Als Leitungskraft tragen Sie die Organisationsverantwortung!",
        keyPoints: [
          "Organisationsverschulden: Sie haften für fehlende Standards",
          "Übernahmeverschulden: Nur qualifiziertes Personal einsetzen",
          "Anweisungsverschulden: Klare Anweisungen dokumentieren",
          "Überwachungsverschulden: Regelmäßige Kontrollen durchführen"
        ]
      },
      {
        id: "recht_dokumentation",
        title: "Rechtssichere Dokumentation",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/51f32cf221af80c83b3c221b3295e4c82d8bfa1fa46054770b269f94150edb0b.png",
        explanation: "Die Patientenakte und das Stethoskop symbolisieren die Verbindung von Medizin und Recht. Eine rechtssichere Dokumentation muss sein: zeitnah, vollständig, nachvollziehbar und fälschungssicher. Jede Maßnahme, jede Beobachtung, jede Abweichung muss dokumentiert werden!",
        keyPoints: [
          "Zeitnah: Dokumentation unmittelbar nach der Maßnahme",
          "Vollständig: Wer, Was, Wann, Wie, Warum",
          "Nachvollziehbar: Für Dritte verständlich",
          "Fälschungssicher: Keine nachträglichen Änderungen ohne Kenntlichmachung"
        ]
      }
    ]
  },

  // MODUL 3: Verantwortlichkeiten und Delegation
  3: {
    title: "Team & Delegation",
    icon: "👥",
    description: "Aufbau effektiver Wundmanagement-Strukturen und klare Verantwortlichkeiten.",
    slides: [
      {
        id: "team_struktur",
        title: "Teamstruktur im Wundmanagement",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/d9fceff5a9150c17f34552ed5083967cbc4e82a3c256a6f69d97d809e60400a0.png",
        explanation: "Hier sehen Sie ein professionelles Wundmanagement-Team in Aktion. Die Hierarchie ist klar: Oben die Leitungskraft mit Gesamtverantwortung, darunter die Wundexperten als Fachspezialisten, und das Pflegeteam für die tägliche Versorgung. Jede Ebene hat klare Aufgaben und Kompetenzen!",
        keyPoints: [
          "Leitungskraft: Gesamtverantwortung und Ressourcenmanagement",
          "Wundexperte: Fachliche Beratung und komplexe Fälle",
          "Pflegefachkraft: Tägliche Wundversorgung nach Standard",
          "Klare Eskalationswege definieren!"
        ]
      },
      {
        id: "raci_modell",
        title: "Das RACI-Modell",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/d9fceff5a9150c17f34552ed5083967cbc4e82a3c256a6f69d97d809e60400a0.png",
        explanation: "Die RACI-Matrix auf dem Bild zeigt, wie Sie Verantwortlichkeiten klar zuordnen: R = Responsible (führt aus), A = Accountable (ist verantwortlich), C = Consulted (wird gefragt), I = Informed (wird informiert). Für jede Aufgabe im Wundmanagement sollte es genau eine Person mit 'A' geben!",
        keyPoints: [
          "R - Responsible: Wer führt die Aufgabe aus?",
          "A - Accountable: Wer trägt die Endergebnisverantwortung?",
          "C - Consulted: Wessen Expertise wird benötigt?",
          "I - Informed: Wer muss über das Ergebnis informiert werden?"
        ]
      },
      {
        id: "delegation_regeln",
        title: "Sichere Delegation",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/d9fceff5a9150c17f34552ed5083967cbc4e82a3c256a6f69d97d809e60400a0.png",
        explanation: "Das Team-Meeting auf dem Bild zeigt den Kern erfolgreicher Delegation: Kommunikation! Die 5 Regeln der sicheren Delegation sind: richtige Aufgabe, richtige Person, richtige Anweisung, richtige Überwachung, richtige Rückmeldung. Delegation entbindet Sie nie von der Überwachungspflicht!",
        keyPoints: [
          "Richtige Aufgabe: Ist die Aufgabe delegierbar?",
          "Richtige Person: Hat sie die nötige Qualifikation?",
          "Richtige Anweisung: Ist der Auftrag klar und verständlich?",
          "Richtige Überwachung: Werden Ergebnisse kontrolliert?"
        ]
      }
    ]
  },

  // MODUL 4: Qualitätsmanagement
  4: {
    title: "Qualitätsmanagement",
    icon: "✅",
    description: "Qualitätsstandards etablieren und MD-Prüfungen souverän bestehen.",
    slides: [
      {
        id: "qm_grundlagen",
        title: "Qualitätsmanagement-Grundlagen",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/2cb123e7f3bc9dd4b9b1b41f8e042aba67e1076194f3e57be09734c10a5c4194.png",
        explanation: "Das Bild zeigt die Kernelemente des Qualitätsmanagements: Die Checkliste für strukturierte Prozesse, das 'Quality'-Siegel für Ihre Zertifizierung, und die MD-Prüfung (früher MDK). Ein gutes QM-System im Wundmanagement reduziert Fehler um bis zu 40%!",
        keyPoints: [
          "PDCA-Zyklus: Plan-Do-Check-Act für kontinuierliche Verbesserung",
          "Expertenstandard DNQP als fachliche Grundlage",
          "Interne Audits alle 3-6 Monate durchführen",
          "Kennzahlen definieren und regelmäßig auswerten"
        ]
      },
      {
        id: "md_pruefung",
        title: "MD-Prüfung vorbereiten",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/2cb123e7f3bc9dd4b9b1b41f8e042aba67e1076194f3e57be09734c10a5c4194.png",
        explanation: "Die Pflegefachkraft prüft die Dokumentation - genau das macht auch der MD! Die häufigsten Beanstandungen betreffen: unvollständige Wunddokumentation, fehlende Fotodokumentation, keine erkennbare Evaluation. Einrichtungen mit regelmäßigen internen Audits haben 30% höhere MD-Erfolgsquoten!",
        keyPoints: [
          "Vollständige Wunddokumentation bei jeder Visite",
          "Fotodokumentation mit Maßstab und Datum",
          "Regelmäßige Evaluation (mind. wöchentlich)",
          "Nachvollziehbare Therapieanpassungen dokumentieren"
        ]
      },
      {
        id: "audit_checkliste",
        title: "Interne Audits durchführen",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/2cb123e7f3bc9dd4b9b1b41f8e042aba67e1076194f3e57be09734c10a5c4194.png",
        explanation: "Die Checkliste auf dem Bild ist Ihr wichtigstes Werkzeug! Ein internes Wundaudit prüft: Werden Standards eingehalten? Ist die Dokumentation vollständig? Sind die Materialien verfügbar? Werden Schulungen durchgeführt? Dokumentieren Sie Abweichungen und definieren Sie Maßnahmen!",
        keyPoints: [
          "Monatliche Stichprobenprüfung der Dokumentation",
          "Quartalweise umfassende Audits aller Wundpatienten",
          "Abweichungen dokumentieren und Maßnahmen festlegen",
          "Ergebnisse im Team besprechen und umsetzen"
        ]
      }
    ]
  },

  // MODUL 5: Wirtschaftlichkeit
  5: {
    title: "Wirtschaftlichkeit",
    icon: "💶",
    description: "Kosten optimieren ohne Qualitätsverlust - wirtschaftliches Wundmanagement.",
    slides: [
      {
        id: "kosten_analyse",
        title: "Kostenanalyse im Wundmanagement",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/b1cdcf3b8bc0a246ff7fa65c2ab513b35c0830eb7a4ae0d787e4566faf74d0c1.png",
        explanation: "Die Grafiken zeigen die typische Kostenverteilung: Eine chronische Wunde kostet 8.000-9.500 Euro pro Jahr! Der größte Kostenfaktor ist nicht das Material, sondern der Personalaufwand (60-70%). Durch effizientes Wundmanagement können Sie bis zu 30% einsparen!",
        keyPoints: [
          "Durchschnittliche Kosten: 8.000-9.500 € pro Patient/Jahr",
          "Personalkosten: 60-70% der Gesamtkosten",
          "Materialkosten: 20-30% der Gesamtkosten",
          "ROI eines strukturierten Wundmanagements: 200-300%"
        ]
      },
      {
        id: "budget_planung",
        title: "Budgetplanung für Wundmaterial",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/b1cdcf3b8bc0a246ff7fa65c2ab513b35c0830eb7a4ae0d787e4566faf74d0c1.png",
        explanation: "Der Bildschirm zeigt eine professionelle Budgetplanung. Wichtig: Billig ist nicht immer günstig! Ein hochwertiger Schaumverband für 8 Euro, der 3 Tage hält, ist wirtschaftlicher als eine Kompresse für 0,50 Euro, die täglich gewechselt werden muss. Berechnen Sie immer die Gesamtkosten!",
        keyPoints: [
          "Total Cost of Ownership statt Stückpreis betrachten",
          "Verbandswechselintervalle berücksichtigen",
          "Heilungszeit als Kostenfaktor einrechnen",
          "Rahmenverträge mit Lieferanten verhandeln"
        ]
      },
      {
        id: "kosten_nutzen",
        title: "Kosten-Nutzen-Optimierung",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/b1cdcf3b8bc0a246ff7fa65c2ab513b35c0830eb7a4ae0d787e4566faf74d0c1.png",
        explanation: "Die Kosten-Nutzen-Kurve zeigt den Sweet Spot: Investitionen in Qualität zahlen sich aus! Ein strukturiertes Wundmanagement-Programm amortisiert sich nach etwa 9 Monaten. Danach sparen Sie jährlich 15-25% der Wundversorgungskosten!",
        keyPoints: [
          "Break-even nach ca. 9 Monaten",
          "Jährliche Einsparung: 15-25% der Wundkosten",
          "Weniger Komplikationen = weniger Kosten",
          "Kürzere Heilungszeiten = weniger Verbandswechsel"
        ]
      }
    ]
  },

  // MODUL 6: Führungsaufgaben
  6: {
    title: "Führung & Schulung",
    icon: "🎓",
    description: "Ihr Team erfolgreich führen und für die Wundversorgung qualifizieren.",
    slides: [
      {
        id: "fuehrung_grundlagen",
        title: "Führung im Wundmanagement",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/4bde9b7b5625fef1f88fea69cd7f2e84692c0cd47558d93d1b0517c99842b0c1.png",
        explanation: "Das Bild zeigt eine typische Schulungssituation: Die Leitungskraft demonstriert, das Team lernt. Gute Führung im Wundmanagement bedeutet: Vorbild sein, Standards setzen, Wissen vermitteln, und Feedback geben. Teams mit offener Fehlerkultur haben bis zu 30% weniger Komplikationen!",
        keyPoints: [
          "Vorbild sein: Selbst die Standards einhalten",
          "Wissen teilen: Regelmäßige Schulungen durchführen",
          "Feedback geben: Konstruktiv und zeitnah",
          "Fehlerkultur: Aus Fehlern lernen statt beschuldigen"
        ]
      },
      {
        id: "schulung_planung",
        title: "Schulungsplanung",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/4bde9b7b5625fef1f88fea69cd7f2e84692c0cd47558d93d1b0517c99842b0c1.png",
        explanation: "Die praktische Demonstration auf dem Bild ist der Schlüssel! Effektive Wundschulungen kombinieren: Theorie (20%), Demonstration (30%), und praktisches Üben (50%). Planen Sie mindestens 2 Schulungen pro Jahr für jeden Mitarbeiter ein, plus Auffrischungen bei neuen Produkten!",
        keyPoints: [
          "Jährliche Pflichtschulung für alle Pflegekräfte",
          "Einarbeitung neuer Mitarbeiter mit Checkliste",
          "Produktschulungen bei Sortimentswechsel",
          "Fallbesprechungen bei komplexen Wunden"
        ]
      },
      {
        id: "team_motivation",
        title: "Team motivieren",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/4bde9b7b5625fef1f88fea69cd7f2e84692c0cd47558d93d1b0517c99842b0c1.png",
        explanation: "Die engagierte Lernatmosphäre auf dem Bild zeigt motivierte Mitarbeiter. Motivation im Wundmanagement entsteht durch: Erfolgserlebnisse sichtbar machen, Expertise anerkennen, Entwicklungsmöglichkeiten bieten. Ein motiviertes Team versorgt Wunden besser und dokumentiert sorgfältiger!",
        keyPoints: [
          "Heilungserfolge im Team teilen und feiern",
          "Wundexperten als Vorbilder etablieren",
          "Fortbildungen ermöglichen und fördern",
          "Eigenverantwortung stärken durch Vertrauen"
        ]
      }
    ]
  },

  // MODUL 7: Dokumentation und Informationsmanagement
  7: {
    title: "Dokumentation",
    icon: "📱",
    description: "Moderne, rechtssichere Dokumentation und DSGVO-konforme Datenhaltung.",
    slides: [
      {
        id: "doku_digital",
        title: "Digitale Wunddokumentation",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/02cc84105ffe478e91cbc0c886852f224b5df535ba6c9e69512693946013294f.png",
        explanation: "Das Tablet auf dem Bild zeigt eine moderne Wund-App mit Fotodokumentation. Digitale Dokumentation bietet enorme Vorteile: sofortige Verfügbarkeit, bessere Lesbarkeit, automatische Zeitstempel. Wichtig: Das System muss DSGVO-konform sein und die Daten sicher speichern!",
        keyPoints: [
          "Sofortige Verfügbarkeit der Wundhistorie",
          "Automatische Zeitstempel für Rechtssicherheit",
          "Fotodokumentation direkt am Patientenbett",
          "Auswertungen und Statistiken auf Knopfdruck"
        ]
      },
      {
        id: "dsgvo_konform",
        title: "DSGVO & Datenschutz",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/02cc84105ffe478e91cbc0c886852f224b5df535ba6c9e69512693946013294f.png",
        explanation: "Das Schild-Symbol steht für Datenschutz nach DSGVO. Wundfotos sind hochsensible Daten! Sie dürfen NIEMALS auf privaten Smartphones gespeichert werden. Nutzen Sie nur freigegebene Dienstgeräte und sichere Systeme. Bei Verstößen drohen Bußgelder bis zu 20 Millionen Euro!",
        keyPoints: [
          "NIEMALS Wundfotos auf privaten Geräten speichern",
          "Einwilligung des Patienten für Fotos einholen",
          "Nur verschlüsselte Übertragung verwenden",
          "Zugriffsrechte klar definieren und überwachen"
        ]
      },
      {
        id: "doku_standard",
        title: "Dokumentationsstandards",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/02cc84105ffe478e91cbc0c886852f224b5df535ba6c9e69512693946013294f.png",
        explanation: "Die strukturierte Oberfläche zeigt einen Dokumentationsstandard. Jede Wunddokumentation muss enthalten: Lokalisation, Größe (mit Maßstab!), Wundgrund, Wundrand, Exsudat, Umgebungshaut, Schmerzen, durchgeführte Maßnahmen. Nutzen Sie standardisierte Formulare!",
        keyPoints: [
          "PUSH-Tool oder vergleichbares Assessment verwenden",
          "Fotodokumentation mit Maßstab und Datum",
          "Wöchentliche Evaluation und Therapieanpassung",
          "Alle Abweichungen vom Standard begründen"
        ]
      }
    ]
  },

  // MODUL 8: Gesamtkonzept entwickeln
  8: {
    title: "Gesamtkonzept",
    icon: "🎯",
    description: "Alles zusammenbringen - Ihr individuelles Wundmanagement-Konzept.",
    slides: [
      {
        id: "konzept_entwicklung",
        title: "Ihr Wundmanagement-Konzept",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/3dd5ad1796e5de99119a05542ee4daf18693ef44d9bf8452a161d91daa6ae8e3.png",
        explanation: "Das Bild zeigt den Weg zum Erfolg: Von der Planung zur Umsetzung zum Ziel! Ein Wundmanagement-Konzept verbindet alles, was Sie gelernt haben: Standards, Strukturen, Qualität, Wirtschaftlichkeit, Führung und Dokumentation. Jetzt bringen Sie alles zusammen!",
        keyPoints: [
          "Standards: Expertenstandard als Grundlage implementieren",
          "Strukturen: Klare Verantwortlichkeiten nach RACI definieren",
          "Prozesse: Vom Assessment bis zur Evaluation standardisieren",
          "Controlling: Kennzahlen festlegen und auswerten"
        ]
      },
      {
        id: "implementierung",
        title: "Erfolgreiche Implementierung",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/3dd5ad1796e5de99119a05542ee4daf18693ef44d9bf8452a161d91daa6ae8e3.png",
        explanation: "Die Roadmap auf dem Bild zeigt den Implementierungsplan. Gehen Sie schrittweise vor: Phase 1 - Analyse (Wo stehen wir?), Phase 2 - Planung (Wo wollen wir hin?), Phase 3 - Umsetzung (Wie kommen wir dahin?), Phase 4 - Evaluation (Haben wir es erreicht?). Planen Sie 6-12 Monate ein!",
        keyPoints: [
          "Phase 1: Ist-Analyse und Bedarfsermittlung (1-2 Monate)",
          "Phase 2: Konzeptentwicklung und Schulungsplanung (2-3 Monate)",
          "Phase 3: Schrittweise Umsetzung mit Pilotbereich (3-4 Monate)",
          "Phase 4: Evaluation und Optimierung (fortlaufend)"
        ]
      },
      {
        id: "erfolg_messen",
        title: "Erfolg messen und feiern",
        image: "https://static.prod-images.emergentagent.com/jobs/343dde11-57f4-4974-9caa-a1d1e2bcfb1b/images/3dd5ad1796e5de99119a05542ee4daf18693ef44d9bf8452a161d91daa6ae8e3.png",
        explanation: "Das Team feiert den Erfolg - und das sollten Sie auch! Messen Sie Ihren Erfolg an konkreten Kennzahlen: Heilungsraten, Komplikationsraten, Dokumentationsqualität, Mitarbeiterzufriedenheit. Feiern Sie Erfolge im Team - das motiviert für die nächsten Herausforderungen!",
        keyPoints: [
          "Heilungsrate: Wie viele Wunden heilen planmäßig ab?",
          "Komplikationsrate: Wie oft treten Probleme auf?",
          "MD-Ergebnisse: Wie schneiden wir bei Prüfungen ab?",
          "Teamzufriedenheit: Wie geht es den Mitarbeitern?"
        ]
      }
    ]
  }
};

// ===================== LG COURSE VISUAL CONTENT =====================
const lgVisualContent = {
  // MODUL 1: Flüssigkeitsbilanzierung
  1: {
    title: "Flüssigkeitsbilanzierung",
    icon: "💧",
    description: "Lernen Sie die korrekte Durchführung der Flüssigkeitsbilanzierung.",
    slides: [
      {
        id: "fluessigkeit_grundlagen",
        title: "Grundlagen der Bilanzierung",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/6b7e93940e41d43208ec9907889f5d0f159703d3739b3665f890b6090162fdbd.png",
        explanation: "Die Flüssigkeitsbilanzierung ist ein wichtiger Bestandteil der Pflege. Der menschliche Körper besteht zu etwa 60 Prozent aus Wasser. Wir unterscheiden zwischen Einfuhr wie Getränke und Infusionen und Ausfuhr wie Urin und Schweiß. Eine ausgeglichene Bilanz ist lebenswichtig für alle Körperfunktionen!",
        keyPoints: [
          "Einfuhr: Getränke, Speisen, Infusionen",
          "Ausfuhr: Urin, Stuhl, Schweiß, Atmung",
          "Normale Trinkmenge: 1,5-2 Liter täglich",
          "Bei Herzinsuffizienz besonders wichtig"
        ]
      }
    ]
  },
  // MODUL 2: Diabetes mellitus
  2: {
    title: "Diabetes mellitus",
    icon: "🩸",
    description: "Blutzuckermessung und Diabetesmanagement in der Pflege.",
    slides: [
      {
        id: "blutzucker_messung",
        title: "Blutzuckermessung",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/3379e713bb9258cbc3a98ef31df54d46e3f6fe651f21e5d7ae7b08920b55f360.png",
        explanation: "Die korrekte Blutzuckermessung ist essenziell für Diabetiker. Verwenden Sie immer ein kalibriertes Messgerät und frische Teststreifen. Die Einstichstelle sollte seitlich am Finger sein, nicht an der Fingerkuppe. Dokumentieren Sie jeden Wert mit Uhrzeit und besonderen Umständen wie vor oder nach dem Essen!",
        keyPoints: [
          "Normale Nüchternwerte: unter 100 mg/dl",
          "Einstich seitlich am Finger",
          "Nicht quetschen - verfälscht das Ergebnis",
          "Werte sofort dokumentieren"
        ]
      }
    ]
  },
  // MODUL 3: Subcutane Injektionen
  3: {
    title: "Subcutane Injektionen",
    icon: "💉",
    description: "Die richtige Technik für s.c. Injektionen erlernen.",
    slides: [
      {
        id: "sc_injektion",
        title: "Subcutane Injektionstechnik",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/687716de0714d6816fe7b44c8d49b7dda8b72e75d0c44182d6e3b23c523fdafa.png",
        explanation: "Bei der subcutanen Injektion wird das Medikament in das Unterhautfettgewebe gespritzt. Bilden Sie eine Hautfalte mit Daumen und Zeigefinger. Der Einstichwinkel beträgt 45 bis 90 Grad je nach Nadellänge. Bei Insulin und Heparin wird nicht aspiriert! Die Injektionsstellen müssen regelmäßig gewechselt werden.",
        keyPoints: [
          "Hautfalte bilden vor Einstich",
          "Winkel: 45-90 Grad je nach Nadel",
          "Nicht aspirieren bei Insulin/Heparin",
          "Injektionsstellen rotieren"
        ]
      }
    ]
  },
  // MODUL 4: Ernährung im Alter
  4: {
    title: "Ernährung im Alter",
    icon: "🥗",
    description: "Besonderheiten der Ernährung älterer Menschen.",
    slides: [
      {
        id: "ernaehrung_alter",
        title: "Ernährung im Alter",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/6b7e93940e41d43208ec9907889f5d0f159703d3739b3665f890b6090162fdbd.png",
        explanation: "Im Alter verändert sich der Stoffwechsel. Der Grundumsatz sinkt, aber der Nährstoffbedarf bleibt gleich oder steigt sogar! Ältere Menschen brauchen mehr Eiweiß. Häufige Probleme sind Appetitlosigkeit und vermindertes Durstgefühl. Mangelernährung muss früh erkannt werden!",
        keyPoints: [
          "Eiweißbedarf: 1g pro kg Körpergewicht",
          "Oft vermindertes Durstgefühl",
          "Mangelernährung früh erkennen",
          "Kleine Mahlzeiten anbieten"
        ]
      }
    ]
  },
  // MODUL 5: Dekubitus-Versorgung
  5: {
    title: "Dekubitus-Versorgung",
    icon: "🩹",
    description: "Professionelle Versorgung von Druckgeschwüren.",
    slides: [
      {
        id: "dekubitus_versorgung",
        title: "Dekubitus-Versorgung",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/687716de0714d6816fe7b44c8d49b7dda8b72e75d0c44182d6e3b23c523fdafa.png",
        explanation: "Dekubitus entsteht durch anhaltenden Druck auf die Haut. Prävention ist das A und O: regelmäßige Lagerungswechsel alle 2 Stunden, Druckentlastung und gute Hautpflege. Bei der Wundversorgung wählen wir das Verbandmaterial nach dem aktuellen Wundzustand aus.",
        keyPoints: [
          "Lagerungswechsel alle 2 Stunden",
          "Druckentlastende Hilfsmittel nutzen",
          "Feuchte Wundbehandlung",
          "Ernährungszustand optimieren"
        ]
      }
    ]
  },
  // MODUL 6: Dermatologische Behandlungen
  6: {
    title: "Dermatologische Behandlungen",
    icon: "🧴",
    description: "Salben, Cremes und Lotionen richtig anwenden.",
    slides: [
      {
        id: "hautpflege",
        title: "Hautpflege und Einreibungen",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/6b7e93940e41d43208ec9907889f5d0f159703d3739b3665f890b6090162fdbd.png",
        explanation: "In der dermatologischen Pflege unterscheiden wir verschiedene Grundlagen. Salben sind fettreich und ideal für trockene Haut. Cremes sind Emulsionen mit Wasser und Fett. Die Auftragsmenge wird oft mit der Fingerspitzeneinheit gemessen. Immer in Haarwuchsrichtung einreiben!",
        keyPoints: [
          "Salbe: fettreich für trockene Haut",
          "Creme: Emulsion mit Wasseranteil",
          "Fingerspitzeneinheit als Maß",
          "In Haarwuchsrichtung auftragen"
        ]
      }
    ]
  },
  // MODUL 7: Medizinische Einreibungen
  7: {
    title: "Medizinische Einreibungen",
    icon: "✋",
    description: "Therapeutische Einreibungen korrekt durchführen.",
    slides: [
      {
        id: "einreibungen",
        title: "Medizinische Einreibungen",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/6b7e93940e41d43208ec9907889f5d0f159703d3739b3665f890b6090162fdbd.png",
        explanation: "Medizinische Einreibungen fördern die Durchblutung und können Schmerzen lindern. Warme Hände sind wichtig für das Wohlbefinden des Patienten. Die Technik umfasst kreisende oder streichende Bewegungen. Beachten Sie Kontraindikationen wie offene Wunden oder Thrombosen!",
        keyPoints: [
          "Hände vorher aufwärmen",
          "Kreisende Bewegungen anwenden",
          "Kontraindikationen beachten",
          "Durchblutung und Entspannung fördern"
        ]
      }
    ]
  },
  // MODUL 8: Augentropfen und Augensalben
  8: {
    title: "Augentropfen und Augensalben",
    icon: "👁️",
    description: "Sichere Applikation am Auge.",
    slides: [
      {
        id: "augentropfen",
        title: "Augenmedikation",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/93cc7559c6dc74cf9e2ca89eb54087e77e4e1b1f708d5150fa504329386e1142.png",
        explanation: "Bei der Augenmedikation ist Hygiene besonders wichtig! Hände waschen und Handschuhe tragen. Der Patient schaut nach oben, Sie ziehen das Unterlid leicht herunter. Der Tropfen fällt in den Bindehautsack, nicht auf die Hornhaut! Bei verschiedenen Präparaten 5 Minuten Abstand halten.",
        keyPoints: [
          "Strenge Hygiene beachten",
          "Unterlid herunterziehen",
          "In Bindehautsack tropfen",
          "5 Min. Abstand zwischen Präparaten"
        ]
      }
    ]
  },
  // MODUL 9: Suprapubischer Katheter
  9: {
    title: "Suprapubischer Katheter",
    icon: "🏥",
    description: "Pflege und Überwachung des SP-Katheters.",
    slides: [
      {
        id: "sp_katheter",
        title: "Suprapubischer Katheter",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/9db007edd3b84e8ad0d4823b1267a00cc7cfc3dd091008d9472651b955513d7e.png",
        explanation: "Der suprapubische Katheter wird durch die Bauchdecke direkt in die Blase gelegt. Im Vergleich zum transurethralen Katheter gibt es weniger Harnwegsinfekte. Die Einstichstelle muss täglich kontrolliert werden auf Rötung, Schwellung und Sekret. Der Wechsel erfolgt alle 4-6 Wochen durch den Arzt.",
        keyPoints: [
          "Weniger Infektionsrisiko als transurethral",
          "Einstichstelle täglich kontrollieren",
          "Wechsel alle 4-6 Wochen",
          "Auf Durchgängigkeit achten"
        ]
      }
    ]
  },
  // MODUL 10: Klistiere und Klysmen
  10: {
    title: "Klistiere und Klysmen",
    icon: "💊",
    description: "Einläufe korrekt durchführen.",
    slides: [
      {
        id: "klistier",
        title: "Klistiere und Klysmen",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/6b7e93940e41d43208ec9907889f5d0f159703d3739b3665f890b6090162fdbd.png",
        explanation: "Klistiere und Klysmen dienen der Darmentleerung. Der Patient liegt auf der linken Seite mit angezogenem rechten Bein. Die Temperatur sollte etwa 37 Grad betragen. Langsam einlaufen lassen und den Patienten unterstützen. Intimsphäre wahren!",
        keyPoints: [
          "Linksseitenlage mit angezogenem Bein",
          "Temperatur: ca. 37 Grad",
          "Langsam einlaufen lassen",
          "Intimsphäre wahren"
        ]
      }
    ]
  },
  // MODUL 11: Kompressionsstrümpfe
  11: {
    title: "Kompressionsstrümpfe",
    icon: "🧦",
    description: "Anlegen und Pflege von Kompressionsstrümpfen.",
    slides: [
      {
        id: "kompression",
        title: "Kompressionstherapie",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/e8cb21a1405624393a110490e364f93c4888603df8296380c7e216d888e5691a.png",
        explanation: "Kompressionsstrümpfe unterstützen den venösen Rückfluss. Es gibt vier Kompressionsklassen. Das Anlegen erfolgt morgens vor dem Aufstehen, wenn die Beine noch nicht geschwollen sind. Die Strümpfe werden gewendet und über den Fuß gerollt, niemals gezogen!",
        keyPoints: [
          "Morgens vor dem Aufstehen anlegen",
          "Strümpfe wenden und rollen",
          "Niemals ziehen",
          "Täglich wechseln und waschen"
        ]
      }
    ]
  },
  // MODUL 12: Inhalationen
  12: {
    title: "Inhalationen",
    icon: "🌬️",
    description: "Verschiedene Inhalationstechniken.",
    slides: [
      {
        id: "inhalation",
        title: "Inhalationstechnik",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/8dcc1f82597e9f22847d6ca9ce4caffe6965be9da76eeb34d1e3b0b60cd54ab1.png",
        explanation: "Inhalationen bringen Medikamente direkt in die Atemwege. Beim Dosieraerosol: Ausatmen, Sprühstoß auslösen, langsam einatmen, Luft anhalten. Der Spacer erleichtert die Koordination. Nach Kortison-Inhalation immer den Mund ausspülen!",
        keyPoints: [
          "Dosieraerosol: ausatmen, sprühen, einatmen",
          "Spacer erleichtert Koordination",
          "Luft 10 Sekunden anhalten",
          "Nach Kortison Mund ausspülen"
        ]
      }
    ]
  },
  // MODUL 13: Arzneimittellehre
  13: {
    title: "Arzneimittellehre",
    icon: "💊",
    description: "Wichtige Begriffe der Pharmakologie.",
    slides: [
      {
        id: "pharmakologie",
        title: "Pharmakologie Grundlagen",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/396e683a2b4db15de7f7bad7a24d29a60b513e1cacfe78fe6df8492f444e7cca.png",
        explanation: "In der Arzneimittellehre unterscheiden wir Pharmakokinetik und Pharmakodynamik. Pharmakokinetik beschreibt, was der Körper mit dem Medikament macht. Pharmakodynamik beschreibt, was das Medikament im Körper bewirkt. Die Halbwertszeit bestimmt die Dosierungsintervalle!",
        keyPoints: [
          "Pharmakokinetik: Weg des Medikaments",
          "Pharmakodynamik: Wirkung im Körper",
          "Halbwertszeit beachten",
          "Wechselwirkungen kennen"
        ]
      }
    ]
  },
  // MODUL 14: Medikamentengabe
  14: {
    title: "Medikamentengabe",
    icon: "✅",
    description: "Die 6-R-Regel sicher anwenden.",
    slides: [
      {
        id: "6r_regel",
        title: "Die 6-R-Regel",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/396e683a2b4db15de7f7bad7a24d29a60b513e1cacfe78fe6df8492f444e7cca.png",
        explanation: "Die 6-R-Regel schützt vor Medikamentenfehlern! Richtiger Patient, richtiges Medikament, richtige Dosierung, richtige Zeit, richtige Anwendung und richtige Dokumentation. Bei Unsicherheiten immer nachfragen! Besonders bei Hochrisiko-Medikamenten doppelt kontrollieren.",
        keyPoints: [
          "Richtiger Patient",
          "Richtiges Medikament",
          "Richtige Dosis, Zeit, Anwendung",
          "Richtige Dokumentation"
        ]
      }
    ]
  },
  // MODUL 15: Notfallsituationen
  15: {
    title: "Notfallsituationen",
    icon: "🚨",
    description: "Lebensbedrohliche Zustände früh erkennen.",
    slides: [
      {
        id: "notfall",
        title: "Notfallsituationen erkennen",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/396e683a2b4db15de7f7bad7a24d29a60b513e1cacfe78fe6df8492f444e7cca.png",
        explanation: "In Notfallsituationen zählt jede Sekunde! Wichtige Warnsignale sind Bewusstlosigkeit, Atemnot, starke Brustschmerzen und plötzliche Lähmungen. Bei Verdacht auf Herzinfarkt oder Schlaganfall sofort Notruf 112! Ruhe bewahren und strukturiert handeln.",
        keyPoints: [
          "Warnsignale erkennen",
          "Notruf 112 wählen",
          "Ruhe bewahren",
          "Stabile Seitenlage bei Bewusstlosen"
        ]
      }
    ]
  },
  // MODUL 16: Prüfungsvorbereitung
  16: {
    title: "Prüfungsvorbereitung",
    icon: "🎓",
    description: "Zusammenfassung und Prüfungstipps.",
    slides: [
      {
        id: "pruefung",
        title: "Prüfungsvorbereitung",
        image: "https://static.prod-images.emergentagent.com/jobs/8f9f840c-8a68-44e7-8ad6-344af4308ce6/images/396e683a2b4db15de7f7bad7a24d29a60b513e1cacfe78fe6df8492f444e7cca.png",
        explanation: "Herzlichen Glückwunsch, Sie haben alle Module durchgearbeitet! Zur Prüfungsvorbereitung wiederholen Sie die wichtigsten Punkte jedes Moduls. Konzentrieren Sie sich auf praktische Anwendungen und Sicherheitsaspekte. Die 6-R-Regel, Injektionstechniken und Notfallmaßnahmen sind Prüfungsklassiker!",
        keyPoints: [
          "Alle Module wiederholen",
          "6-R-Regel beherrschen",
          "Praktische Techniken üben",
          "Notfallmaßnahmen kennen"
        ]
      }
    ]
  }
};

// ===================== PFLEGEFACHKRÄFTE VISUAL CONTENT =====================
// Eigene Bilder für den 9-Modul-Kurs "Chronisch und schwerheilende Wunden für Pflegefachkräfte"

const pflegefachkraefteVisualContent = {
  // MODUL 1: Physiologie & Anatomie der Haut
  1: {
    title: "Physiologie & Anatomie der Haut",
    icon: "🔬",
    description: "Aufbau der Haut mit den drei Schichten: Epidermis, Dermis und Subcutis.",
    slides: [
      {
        id: "pfk_haut_anatomie",
        title: "Die drei Hautschichten",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/ec7792aff2743903ca576cc32080624d3c1fa74810309438d41fe5be74d2ac69.png",
        explanation: "Die Haut besteht aus drei Hauptschichten: Die Epidermis (Oberhaut) ist die äußerste Schutzschicht mit einer Dicke von 0,03-0,05 mm. Die Dermis (Lederhaut) enthält Blutgefäße, Nerven und Haarfollikel. Die Subcutis (Unterhaut) besteht aus Fettgewebe und dient als Energiespeicher und Wärmeisolierung.",
        keyPoints: [
          "Epidermis: Schutzfunktion, Zellerneuerung alle 28 Tage",
          "Dermis: Blutgefäße, Nervenendigungen, Kollagenfasern",
          "Subcutis: Fettgewebe, Wärmeisolierung, Stoßdämpfung",
          "Hautanhangsgebilde: Haare, Talg- und Schweißdrüsen"
        ]
      },
      {
        id: "pfk_gefaessversorgung",
        title: "Gefäßversorgung der Haut",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/ec7792aff2743903ca576cc32080624d3c1fa74810309438d41fe5be74d2ac69.png",
        explanation: "Die Haut wird durch ein komplexes Gefäßnetzwerk versorgt. Die kutane Durchblutung ist essentiell für Wundheilung, Thermoregulation und Nährstoffversorgung. Durchblutungsstörungen führen zu Gewebeschäden.",
        keyPoints: [
          "Arterielle Versorgung: Sauerstoff und Nährstoffe",
          "Venöser Rückfluss: Abtransport von Stoffwechselprodukten",
          "Kapillarnetz: Austausch auf Gewebeebene",
          "Lymphsystem: Drainage von Gewebeflüssigkeit"
        ]
      }
    ]
  },
  // MODUL 2: Wundheilung, Wundarten & Hygiene
  2: {
    title: "Wundheilung & Wundarten",
    icon: "🩹",
    description: "Die drei Phasen der Wundheilung und verschiedene Wundarten.",
    slides: [
      {
        id: "pfk_wundheilung_phasen",
        title: "Phasen der Wundheilung",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/a3f6274538c36a16e56c6dfd6a5d227ef178cf23967fabe7f00a942f4f2e6511.png",
        explanation: "Die Wundheilung verläuft in drei Phasen: 1. Exsudationsphase (Tag 0-4) mit Entzündung und Reinigung, 2. Granulationsphase (Tag 4-21) mit Neubildung von Gewebe, 3. Epithelisierungsphase (ab Tag 21) mit Hautneubildung. Bei chronischen Wunden stagniert dieser Prozess.",
        keyPoints: [
          "Exsudationsphase: Blutstillung, Entzündung, Reinigung",
          "Granulationsphase: Neue Blutgefäße, Bindegewebe",
          "Epithelisierung: Hautneubildung vom Wundrand",
          "Chronische Wunden: Stagnation in einer Phase"
        ]
      },
      {
        id: "pfk_hygiene",
        title: "Hygiene in der Wundversorgung",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/a3f6274538c36a16e56c6dfd6a5d227ef178cf23967fabe7f00a942f4f2e6511.png",
        explanation: "Aseptisches Arbeiten ist essenziell! Händedesinfektion (30 Sek.), sterile Materialien und No-Touch-Technik sind Pflicht. Infektionszeichen: Rubor, Calor, Tumor, Dolor, Functio laesa.",
        keyPoints: [
          "Händedesinfektion: 30 Sekunden Einwirkzeit",
          "Sterile Materialien verwenden",
          "No-Touch-Technik beachten",
          "5 Kardinalsymptome der Infektion erkennen"
        ]
      }
    ]
  },
  // MODUL 3: Gefäßbedingte Erkrankungen
  3: {
    title: "Ulcus cruris: Venös vs. Arteriell",
    icon: "🦵",
    description: "Unterschiede zwischen venösen und arteriellen Ulzera.",
    slides: [
      {
        id: "pfk_ulcus_vergleich",
        title: "Venöses vs. Arterielles Ulcus",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/0c8b6b46c5bf514c4d046e55b31d13b1a6904f9d4f7c5f1191f490c923438775.png",
        explanation: "Das venöse Ulcus cruris liegt typischerweise am Innenknöchel, nässt stark und hat unregelmäßige Ränder. Die Umgebung zeigt Hyperpigmentierung. Das arterielle Ulcus sitzt an Zehen oder Fersen, ist schmerzhaft und hat scharfe Ränder mit blassem Wundgrund.",
        keyPoints: [
          "Venös: Innenknöchel, nässend, wenig schmerzhaft",
          "Arteriell: Zehen/Ferse, trocken, sehr schmerzhaft",
          "Venös: Kompression ist DIE Therapie",
          "Arteriell: Kompression ist KONTRAINDIZIERT!"
        ]
      },
      {
        id: "pfk_cvi_stadien",
        title: "CVI-Stadien nach Widmer",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/0c8b6b46c5bf514c4d046e55b31d13b1a6904f9d4f7c5f1191f490c923438775.png",
        explanation: "Die Chronisch-Venöse Insuffizienz wird nach Widmer in 3 Stadien eingeteilt: Stadium I zeigt Ödeme und Corona phlebectatica, Stadium II zeigt Hautveränderungen wie Hyperpigmentierung, Stadium III zeigt ein florides oder abgeheiltes Ulcus.",
        keyPoints: [
          "Stadium I: Ödeme, Corona phlebectatica",
          "Stadium II: Hautveränderungen, Dermatitis",
          "Stadium III: Ulcus cruris (florid oder abgeheilt)",
          "Kompressionstherapie als Goldstandard"
        ]
      }
    ]
  },
  // MODUL 4: PAVK & Lymphangiopathien
  4: {
    title: "PAVK-Stadien nach Fontaine",
    icon: "🩺",
    description: "Die 4 Stadien der peripheren arteriellen Verschlusskrankheit.",
    slides: [
      {
        id: "pfk_pavk_stadien",
        title: "Fontaine-Klassifikation",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/661991bec6b30536bc9320007eb9d8aaf074b2a9322286e44d2c5fe53febab0d.png",
        explanation: "Die PAVK wird nach Fontaine klassifiziert: Stadium I ist asymptomatisch, Stadium II zeigt Claudicatio intermittens (Schaufensterkrankheit), Stadium III zeigt Ruheschmerzen, Stadium IV zeigt Nekrosen und Gangrän. Der wichtigste Risikofaktor ist Nikotinkonsum!",
        keyPoints: [
          "Stadium I: Asymptomatisch (ABI < 0,9)",
          "Stadium II: Claudicatio intermittens",
          "Stadium III: Ruheschmerzen, besonders nachts",
          "Stadium IV: Nekrosen, Gangrän, kritische Ischämie"
        ]
      },
      {
        id: "pfk_lymphoedem",
        title: "Lymphödem-Stadien",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/661991bec6b30536bc9320007eb9d8aaf074b2a9322286e44d2c5fe53febab0d.png",
        explanation: "Das Lymphödem wird in 4 Stadien eingeteilt. Das Stemmer-Zeichen (Hautfalte über 2. Zehe nicht abhebbar) ist ein wichtiges diagnostisches Kriterium. Die Therapie erfolgt durch Komplexe Physikalische Entstauungstherapie (KPE).",
        keyPoints: [
          "Stemmer-Zeichen: Diagnostisches Kriterium",
          "Stadium I: Reversibel, weiches Ödem",
          "Stadium II: Irreversibel, Fibrosierung",
          "KPE: Lymphdrainage + Kompression + Bewegung"
        ]
      }
    ]
  },
  // MODUL 5: Diabetisches Fußsyndrom & Dekubitus
  5: {
    title: "DFS & Dekubitus",
    icon: "🦶",
    description: "Diabetisches Fußsyndrom und Dekubitus-Klassifikation.",
    slides: [
      {
        id: "pfk_dfs_dekubitus",
        title: "Diabetisches Fußsyndrom & Dekubitus",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/6dc732ccded1320a283fbad8651b668469fe8655dc800f53ee28ef4c7a8221cc.png",
        explanation: "Das Diabetische Fußsyndrom entsteht durch Neuropathie und Angiopathie. Dekubitus entsteht durch anhaltenden Druck auf Knochen-Hautbrücken. Beide erfordern präventive Maßnahmen und frühzeitige Intervention.",
        keyPoints: [
          "DFS: Neuropathie + Angiopathie + Infektanfälligkeit",
          "Wagner-Klassifikation: Grad 0-5",
          "Dekubitus: Druck, Scherkräfte, Reibung",
          "EPUAP-Kategorien: I-IV"
        ]
      },
      {
        id: "pfk_praevention",
        title: "Prävention",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/6dc732ccded1320a283fbad8651b668469fe8655dc800f53ee28ef4c7a8221cc.png",
        explanation: "Prävention ist der Schlüssel! Beim DFS: tägliche Fußinspektion, Podologie, orthopädisches Schuhwerk, HbA1c < 7%. Beim Dekubitus: regelmäßige Lagerung (alle 2-4 Stunden), Druckentlastung, Hautpflege.",
        keyPoints: [
          "DFS: Tägliche Fußinspektion",
          "DFS: HbA1c-Ziel < 7%",
          "Dekubitus: Lagerung alle 2-4 Stunden",
          "Dekubitus: 30°-Schräglagerung bevorzugen"
        ]
      }
    ]
  },
  // MODUL 6: Wundbehandlungsprozess
  6: {
    title: "Wundbehandlungsprozess",
    icon: "🩺",
    description: "Strukturierter Ablauf der professionellen Wundversorgung.",
    slides: [
      {
        id: "pfk_wundbehandlung",
        title: "Wundversorgung Schritt für Schritt",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/f9f3c3e5504b928f16df90c7d0908f3538e6bcfbe44c71ca76e7a0738d22e5e2.png",
        explanation: "Die Wundversorgung folgt einem strukturierten Ablauf: 1. Vorbereitung und Händedesinfektion, 2. Schonende Verbandsabnahme, 3. Wundinspektion und -beurteilung, 4. Wundreinigung mit körperwarmer Lösung, 5. Auswahl passender Verbandmaterialien, 6. Dokumentation.",
        keyPoints: [
          "Händedesinfektion vor und nach Versorgung",
          "Schonende Verbandsabnahme (ggf. anfeuchten)",
          "Körperwarme Spüllösung verwenden",
          "Verbandwahl passend zur Wundphase"
        ]
      },
      {
        id: "pfk_verbandmaterialien",
        title: "Moderne Wundauflagen",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/f9f3c3e5504b928f16df90c7d0908f3538e6bcfbe44c71ca76e7a0738d22e5e2.png",
        explanation: "Hydrokolloid-Verbände eignen sich für leicht nässende Wunden, Alginate für stark exsudierende Wunden, Schaumverbände für mittlere Exsudation. Die NPWT (Unterdruck-Wundtherapie) fördert die Granulation bei großflächigen Wunden.",
        keyPoints: [
          "Hydrokolloid: Leicht nässende Wunden",
          "Alginate: Stark exsudierende Wunden",
          "Schaumverbände: Mittlere Exsudation",
          "NPWT: Fördert Granulation"
        ]
      }
    ]
  },
  // MODUL 7: Kompressionstherapie & Schmerzmanagement
  7: {
    title: "Kompression & Schmerz",
    icon: "🩹",
    description: "Kompressionstherapie und multimodales Schmerzmanagement.",
    slides: [
      {
        id: "pfk_kompression",
        title: "Kompressionstherapie",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/481159dd0d055874291b449d23de8988745e6355958a5140f7a595b1a4f5205e.png",
        explanation: "Die Kompressionstherapie ist der Goldstandard bei venösen Wunden! Kompressionsklassen I-IV nach Druckstärke. Absolut kontraindiziert bei PAVK mit ABI < 0,5! Vor jeder Kompression ABI messen!",
        keyPoints: [
          "Klasse I: 18-21 mmHg (leichte CVI)",
          "Klasse II: 23-32 mmHg (mittelschwere CVI)",
          "Klasse III: 34-46 mmHg (schwere CVI)",
          "KONTRAINDIKATION: ABI < 0,5"
        ]
      },
      {
        id: "pfk_schmerz",
        title: "Schmerzmanagement",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/481159dd0d055874291b449d23de8988745e6355958a5140f7a595b1a4f5205e.png",
        explanation: "80% der Wundpatienten haben Schmerzen! Schmerzerfassung mit NRS (0-10). WHO-Stufenschema: Stufe I Nicht-Opioide, Stufe II schwache Opioide, Stufe III starke Opioide. Topische Analgesie 30-60 Min. vor Verbandwechsel!",
        keyPoints: [
          "Schmerzerfassung: NRS 0-10",
          "WHO-Stufenschema beachten",
          "Topische Analgesie 30-60 Min. vorher",
          "Atraumatische Verbandwechsel"
        ]
      }
    ]
  },
  // MODUL 8: Ernährung, Dokumentation & Qualitätssicherung
  8: {
    title: "Ernährung & Dokumentation",
    icon: "📋",
    description: "Ernährungstherapie und professionelle Wunddokumentation.",
    slides: [
      {
        id: "pfk_ernaehrung",
        title: "Ernährung bei Wundheilung",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/bb61367e6574b4c74c4dc261e7ab87848cd54ceb702555cf1e5295c768f4d6b4.png",
        explanation: "Bis zu 40% der Wundpatienten sind mangelernährt! Protein-Bedarf: 1,2-1,5 g/kg/Tag für Wundheilung. Vitamin C für Kollagenbildung, Zink für Zellteilung. Ausreichend Flüssigkeit: 30-35 ml/kg/Tag.",
        keyPoints: [
          "40% der Wundpatienten mangelernährt",
          "Protein: 1,2-1,5 g/kg Körpergewicht/Tag",
          "Vitamin C, A, E und Zink wichtig",
          "Flüssigkeit: 30-35 ml/kg/Tag"
        ]
      },
      {
        id: "pfk_dokumentation",
        title: "Wunddokumentation",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/bb61367e6574b4c74c4dc261e7ab87848cd54ceb702555cf1e5295c768f4d6b4.png",
        explanation: "Was nicht dokumentiert ist, gilt als nicht durchgeführt! Dokumentiert werden: Lokalisation, Größe, Wundgrund, Exsudat, Wundränder, Schmerz, Maßnahmen. Fotodokumentation erfordert Patienteneinwilligung!",
        keyPoints: [
          "Vollständige Parameter erfassen",
          "Objektiv und nachvollziehbar",
          "Fotodokumentation mit Einwilligung",
          "DNQP-Expertenstandard beachten"
        ]
      }
    ]
  },
  // MODUL 9: Kommunikation & Beratung
  9: {
    title: "Kommunikation & Beratung",
    icon: "💬",
    description: "Patientenberatung und interdisziplinäre Kommunikation.",
    slides: [
      {
        id: "pfk_kommunikation",
        title: "Motivierende Gesprächsführung",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/4f1c6f4efb5443ff420500d805d12ff086a397ab74be79d5f51eb470a692c6c5.png",
        explanation: "Motivierende Gesprächsführung steigert die Therapieadhärenz! REGEL-Prinzipien: Resistenz vermindern, Empathie ausdrücken, Gegensätze aufzeigen, Eigenverantwortung fördern, Lösungskompetenz stärken. Teach-Back-Methode zur Verständniskontrolle!",
        keyPoints: [
          "REGEL-Prinzipien anwenden",
          "Aktives Zuhören und Paraphrasieren",
          "Teach-Back-Methode nutzen",
          "Empathische Rückmeldung geben"
        ]
      },
      {
        id: "pfk_sbar",
        title: "SBAR-Methode",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/4f1c6f4efb5443ff420500d805d12ff086a397ab74be79d5f51eb470a692c6c5.png",
        explanation: "Die SBAR-Methode strukturiert die interdisziplinäre Kommunikation: S = Situation (aktuelles Problem), B = Background (Hintergrund), A = Assessment (Einschätzung), R = Recommendation (Empfehlung). Nutzen Sie diese Methode bei Übergaben und Konsilen!",
        keyPoints: [
          "S = Situation: Was ist passiert?",
          "B = Background: Relevante Vorgeschichte",
          "A = Assessment: Fachliche Einschätzung",
          "R = Recommendation: Handlungsempfehlung"
        ]
      }
    ]
  }
};

// ===================== PDL-WEITERBILDUNG VISUAL CONTENT =====================
// Eigene Bilder für den 50-Modul-Kurs "Weiterbildung zur Pflegedienstleitung"

const pdlVisualContent = {
  // MODUL 1: Einführung in die PDL-Weiterbildung
  1: {
    title: "Einführung in die PDL-Weiterbildung",
    icon: "🎯",
    description: "Willkommen zur PDL-Weiterbildung: Orientierung, Ziele und erste Selbstreflexion.",
    slides: [
      {
        id: "pdl_einfuehrung_ziele",
        title: "Ziele der PDL-Weiterbildung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/f875c7aee9f5afa272c778b9b733945cd5f5d63b0f48bc3007bdbea0470f6521.png",
        explanation: "Die PDL-Weiterbildung entwickelt vier Kompetenzbereiche: Fachkompetenz (Führung, Recht, QM, BWL), Sozialkompetenz (Kommunikation, Konfliktmanagement), Selbstkompetenz (Reflexion, Resilienz) und Methodenkompetenz (Strategisches Denken, Planung). Das Berufsbild der PDL hat sich von administrativer Koordination zur strategischen Führungsposition gewandelt.",
        keyPoints: [
          "Fachkompetenz: Führung, Recht, QM, BWL",
          "Sozialkompetenz: Kommunikation, Konflikte",
          "Selbstkompetenz: Reflexion, Resilienz",
          "Methodenkompetenz: Strategie, Planung"
        ]
      },
      {
        id: "pdl_einfuehrung_rolle",
        title: "Der Führungskompass",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/f875c7aee9f5afa272c778b9b733945cd5f5d63b0f48bc3007bdbea0470f6521.png",
        explanation: "Entwickeln Sie Ihren persönlichen Führungskompass! Er besteht aus vier Elementen: Ihren Werten (z.B. Fairness, Verlässlichkeit), Ihren Stärken als Führungskraft, Ihren Entwicklungsfeldern und Ihrer Führungsvision. Typische Rollenkonflikte sind Loyalitätskonflikte (Träger vs. Team), Zeitkonflikte (Verwaltung vs. Präsenz) und Identitätskonflikte (Kollegin zu Vorgesetzter).",
        keyPoints: [
          "Meine Werte: Was ist mir wichtig?",
          "Meine Stärken: Was bringe ich mit?",
          "Meine Entwicklungsfelder: Was möchte ich lernen?",
          "Meine Führungsvision: Was möchte ich bewirken?"
        ]
      }
    ]
  },
  // MODUL 2: Aufgaben und rechtliche Verantwortung
  2: {
    title: "Aufgaben & Recht der PDL",
    icon: "⚖️",
    description: "Aufgabenprofil, rechtliche Grundlagen und Haftungsfragen der Pflegedienstleitung.",
    slides: [
      {
        id: "pdl_aufgaben_profil",
        title: "Das Aufgabenprofil der PDL",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/839be30fcb2db5cb8f37409653d654652eaded5cac6c58e2b37a5edbb2b9b0a4.png",
        explanation: "Die PDL-Aufgaben umfassen vier Kernbereiche: Personalmanagement (Dienstplanung, Mitarbeiterführung, Fortbildung), Pflegequalität (Standards, Audits, MDK-Prüfungen), Organisation (Prozesse, Ressourcen, Koordination) und Kommunikation (Schnittstellenfunktion zu allen Stakeholdern). Die rechtlichen Grundlagen umfassen SGB XI, Heimgesetze, BGB und StGB.",
        keyPoints: [
          "Personalmanagement: Führung & Entwicklung",
          "Pflegequalität: Standards & Prüfungen",
          "Organisation: Prozesse & Ressourcen",
          "Kommunikation: Schnittstelle zu allen"
        ]
      },
      {
        id: "pdl_recht_haftung",
        title: "Rechtliche Verantwortung & Haftung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/839be30fcb2db5cb8f37409653d654652eaded5cac6c58e2b37a5edbb2b9b0a4.png",
        explanation: "Die PDL haftet nicht nur für eigene Handlungen, sondern auch für Organisationsverschulden! Beispiele: fehlender Einarbeitungsplan, veraltete Standards, keine Dokumentationskontrolle. Bei freiheitsentziehenden Maßnahmen (FEM) ist immer eine richterliche Genehmigung oder wirksame Einwilligung erforderlich. Gute Dokumentation ist der beste Haftungsschutz!",
        keyPoints: [
          "SGB XI: Pflegeversicherung & Qualität",
          "Organisationsverschulden vermeiden",
          "FEM: Immer richterliche Genehmigung",
          "Dokumentation = Haftungsschutz"
        ]
      }
    ]
  },
  // MODUL 3: Organisationsstrukturen
  3: {
    title: "Organisationsstrukturen",
    icon: "🏢",
    description: "Aufbau- und Ablauforganisation, Organigramme und die PDL im Organisationsgefüge.",
    slides: [
      {
        id: "pdl_organisation_aufbau",
        title: "Aufbau- und Ablauforganisation",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/f340712720bb40408c734f89fa459ca9ab60d06ebc12d3d6cc0857c5fdf04fe4.png",
        explanation: "Das Organigramm zeigt die Aufbauorganisation: Wer ist wem unterstellt? Die typische Hierarchie: Träger/GF → Heimleitung → PDL → Wohnbereichsleitung → Pflegefachkräfte → Pflegehilfskräfte. Die Ablauforganisation regelt die Prozesse: Wie werden Aufgaben erledigt? Kernprozesse sind Pflegeprozess, Aufnahme/Entlassung und Medikamentenmanagement.",
        keyPoints: [
          "Aufbauorganisation: Wer → Wem unterstellt",
          "PDL: Fachliche Führung der Pflege",
          "Ablauforganisation: Wie → Prozesse",
          "Kernprozesse: Pflege, Aufnahme, Medikamente"
        ]
      },
      {
        id: "pdl_organisation_rolle",
        title: "Die PDL als Schnittstelle",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/f340712720bb40408c734f89fa459ca9ab60d06ebc12d3d6cc0857c5fdf04fe4.png",
        explanation: "Die PDL ist die zentrale Schnittstelle zwischen strategischer Führungsebene und operativem Pflegeteam. Ihr Rollenprofil umfasst: Führungsperson (Team führen), Managerin (Personal, Budget, Dienstplan), Fachexpertin (Standards, QM) und Kommunikatorin (Bindeglied zu allen). Die SWOT-Analyse ist ein wichtiges Instrument zur strategischen Standortbestimmung.",
        keyPoints: [
          "Führungsperson: Team mit verschiedenen Qualifikationen",
          "Managerin: Personal, Budget, Dienstplan",
          "Fachexpertin: Standards, Qualitätssicherung",
          "SWOT: Stärken, Schwächen, Chancen, Risiken"
        ]
      }
    ]
  },
  // MODUL 4: Von der Fachkraft zur Führungskraft
  4: {
    title: "Fachkraft → Führungskraft",
    icon: "🔄",
    description: "Der Rollenwechsel: Unterschiede, Fallen, Führungsstile und Delegation.",
    slides: [
      {
        id: "pdl_rollenwechsel_unterschied",
        title: "Der fundamentale Wandel",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/c8a319a72d6f74d6c41593d2fd875fc883cc0e732a06e740e7e625e326f13cf2.png",
        explanation: "Der Übergang von der Fachkraft zur Führungskraft ist ein fundamentaler Wandel. Als Fachkraft liegt der Fokus auf der direkten Patientenversorgung, als Führungskraft auf Team und Organisation. Der Erfolg misst sich nicht mehr an der einzelnen Pflegehandlung, sondern an Teamentwicklung und Qualität. Die Zeitperspektive wechselt von kurzfristig zu langfristig.",
        keyPoints: [
          "Fachkraft: Patientenversorgung direkt",
          "Führungskraft: Team & Organisation",
          "Handlung: Ausführen → Planen & Delegieren",
          "Zeit: Kurzfristig → Langfristig"
        ]
      },
      {
        id: "pdl_rollenwechsel_fallen",
        title: "Fallen und Führungsstile",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/c8a319a72d6f74d6c41593d2fd875fc883cc0e732a06e740e7e625e326f13cf2.png",
        explanation: "Typische Fallen beim Rollenwechsel: 'Ich mache es selbst' (führt zu Überlastung), 'Kumpel bleiben' (untergräbt Autorität), 'Alles fachlich lösen' (oft braucht es Struktur, keine Lösung). Führungsstile: Situatives Führen (an Reifegrad anpassen), Transformational (Inspiration), Servant Leadership (Bedingungen schaffen), Kooperativ (gemeinsame Entscheidungen).",
        keyPoints: [
          "Falle: Selbst machen → Überlastung",
          "Falle: Kumpel bleiben → Autoritätsverlust",
          "Delegieren: Aufgabe übertragen, Verantwortung behalten",
          "Grenzen setzen ist Führungskompetenz!"
        ]
      }
    ]
  },
  // MODUL 5: Rolle der Pflegedienstleitung (Vertiefung)
  5: {
    title: "Rolle der Pflegedienstleitung",
    icon: "👔",
    description: "Kernaufgaben der PDL: Pflegequalität, Personalführung und ethisches Handeln.",
    slides: [
      {
        id: "pdl_rolle_qualitaet",
        title: "Verantwortung für Pflegequalität",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/30fa3b0604c95c57e8fdfd3ccc669e6d0a9779a07fc3c4e953591577efa8cbe9.png",
        explanation: "Die PDL steht am Schnittpunkt von Qualitätsmanagement, Mitarbeiterführung und Ethik. Das Donabedian-Modell unterscheidet drei Dimensionen der Pflegequalität: Strukturqualität (Personal, Räume), Prozessqualität (Pflegeplanung, Dokumentation) und Ergebnisqualität (Dekubitusrate, Zufriedenheit). Der PDCA-Zyklus ist das zentrale Werkzeug zur kontinuierlichen Verbesserung.",
        keyPoints: [
          "Donabedian: Struktur-, Prozess-, Ergebnisqualität",
          "PDCA-Zyklus für kontinuierliche Verbesserung",
          "§ 113 SGB XI: MDK-Qualitätsprüfungen",
          "Qualität ist immer Teamarbeit!"
        ]
      },
      {
        id: "pdl_rolle_personal",
        title: "Personalführung und Teamentwicklung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/30fa3b0604c95c57e8fdfd3ccc669e6d0a9779a07fc3c4e953591577efa8cbe9.png",
        explanation: "Personalverantwortung hat drei Dimensionen: rechtlich (Arbeitsrecht, Arbeitszeitgesetz), fürsorglich (Gesundheitsschutz, Burnout-Prävention) und entwicklungsbezogen (Förderung, Weiterbildung). Die Teamentwicklung nach Tuckman verläuft in vier Phasen: Forming, Storming, Norming, Performing. Die PDL passt ihren Führungsstil an jede Phase an.",
        keyPoints: [
          "Drei Dimensionen: rechtlich, fürsorglich, entwicklungsbezogen",
          "Tuckman: Forming → Storming → Norming → Performing",
          "Gesunde Führung: § 5 ArbSchG Gefährdungsbeurteilung",
          "Reflexion als professionelle Kompetenz (Gibbs-Modell)"
        ]
      }
    ]
  },
  // MODUL 6: Abgrenzung und Schnittstellenmanagement
  6: {
    title: "Schnittstellenmanagement",
    icon: "🔗",
    description: "Rollenklarheit zwischen Träger, GF und PDL sowie das SBAR-Schema.",
    slides: [
      {
        id: "pdl_rollen_abgrenzung",
        title: "Die drei Leitungsebenen",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/f340712720bb40408c734f89fa459ca9ab60d06ebc12d3d6cc0857c5fdf04fe4.png",
        explanation: "Die Grafik zeigt die drei Leitungsebenen einer Pflegeeinrichtung: Der Träger ist für strategische Ziele, Zulassung und Finanzierung verantwortlich. Die Geschäftsführung steuert den operativ-kaufmännischen Bereich. Die PDL trägt die fachlich-pflegerische Verantwortung nach § 71 SGB XI. Klare Abgrenzung verhindert Konflikte und Kompetenzstreitigkeiten.",
        keyPoints: [
          "Träger: Strategie, Ziele, Zulassung",
          "GF: Operativ-kaufmännische Steuerung",
          "PDL: Fachlich-pflegerische Leitung (§ 71 SGB XI)",
          "Klare Rollenabgrenzung = weniger Konflikte"
        ]
      },
      {
        id: "pdl_sbar",
        title: "Das SBAR-Schema",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/f340712720bb40408c734f89fa459ca9ab60d06ebc12d3d6cc0857c5fdf04fe4.png",
        explanation: "SBAR ist ein international anerkanntes Kommunikationsmodell für strukturierte Übergaben. S steht für Situation (Was ist los?), B für Background (Relevanter Hintergrund), A für Assessment (Eigene Einschätzung), R für Recommendation (Handlungsvorschlag). Studien zeigen: SBAR reduziert vermeidbare Zwischenfälle bei Übergaben um bis zu 70%!",
        keyPoints: [
          "S - Situation: Was ist gerade los?",
          "B - Background: Relevanter Hintergrund",
          "A - Assessment: Eigene Einschätzung",
          "R - Recommendation: Handlungsvorschlag"
        ]
      }
    ]
  },
  // MODUL 7: Berufsethik und Verantwortung
  7: {
    title: "Berufsethik & Verantwortung",
    icon: "⚖️",
    description: "Ethisches Handeln, Wertekonflikte und strukturierte Entscheidungsfindung.",
    slides: [
      {
        id: "pdl_ethik_werte",
        title: "Werte als Fundament",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/c8a319a72d6f74d6c41593d2fd875fc883cc0e732a06e740e7e625e326f13cf2.png",
        explanation: "Die Waage symbolisiert die Balance ethischer Werte in der Pflege. Integrität bedeutet Übereinstimmung von Worten und Taten. Verantwortungsbewusstsein ist die Bereitschaft, für Folgen einzustehen. Gerechtigkeit bedeutet faire Behandlung aller. Fürsorge ist das aktive Wohlergehen anderer. Wertekonflikte zeigen, dass man ethisch denkt!",
        keyPoints: [
          "Integrität: Wort und Tat stimmen überein",
          "Verantwortung: Für Folgen einstehen",
          "Gerechtigkeit: Faire Behandlung aller",
          "Fürsorge: Wohlergehen als Verpflichtung"
        ]
      },
      {
        id: "pdl_verantwortung",
        title: "Vier Arten der Verantwortung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/c8a319a72d6f74d6c41593d2fd875fc883cc0e732a06e740e7e625e326f13cf2.png",
        explanation: "Handlungsverantwortung bezieht sich auf aktives Tun. Unterlassungsverantwortung: Auch Nicht-Handeln ist eine Entscheidung! Rollenverantwortung umfasst die Pflichten aus der beruflichen Position. Systemverantwortung bedeutet Mitverantwortung für Strukturen. Echte Verantwortungsübernahme zeigt sich darin, Fehler offen zu benennen und Lösungen vorzuschlagen.",
        keyPoints: [
          "Handlungsverantwortung: Für aktives Tun",
          "Unterlassungsverantwortung: Auch Schweigen ist Entscheidung",
          "Rollenverantwortung: Pflichten der Position",
          "Systemverantwortung: Missstände aufzeigen"
        ]
      }
    ]
  },
  // MODUL 8: Betriebsführung in der Pflege
  8: {
    title: "Betriebsführung",
    icon: "📊",
    description: "Pflege als Wirtschaftsbetrieb: Finanzierung, Kosten und Steuerungsinstrumente.",
    slides: [
      {
        id: "pdl_finanzierung",
        title: "Finanzierungsstruktur der Pflege",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/839be30fcb2db5cb8f37409653d654652eaded5cac6c58e2b37a5edbb2b9b0a4.png",
        explanation: "Die Pflegefinanzierung ruht auf drei Säulen: SGB XI (Pflegeversicherung) zahlt den einrichtungseinheitlichen Eigenanteil nach Pflegegrad. Der Eigenanteil der Bewohner umfasst Unterkunft, Verpflegung und Investitionskosten. SGB XII (Sozialhilfe) springt ein, wenn Kosten nicht selbst getragen werden können. Personalkosten machen 65-75% der Gesamtkosten aus!",
        keyPoints: [
          "SGB XI: Pflegeversicherung (EEE nach Pflegegrad)",
          "Eigenanteil: Unterkunft, Verpflegung, Investition",
          "SGB XII: Sozialhilfe als Auffangnetz",
          "Personalkosten: 65-75% der Gesamtkosten!"
        ]
      },
      {
        id: "pdl_wirtschaftlichkeit",
        title: "Wirtschaftlichkeitsgebot § 29 SGB XI",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/839be30fcb2db5cb8f37409653d654652eaded5cac6c58e2b37a5edbb2b9b0a4.png",
        explanation: "Das Wirtschaftlichkeitsgebot verlangt wirtschaftliche und sparsame Leistungserbringung - ohne dass Qualität leidet! Wirtschaftlich bedeutet: Mit vorhandenen Ressourcen bestmögliche Ergebnisse erzielen. Sparsam bedeutet: Unnötige Ausgaben vermeiden. Die Grenze: Qualität darf nie gefährdet werden! 95% Auslastung ist die wirtschaftliche Mindestmarke.",
        keyPoints: [
          "§ 29 SGB XI: Wirtschaftlichkeitsgebot",
          "Wirtschaftlich ≠ So wenig wie möglich ausgeben",
          "Grenze: Pflegequalität darf nicht leiden",
          "Ziel: Mindestens 95% Auslastung"
        ]
      }
    ]
  },
  // MODUL 9: Grundlagen der BWL in der Pflege
  9: {
    title: "BWL-Grundlagen",
    icon: "💰",
    description: "Betriebswirtschaftliche Grundbegriffe und Kennzahlen für die PDL.",
    slides: [
      {
        id: "pdl_bwl_kosten",
        title: "Kosten, Erlöse und Ergebnis",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/97622aa8205a24e276d799af91418a681eda2fe648b7805d4a2ba252e67af375.png",
        explanation: "Kosten sind der bewertete Verbrauch von Gütern und Dienstleistungen. Wir unterscheiden Personalkosten (Löhne, Sozialabgaben), Sachkosten (Material, Lebensmittel), Gemeinkosten (Miete, Energie) und Abschreibungen. Erlöse sind die Einnahmen durch Pflegeleistungen. Das Ergebnis ergibt sich aus: Erlöse minus Kosten gleich Gewinn oder Verlust.",
        keyPoints: [
          "Personalkosten: 65-75% der Gesamtkosten",
          "Fixe Kosten: Unabhängig von Leistungsmenge",
          "Variable Kosten: Ändern sich mit Leistung",
          "Ergebnis = Erlöse - Kosten"
        ]
      },
      {
        id: "pdl_kennzahlen",
        title: "Wichtige Kennzahlen für die PDL",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/97622aa8205a24e276d799af91418a681eda2fe648b7805d4a2ba252e67af375.png",
        explanation: "Das Magische Dreieck der Pflege zeigt drei zu balancierende Dimensionen: Pflegequalität, Wirtschaftlichkeit und Mitarbeiterzufriedenheit. Wichtige Kennzahlen sind: Personalkostenquote (Ziel 65-75%), Auslastungsgrad (Ziel 95%), Fachkraftquote (mindestens 50%), Krankenquote (unter 6%). Die PDL ist die zentrale Instanz, die dieses Dreieck moderiert.",
        keyPoints: [
          "Personalkostenquote: Ziel 65-75%",
          "Auslastungsgrad: Ziel mindestens 95%",
          "Fachkraftquote: Mindestens 50%",
          "Magisches Dreieck: Qualität-Wirtschaft-Personal"
        ]
      }
    ]
  },
  // MODUL 10: Kostenarten & Kostenstellen
  10: {
    title: "Kostenarten & Kostenstellen",
    icon: "📊",
    description: "Grundlagen der Kostenartenrechnung und Kostenstellenrechnung im Pflegebetrieb.",
    slides: [
      {
        id: "pdl_kostenarten",
        title: "Kostenarten im Pflegebetrieb",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/8a88c8b15b8ecb6bab705a7ee41f57770c5e7f1997201ac6c8c6dffdaeb53f3f35.png",
        explanation: "Die Kostenartenrechnung erfasst alle anfallenden Kosten nach ihrer Art. Personalkosten machen mit 60-75% den größten Anteil aus. Dazu kommen Sachkosten für Material, Raumkosten für Miete und Energie, sowie Abschreibungen für Gebäude und Geräte.",
        keyPoints: [
          "Personalkosten: 60-75% der Gesamtkosten",
          "Sachkosten: Pflegematerial, Lebensmittel",
          "Raumkosten: Miete, Heizung, Energie",
          "Abschreibungen: Gebäude, Fahrzeuge, IT"
        ]
      },
      {
        id: "pdl_kostenstellen",
        title: "Der Betriebsabrechnungsbogen (BAB)",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/8a88c8b15b8ecb6bab705a7ee41f57770c5e7f1997201ac6c8c6dffdaeb53f3f35.png",
        explanation: "Der BAB ist das zentrale Instrument zur Verteilung der Gemeinkosten auf Kostenstellen. Typische Kostenstellen: Wohnbereiche, Hauswirtschaft, Verwaltung, Küche. Verteilungsschlüssel wie Quadratmeter oder Mitarbeiteranzahl sorgen für verursachungsgerechte Zuordnung.",
        keyPoints: [
          "Zeilen: Kostenarten",
          "Spalten: Kostenstellen",
          "Verteilungsschlüssel definieren",
          "Transparenz für Pflegesatzverhandlungen"
        ]
      }
    ]
  },
  // MODUL 11: Kostenträger & Leistungen
  11: {
    title: "Kostenträger & Leistungen",
    icon: "💶",
    description: "SGB XI, SGB V und SGB XII - Die Säulen der Pflegefinanzierung.",
    slides: [
      {
        id: "pdl_kostentraeger",
        title: "Die drei Säulen der Pflegefinanzierung",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/5c9c3bf91cab350d0aaeb6bb5e97b80082d4c8fa4f06ea60ec396b3cdb8fea6f.png",
        explanation: "SGB XI ist die Pflegeversicherung mit Sachleistungen nach Pflegegrad. SGB V ist die Krankenversicherung für Behandlungspflege mit ärztlicher Verordnung. SGB XII ist die Sozialhilfe für Menschen, die Kosten nicht selbst tragen können.",
        keyPoints: [
          "SGB XI: Pflegeversicherung (761€ - 2.200€)",
          "SGB V: Behandlungspflege mit Verordnung",
          "SGB XII: Sozialhilfe als Auffangnetz",
          "Entlastungsbetrag: 125€ ab PG 1"
        ]
      },
      {
        id: "pdl_leistungen",
        title: "Leistungsdokumentation als Abrechnungsgrundlage",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/5c9c3bf91cab350d0aaeb6bb5e97b80082d4c8fa4f06ea60ec396b3cdb8fea6f.png",
        explanation: "Was nicht dokumentiert ist, kann nicht abgerechnet werden! Im ambulanten Bereich: Jeden Einsatz mit Datum, Uhrzeit und Leistungen dokumentieren. Im stationären Bereich: Pflegeanamnese, -planung und -bericht als Grundlage für MDK-Prüfung.",
        keyPoints: [
          "Dokumentation = Abrechnungsgrundlage",
          "Ambulant: Einsatzprotokoll je Besuch",
          "Stationär: Basis für Pflegesatzverhandlung",
          "MDK-Prüfung vorbereiten"
        ]
      }
    ]
  },
  // MODUL 12: Kosten- und Leistungsrechnung
  12: {
    title: "Kosten- und Leistungsrechnung",
    icon: "📈",
    description: "Vollkostenrechnung, Teilkostenrechnung und Break-even-Analyse.",
    slides: [
      {
        id: "pdl_klr",
        title: "Vollkosten- vs. Teilkostenrechnung",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/9dc58bf38c6a5c3b98b08dc4ee3f52e68e0d54ce2a34dc91ed8c84bb52c3eef0.png",
        explanation: "Vollkostenrechnung verteilt alle Kosten auf Kostenträger - Standard in der stationären Pflege. Teilkostenrechnung rechnet nur variable Kosten direkt zu. Der Deckungsbeitrag (Erlös - variable Kosten) zeigt, ob eine Leistung zur Fixkostendeckung beiträgt.",
        keyPoints: [
          "Vollkosten: Alle Kosten verteilen",
          "Teilkosten: Nur variable Kosten",
          "Deckungsbeitrag I = Erlös - variable Kosten",
          "Entscheidungshilfe: Lohnt sich die Leistung?"
        ]
      },
      {
        id: "pdl_breakeven",
        title: "Die Break-even-Analyse",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/9dc58bf38c6a5c3b98b08dc4ee3f52e68e0d54ce2a34dc91ed8c84bb52c3eef0.png",
        explanation: "Der Break-even-Punkt zeigt, ab welcher Auslastung die Einrichtung ihre Kosten deckt. Formel: Fixkosten ÷ Deckungsbeitrag pro Einheit. Unterhalb des Break-even: Verlust. Oberhalb: Gewinn. Kennen Sie Ihren Break-even!",
        keyPoints: [
          "Break-even = Kostendeckungspunkt",
          "Formel: Fixkosten ÷ DB pro Einheit",
          "Unterschreitung erfordert Handlung",
          "Monatliche Kontrolle empfohlen"
        ]
      }
    ]
  },
  // MODUL 13: Budgetplanung
  13: {
    title: "Budgetplanung",
    icon: "📋",
    description: "Budgetarten, Soll-Ist-Vergleiche und Kennzahlen für die Budgetkontrolle.",
    slides: [
      {
        id: "pdl_budget",
        title: "Budgetarten und Aufbau",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/8d8523bfb3d6a26cc3df9fd5f6ad6f9f2c5e11deb3e3fe9c26fd61efa3c1e7bd.png",
        explanation: "Ein Budget ist ein in Zahlen ausgedrückter Plan. Wichtige Budgetarten: Personalbudget (größter Posten), Sachkostenbudget, Investitionsbudget, Erlösbudget. Der Aufbau erfolgt in 4 Schritten: Vorjahresanalyse, Erlösplanung, Kostenplanung, Abstimmung.",
        keyPoints: [
          "Personalbudget: 65-75% der Kosten",
          "Sachkostenbudget: Material, Büro",
          "Erlösplanung: Belegungsquote zentral",
          "Abstimmung mit Trägerleitung"
        ]
      },
      {
        id: "pdl_sollIst",
        title: "Soll-Ist-Vergleich & Kennzahlen",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/8d8523bfb3d6a26cc3df9fd5f6ad6f9f2c5e11deb3e3fe9c26fd61efa3c1e7bd.png",
        explanation: "Der monatliche Soll-Ist-Vergleich ist Pflicht! Ab 5% Abweichung: schriftliche Erklärung. Wichtige Kennzahlen: Belegungsquote ≥95%, Personalkostenquote 65-75%, Überstundenquote ≤8%, Ausfallquote ≤5%.",
        keyPoints: [
          "Belegungsquote: Ziel ≥95%",
          "Personalkostenquote: 65-75%",
          "Überstundenquote: max. 8%",
          "Monatliche Kontrolle ist Pflicht"
        ]
      }
    ]
  },
  // MODUL 14: Ressourcenmanagement
  14: {
    title: "Ressourcenmanagement",
    icon: "⚖️",
    description: "Personal als wirtschaftlicher Faktor und der Zielkonflikt Sparen vs. Qualität.",
    slides: [
      {
        id: "pdl_personal",
        title: "Personal als wirtschaftlicher Faktor",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/296d6fd820f48d2c9c1eef2ddfcbf9c002cdbab9a880cb942d0967b17886.png",
        explanation: "Personal ist die wichtigste und teuerste Ressource: 65-75% der Kosten! Versteckte Kosten: Rekrutierung, Einarbeitung, Fluktuation. Leiharbeit ist 30-50% teurer! Bindungsmaßnahmen sind langfristig günstiger als häufige Neubesetzungen.",
        keyPoints: [
          "Personalkosten: 65-75% der Kosten",
          "Leiharbeit: 30-50% teurer",
          "Fluktuation ist teuer",
          "Bindung statt Neubesetzung"
        ]
      },
      {
        id: "pdl_sparenQualitaet",
        title: "Sparen vs. Qualität",
        image: "https://static.prod-images.emergentagent.com/jobs/64153e2a-6e99-415d-91de-c12ab1b2f9e1/images/296d6fd820f48d2c9c1eef2ddfcbf9c002cdbab9a880cb942d0967b17886.png",
        explanation: "Die Grenze: Die körperliche oder psychische Unversehrtheit von Bewohnern und Mitarbeitenden darf nie gefährdet werden! Sparen funktioniert bei Prozessoptimierung und Einkauf. Sparen schadet bei Personaluntergrenzen und Sicherheitsstandards.",
        keyPoints: [
          "Sparen OK: Prozesse, Einkauf, Energie",
          "Sparen NICHT OK: Personaluntergrenzen",
          "Grenze: Unversehrtheit aller",
          "PDL = Manager UND Anwalt"
        ]
      }
    ]
  },
  // MODUL 15: Change Management
  15: {
    title: "Change Management",
    icon: "🔄",
    description: "Einführung in Veränderungsprozesse: Phasenmodelle und die Rolle der PDL.",
    slides: [
      {
        id: "pdl_change_grundlagen",
        title: "Veränderung vs. Transformation",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/97622aa8205a24e276d799af91418a681eda2fe648b7805d4a2ba252e67af375.png",
        explanation: "Veränderung (Change) sind begrenzte Anpassungen innerhalb bestehender Strukturen – z.B. neue Software oder Dienstplanumstellung. Transformation dagegen verändert fundamental: Werte, Kultur und Selbstverständnis der Organisation. Lewins 3-Phasen-Modell: Auftauen (Status quo lockern) → Bewegen (Neues einführen) → Einfrieren (Neues stabilisieren).",
        keyPoints: [
          "Change: Anpassung im Rahmen",
          "Transformation: Kulturwandel",
          "Lewin: Auftauen → Bewegen → Einfrieren",
          "Goldene Regel: 3x mehr kommunizieren!"
        ]
      },
      {
        id: "pdl_change_rolle",
        title: "Die PDL als Gestalterin",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/97622aa8205a24e276d799af91418a681eda2fe648b7805d4a2ba252e67af375.png",
        explanation: "Die PDL in der Sandwichposition: Von oben kommen Umsetzungsvorgaben, von unten der Wunsch nach Schutz und Stabilität. Ihre Rollen: Orientierungsgeberin (das Warum kommunizieren), Vermittlerin (zwischen Träger und Team), Stabilisatorin (Sicherheit durch Struktur) und Motivatorin (Veränderung als Chance). Widerstand ist kein Versagen – er ist ein wertvolles Signal!",
        keyPoints: [
          "Sandwichposition: Oben & Unten",
          "Rollen: Orientierung, Vermittlung, Stabilität",
          "Widerstand = wertvolles Signal",
          "Transparent kommunizieren!"
        ]
      }
    ]
  },
  // MODUL 16: Organisationsentwicklung
  16: {
    title: "OE & Analyseinstrumente",
    icon: "📊",
    description: "SWOT-Analyse, Ist-/Soll-Analyse und Stakeholder-Management.",
    slides: [
      {
        id: "pdl_oe_swot",
        title: "Die SWOT-Analyse",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/97622aa8205a24e276d799af91418a681eda2fe648b7805d4a2ba252e67af375.png",
        explanation: "SWOT ist ein mächtiges Analysewerkzeug mit vier Feldern: Strengths (Stärken) – Was läuft gut intern? Weaknesses (Schwächen) – Wo gibt es Probleme? Opportunities (Chancen) – Welche externen Möglichkeiten? Threats (Risiken) – Welche Gefahren von außen? Durchführung: Fokusthema definieren → Team füllt Felder → Schwerpunkte herausarbeiten → Maßnahmen ableiten.",
        keyPoints: [
          "S: Strengths (Stärken)",
          "W: Weaknesses (Schwächen)",
          "O: Opportunities (Chancen)",
          "T: Threats (Risiken)"
        ]
      },
      {
        id: "pdl_oe_stakeholder",
        title: "Stakeholder-Analyse",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/97622aa8205a24e276d799af91418a681eda2fe648b7805d4a2ba252e67af375.png",
        explanation: "Stakeholder sind alle Interessengruppen: Pflegekräfte, Leitung, Bewohnende, Angehörige, Behörden... Analyseschritte: 1. Einfluss einschätzen – Wer kann fördern oder blockieren? 2. Haltung analysieren – Befürwortend, neutral oder ablehnend? 3. Beteiligung planen – Wer braucht was? Schlüssel-Stakeholder haben hohen Einfluss UND hohe Betroffenheit – frühzeitig einbinden!",
        keyPoints: [
          "Stakeholder: Alle Interessengruppen",
          "Einfluss & Haltung analysieren",
          "Schlüssel-Stakeholder früh einbinden",
          "OE braucht 12-24 Monate!"
        ]
      }
    ]
  },
  // MODUL 17: Entwicklungsfelder & Kennzahlen
  17: {
    title: "Kennzahlen & Entwicklungsfelder",
    icon: "📈",
    description: "Datengestützte Entscheidungsfindung mit Kennzahlen und Feedbacksystemen.",
    slides: [
      {
        id: "pdl_kennzahlen_ueberblick",
        title: "Kennzahlen als Führungsinstrument",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/e4ae5192f6562739b3011467cf3f27d211b5b8670aaa995148afccb4834d1966.png",
        explanation: "Kennzahlen sind der Puls Ihrer Einrichtung! Pflegequalität: Dekubitusrate < 2%, Sturzrate, FEM. Personalkennzahlen: Krankenquote < 5%, Fluktuationsrate < 10%, Fachkraftquote ≥ 50%. Wirtschaft: Auslastungsgrad ≥ 95%. Personalkosten machen 60-70% aus – Personalplanung ist das wichtigste Steuerungsinstrument! Trendanalyse wichtiger als Einzelwerte.",
        keyPoints: [
          "Krankenquote: Ziel < 5%",
          "Fluktuation: Ziel < 10% p.a.",
          "Auslastung: Ziel ≥ 95%",
          "Personal = 60-70% der Kosten"
        ]
      },
      {
        id: "pdl_feedback_systeme",
        title: "Feedbacksysteme etablieren",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/e4ae5192f6562739b3011467cf3f27d211b5b8670aaa995148afccb4834d1966.png",
        explanation: "Intern: Teambesprechungen, Übergaben, Vorschlagswesen, CIRS, Mitarbeitergespräche. Extern: Bewohner-/Angehörigenbefragungen, Beschwerdemanagement, MDK-Ergebnisse. Mitarbeitendenbefragungen: Kurz (10-15 Fragen), anonym, Ergebnisse zurückmelden! Größte Falle: Ergebnisse verschwinden in der Schublade. Fluktuation kostet 30.000-50.000 € pro Stelle!",
        keyPoints: [
          "Intern: Team, Übergaben, CIRS",
          "Extern: Bewohner, Beschwerden, MDK",
          "Befragungen: Ergebnisse zeigen!",
          "Fluktuation: 30-50T€ pro Stelle"
        ]
      }
    ]
  },
  // MODUL 18: Projektmanagement
  18: {
    title: "Projektmanagement",
    icon: "📋",
    description: "SMART-Ziele, Projektphasen, Ressourcen- und Risikoplanung.",
    slides: [
      {
        id: "pdl_projekt_smart",
        title: "SMART-Ziele und Projektphasen",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/e4ae5192f6562739b3011467cf3f27d211b5b8670aaa995148afccb4834d1966.png",
        explanation: "SMART-Ziele: Spezifisch (Was genau?), Messbar (Wie messen?), Attraktiv (Motivierend?), Realistisch (Erreichbar?), Terminiert (Bis wann?). Die vier Projektphasen: 1. Initiierung – Idee, Machbarkeit, Projektauftrag. 2. Planung – Ziele, Ressourcen, Zeitplan, Risiken. 3. Durchführung – Umsetzen, Kommunizieren, Überwachen. 4. Abschluss (oft unterschätzt!) – Übergabe, Reflexion, Wissenssicherung.",
        keyPoints: [
          "S: Spezifisch – Was genau?",
          "M: Messbar – Wie prüfen?",
          "R: Realistisch – Machbar?",
          "T: Terminiert – Bis wann?"
        ]
      },
      {
        id: "pdl_projekt_risiko",
        title: "Ressourcen und Risiken",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/e4ae5192f6562739b3011467cf3f27d211b5b8670aaa995148afccb4834d1966.png",
        explanation: "Ressourcenplanung: Personal (Wer wann?), Finanzen (Budget + 10-15% Puffer!), Zeit (realistisch!), Material. Typische Risiken: Personalmangel → Vertretungsregelung. Fehlende Rückendeckung → Projektauftrag formell. Widerstand → Frühzeitig kommunizieren. Verzögerungen → Puffer einplanen. Das Eisberg-Modell: Oberhalb = Aufgaben, Ziele. Unterhalb = Ängste, Beziehungen – dort entstehen die meisten Probleme!",
        keyPoints: [
          "Budget: +10-15% Puffer",
          "Risiko: Vertretungsregelung",
          "Risiko: Frühzeitig kommunizieren",
          "Eisberg: Unterhalb = Probleme"
        ]
      }
    ]
  },
  // MODUL 19: Digitalisierung & Widerstand
  19: {
    title: "Digitalisierung & Widerstand",
    icon: "💻",
    description: "Digitale Transformation, Chancen und Risiken, Widerstandsmanagement.",
    slides: [
      {
        id: "pdl_digital_chancen",
        title: "Digitalisierung in der Pflege",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/7c7213302ad2c206b7bbadab74f0c36295e0df83bb72e5b70d9b369b83b13a5b.png",
        explanation: "Chancen: Qualitätssteigerung durch lückenlose Dokumentation, Effizienz (bis 30 Min/Schicht sparen!), Transparenz, Arbeitgeberattraktivität. Risiken: Datenschutz, technische Ausfälle, digitale Spaltung, hohe Kosten, Akzeptanzprobleme. Rechtliche Grundlagen: § 113 SGB XI, DSGVO, 10 Jahre Aufbewahrung. Automatisierte Workflows verhindern Vergessen wichtiger Schritte.",
        keyPoints: [
          "Zeitersparnis: bis 30 Min/Schicht",
          "Risiko: Datenschutz beachten",
          "Risiko: Notfallpläne für Ausfälle",
          "Rechtlich: DSGVO, 10 J. Aufbewahrung"
        ]
      },
      {
        id: "pdl_widerstand_umgang",
        title: "Professioneller Umgang mit Widerstand",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/7c7213302ad2c206b7bbadab74f0c36295e0df83bb72e5b70d9b369b83b13a5b.png",
        explanation: "Ursachen: Angst vor Unbekanntem, mangelnde Information, schlechte Erfahrungen, befürchteter Mehraufwand. Strategien: 1. Frühzeitig informieren (vor Gerüchten!). 2. Aktiv zuhören. 3. Pilotphasen nutzen. 4. Erfolge feiern. Phasen des Widerstands: Schock → Ablehnung → Einsicht → Akzeptanz → Integration. Change Agents sind engagierte MA als Botschafter. Partizipation ist nicht Konsens – die PDL entscheidet!",
        keyPoints: [
          "Früh informieren, aktiv zuhören",
          "Pilotphasen & Erfolge feiern",
          "Phasen: Schock → Integration",
          "Change Agents als Multiplikatoren"
        ]
      }
    ]
  },
  // MODUL 20: Kennzahlen & Controlling
  20: {
    title: "Kennzahlen & Controlling",
    icon: "📊",
    description: "Systematische Erfassung und Analyse von Kennzahlen für faktengestützte Führung.",
    slides: [
      {
        id: "pdl_kennzahlen_arten",
        title: "Die vier Kennzahlenbereiche",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/7c7213302ad2c206b7bbadab74f0c36295e0df83bb72e5b70d9b369b83b13a5b.png",
        explanation: "Personalkennzahlen: Krankenquote (Ziel < 8-10%), Fluktuationsrate, Überstunden, Fortbildungsquote. Qualitätskennzahlen: Dekubitusrate, Sturzrate, FEM, Kundenzufriedenheit. Finanzkennzahlen: Personalkosten pro Stunde, Belegungsquote (Ziel ≥ 95%). Strukturkennzahlen: Pflegegrad-Verteilung, Betreuungsschlüssel. Tipp: Kennzahlen gewinnen erst im Vergleich Wert!",
        keyPoints: [
          "Krankenquote: Ziel < 8-10%",
          "Belegungsquote: Ziel ≥ 95%",
          "Fluktuation = teure Kündigungen",
          "Vergleich macht Kennzahlen wertvoll"
        ]
      },
      {
        id: "pdl_controlling_systeme",
        title: "PDCA und Balanced Scorecard",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/7c7213302ad2c206b7bbadab74f0c36295e0df83bb72e5b70d9b369b83b13a5b.png",
        explanation: "PDCA-Zyklus: Plan (Ziele definieren) → Do (umsetzen) → Check (messen) → Act (korrigieren). Balanced Scorecard mit vier Perspektiven: Finanzen, Kunden/Bewohner, Interne Prozesse, Lernen/Entwicklung. Kennzahlen-Ampel: 🟢 Ziel erreicht, 🟡 Beobachtung nötig, 🔴 Sofortiger Handlungsbedarf. Moderne Software bietet automatisierte Berichte und Frühwarnsysteme.",
        keyPoints: [
          "PDCA: Plan → Do → Check → Act",
          "BSC: 4 Perspektiven ausgewogen",
          "Ampel: Grün-Gelb-Rot-System",
          "Software: Dashboards & Alarme"
        ]
      }
    ]
  },
  // MODUL 21: Pflegeorganisation & Prozesse
  21: {
    title: "Organisation & Prozesse",
    icon: "🔄",
    description: "Aufbau- und Ablauforganisation, Prozessmanagement und Optimierung.",
    slides: [
      {
        id: "pdl_organisation_aufbau",
        title: "Aufbau- und Ablauforganisation",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/30fa3b0604c95c57e8fdfd3ccc669e6d0a9779a07fc3c4e953591577efa8cbe9.png",
        explanation: "Aufbauorganisation: WER ist WEM rechenschaftspflichtig? Organigramm, Stellenbeschreibungen, Hierarchie. Ablauforganisation: WIE wird Arbeit getan? Prozesse, Zeitabläufe, Dienstplanung, Übergaben. Das Stablinienmodell verbindet klare Hierarchie mit beratenden Stabsstellen (QMB) und überwiegt in der Praxis. Drei Prozesskategorien: Kern-, Unterstützungs- und Führungsprozesse.",
        keyPoints: [
          "Aufbau: WER → WEM (Hierarchie)",
          "Ablauf: WIE → WANN (Prozesse)",
          "Stablinienmodell = Praxisstandard",
          "3 Kategorien: Kern, Support, Führung"
        ]
      },
      {
        id: "pdl_prozess_optimierung",
        title: "Prozessoptimierung & QM",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/30fa3b0604c95c57e8fdfd3ccc669e6d0a9779a07fc3c4e953591577efa8cbe9.png",
        explanation: "Methoden: PDCA-Zyklus für kontinuierliche Verbesserung. SAA (Standardarbeitsanweisungen) für Einheitlichkeit. Prozessvisualisierung macht Engpässe sichtbar. FMEA für proaktive Risikobewertung. Lean-Prinzipien eliminieren Verschwendung. QM ist Pflicht nach § 113 SGB XI: Prozesse definieren → implementieren → überwachen → verbessern.",
        keyPoints: [
          "PDCA: Kontinuierliche Verbesserung",
          "SAA: Einheitliche Standards",
          "Lean: Verschwendung eliminieren",
          "QM nach § 113 SGB XI ist Pflicht"
        ]
      }
    ]
  },
  // MODUL 22: Zielkonflikte & Entscheidungen
  22: {
    title: "Zielkonflikte & Entscheidungen",
    icon: "⚖️",
    description: "Wirtschaftlichkeit vs. Qualität, Entscheidungswerkzeuge und Ethik.",
    slides: [
      {
        id: "pdl_zielkonflikte_arten",
        title: "Arten von Zielkonflikten",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/e4ae5192f6562739b3011467cf3f27d211b5b8670aaa995148afccb4834d1966.png",
        explanation: "Ressourcenkonflikte: Zeit, Personal, Geld sind begrenzt. Interessenkonflikte: Träger, MA, Bewohner, Angehörige haben verschiedene Ziele. Normenkonflikte: Pflegestandards vs. Gesetze vs. interne Vorgaben. Zeitkonflikte: Kurzfristige Anforderungen vs. langfristige Entwicklung. Der strukturierte Analyseprozess: Erkennen → Analysieren → Bewerten → Entscheiden. Wer Konflikte nicht explizit macht, entscheidet implizit!",
        keyPoints: [
          "Ressourcen: Zeit, Personal, Geld",
          "Interessen: Viele Stakeholder",
          "Normen: Standards vs. Gesetze",
          "Prozess: Erkennen → Entscheiden"
        ]
      },
      {
        id: "pdl_ethik_entscheidung",
        title: "Ethik als Entscheidungsgrundlage",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/e4ae5192f6562739b3011467cf3f27d211b5b8670aaa995148afccb4834d1966.png",
        explanation: "Die vier ethischen Grundprinzipien: 1. Autonomie – Wille des Bewohners hat Vorrang. 2. Nicht-Schaden – Schädliche Maßnahmen unterlassen. 3. Fürsorge – Aktiv schützen und fördern. 4. Gerechtigkeit – Faire Ressourcenverteilung. Werkzeuge: Nutzwertanalyse, Kosten-Nutzen-Abwägung, Priorisierungsmatrix (Eisenhower), ethische Fallbesprechung. Eine Entscheidung ist nur so gut wie ihre Kommunikation!",
        keyPoints: [
          "Autonomie: Bewohnerwille zuerst",
          "Nicht-Schaden: Primum non nocere",
          "Fürsorge: Aktiv schützen",
          "Gerechtigkeit: Fair verteilen"
        ]
      }
    ]
  },
  // MODUL 23: Recht & Haftung
  23: {
    title: "Recht & Haftung",
    icon: "⚖️",
    description: "Gesetzliche Grundlagen, Haftungsdimensionen und Organisationsverschulden.",
    slides: [
      {
        id: "pdl_recht_grundlagen",
        title: "Gesetzliche Grundlagen",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/839be30fcb2db5cb8f37409653d654652eaded5cac6c58e2b37a5edbb2b9b0a4.png",
        explanation: "SGB XI: Pflegeversicherung, Leistungsansprüche, Qualitätsanforderungen. SGB V: Krankenversicherung, Behandlungspflege. Heimgesetze: Betriebs- und Aufsichtsvorschriften, Bewohnerrechte. Arbeitsrecht: Arbeitszeitgesetz, Kündigungsschutz, Tarifverträge. Weitere: DSGVO, Infektionsschutzgesetz, Betreuungsrecht. Externe Prüfinstanzen: MDK, Heimaufsicht, Gesundheitsamt, Pflegekassen.",
        keyPoints: [
          "SGB XI: Pflegeversicherung",
          "Heimgesetze: Betriebsvorschriften",
          "Arbeitsrecht: MA-Schutz",
          "Prüfinstanzen: MDK, Heimaufsicht"
        ]
      },
      {
        id: "pdl_haftung_delegation",
        title: "Haftung und Delegation",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/839be30fcb2db5cb8f37409653d654652eaded5cac6c58e2b37a5edbb2b9b0a4.png",
        explanation: "Drei Haftungsdimensionen: 1. Zivilrechtlich (Schadensersatz nach BGB §§ 823 ff.). 2. Strafrechtlich (Körperverletzung, unterlassene Hilfeleistung). 3. Arbeitsrechtlich (Abmahnung, Kündigung). Organisationsverschulden: Haftung für strukturelle Mängel! Fehlende Einarbeitung, veraltete Standards, Unterschreitung Fachkraftquote. Delegation: Anordnungsverantwortung (PDL) + Übernahmeverantwortung (MA) + Kontrollpflicht (PDL)!",
        keyPoints: [
          "3 Dimensionen: Zivil, Straf, Arbeit",
          "Organisationsverschulden = strukturelle Mängel",
          "Delegation ≠ Verantwortung abgeben",
          "FEM: Immer Richter-Genehmigung!"
        ]
      }
    ]
  },
  // MODUL 24: Modulabschluss & Transfer
  24: {
    title: "Abschluss & Transfer",
    icon: "🎓",
    description: "Wiederholung, Reflexion und konkreter Praxistransfer.",
    slides: [
      {
        id: "pdl_abschluss_reflexion",
        title: "Feedback als Führungskompetenz",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/c8a319a72d6f74d6c41593d2fd875fc883cc0e732a06e740e7e625e326f13cf2.png",
        explanation: "Konstruktives Feedback geben: Beobachtungen beschreiben (nicht bewerten), Wirkung benennen, Veränderungsvorschläge formulieren. Feedback empfangen: Zuhören ohne Rechtfertigung, nachfragen, Zeit für Verarbeitung nehmen. Reflexionsfragen: Was hat überrascht? Was war der Aha-Moment? Wo besteht noch Entwicklungsbedarf?",
        keyPoints: [
          "Feedback: Beschreiben, nicht bewerten",
          "Empfangen: Zuhören ohne Rechtfertigung",
          "Reflexion: Was nehme ich mit?",
          "Wachstum durch professionelles Feedback"
        ]
      },
      {
        id: "pdl_transfer_praxis",
        title: "Mein persönlicher Aktionsplan",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/c8a319a72d6f74d6c41593d2fd875fc883cc0e732a06e740e7e625e326f13cf2.png",
        explanation: "Sofortmaßnahmen (Woche 1): Eine kleine, konkrete Aktion ohne Budget. Kurzfristige Ziele (Monat 1): 1-2 erreichbare Vorhaben mit Enddatum. Mittelfristige Entwicklung (Quartal): Strukturelle Veränderungen. Accountability-Partnering: Aktionsplan mit Kollegen teilen verdoppelt Umsetzungswahrscheinlichkeit! Führung in der Pflege ist Verantwortung gegenüber Mitarbeitenden, Bewohnern und Angehörigen.",
        keyPoints: [
          "Woche 1: Sofortmaßnahme starten",
          "Monat 1: Messbare Ziele setzen",
          "Quartal: Strukturen verändern",
          "Partner finden = Erfolg verdoppeln"
        ]
      }
    ]
  },
  // MODUL 25: Kommunikation im Veränderungsprozess
  25: {
    title: "Kommunikation im Veränderungsprozess",
    icon: "💬",
    description: "Transparenz, Beteiligung und Führungsstile bei Veränderungen.",
    slides: [
      {
        id: "pdl_kommunikation_transparenz",
        title: "Kommunikationsstrategien bei Veränderung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/7f955b0838917a6ad9fb3ab9743baa9a8ef3a510200150ceac0e1cbcd4afeeaf.png",
        explanation: "Transparenz ist ein Führungsinstrument: Die richtigen Informationen zum richtigen Zeitpunkt. Typische Fallstricke vermeiden: Informationen nicht zurückhalten, keine reine Top-Down-Kommunikation, emotionale Einordnung geben. Wirkung: Reduktion von Gerüchten, erhöhte Akzeptanz, psychologische Sicherheit. Die vier Steuerungsdimensionen: Wer kommuniziert? Was? Wann? Wie?",
        keyPoints: [
          "Transparenz ≠ alle Infos preisgeben",
          "Gerüchte durch offene Kommunikation vermeiden",
          "4 Dimensionen: Wer, Was, Wann, Wie",
          "Psychologische Sicherheit schaffen"
        ]
      },
      {
        id: "pdl_fuehrungsstil_veraenderung",
        title: "Führungsstile bei Veränderungen",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/7f955b0838917a6ad9fb3ab9743baa9a8ef3a510200150ceac0e1cbcd4afeeaf.png",
        explanation: "Situatives Führen kombiniert Aufgaben- und Beziehungsorientierung. Vier Stile: Anweisen (für Neue), Verkaufen (für Lernende), Beteiligen (für Erfahrene mit Unsicherheit), Delegieren (für Experten). In Krisen: Autoritärer Stil mit klaren Direktiven. Bei komplexen Problemen: Kooperativer Stil mit Einbezug. Kernkompetenz: Flexibler Wechsel je nach Situation!",
        keyPoints: [
          "Situatives Führen = Flexibilität",
          "4 Stile nach Reifegrad des MA",
          "Krisen: Autoritär, klar, schnell",
          "Komplex: Kooperativ, einbeziehend"
        ]
      }
    ]
  },
  // MODUL 26: Evaluation & Nachhaltigkeit
  26: {
    title: "Evaluation & Nachhaltigkeit",
    icon: "📊",
    description: "Erfolgskontrolle, KPIs und der PDCA-Zyklus.",
    slides: [
      {
        id: "pdl_erfolgskontrolle_kpis",
        title: "Erfolgskontrolle & Kennzahlen",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/e76a2000f3de8436440cafcd44b7d4d80da4ea0c47501cab4b2fba153e53b209.png",
        explanation: "Der Regelkreis: Ziele setzen → Maßnahmen umsetzen → Ergebnisse messen → auswerten → korrigieren. Klinische KPIs: Sturz-, Dekubitusrate, Medikamentenfehler. Personal-KPIs: Krankenquote, Fluktuation, Überstunden. Kunden-KPIs: Zufriedenheitsindex, Beschwerden. Das Donabedian-Modell: Struktur-, Prozess- und Ergebnisqualität als Zusammenspiel.",
        keyPoints: [
          "Regelkreis für lernendes QM",
          "KPIs: Klinisch, Personal, Kunden",
          "Donabedian: Struktur→Prozess→Ergebnis",
          "Zielkorridore und Alarmwerte definieren"
        ]
      },
      {
        id: "pdl_pdca_nachhaltigkeit",
        title: "PDCA-Zyklus & Verstetigung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/e76a2000f3de8436440cafcd44b7d4d80da4ea0c47501cab4b2fba153e53b209.png",
        explanation: "PDCA: Plan (Problem analysieren, Ziele setzen) → Do (Maßnahmen umsetzen) → Check (Ergebnisse prüfen) → Act (Standardisieren oder anpassen). Verstetigung auf 4 Ebenen: Strukturell verankern, Schulen und üben, Monitoren und rückmelden, Kultur prägen. Rückfälle sind normal – Ursachenanalyse statt Schuldzuweisung!",
        keyPoints: [
          "PDCA: Plan-Do-Check-Act",
          "4 Ebenen der Verstetigung",
          "Rückfälle analysieren, nicht sanktionieren",
          "Qualität = gelebte Haltung"
        ]
      }
    ]
  },
  // MODUL 27: Gesprächsführung – Grundlagen
  27: {
    title: "Gesprächsführung – Grundlagen",
    icon: "🗣️",
    description: "Sender-Empfänger-Modell und Kommunikationsstörungen.",
    slides: [
      {
        id: "pdl_sender_empfaenger",
        title: "Das Sender-Empfänger-Modell",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/5bc38f44539047ef9ef859702a4b333f7b2eba9b4a452b173f4747442c54ff01.png",
        explanation: "Sender kodiert die Botschaft → Kanal überträgt → Empfänger dekodiert → Feedback schließt den Kreis. Missverständnisse entstehen an den Übergängen! Störungsarten: Semantisch (unterschiedliche Bedeutungen), Emotional (Stress beeinflusst), Organisational (unklare Strukturen), Kulturell (verschiedene Kommunikationsstile). Kommunikative Präzision = Versorgungsqualität!",
        keyPoints: [
          "Kodieren → Kanal → Dekodieren → Feedback",
          "Missverständnisse an Übergängen",
          "4 Störungsarten erkennen",
          "Präzision sichert Qualität"
        ]
      },
      {
        id: "pdl_sprache_fuehrung",
        title: "Die Wirkung von Sprache",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/5bc38f44539047ef9ef859702a4b333f7b2eba9b4a452b173f4747442c54ff01.png",
        explanation: "Sach- und Beziehungsebene: Wenn die Beziehung gestört ist, kommt Sachliches nicht an! Vier-Ohren-Modell: Sachinhalt, Selbstoffenbarung, Beziehung, Appell. Spracharten: Aktivierend (motiviert), Wertschätzend (konkret loben), Grenzsetzend (klar aber respektvoll), Fragend (öffnet Dialog). Sprache ist das mächtigste Führungswerkzeug!",
        keyPoints: [
          "Beziehung vor Sachinhalt",
          "4 Ohren: Sache, Selbst, Beziehung, Appell",
          "Aktivierend, Wertschätzend, Fragend",
          "Sprache = mächtigstes Werkzeug"
        ]
      }
    ]
  },
  // MODUL 28: Gesprächsführung – Gesprächsarten
  28: {
    title: "Gesprächsführung – Gesprächsarten",
    icon: "📋",
    description: "Mitarbeitergespräche, Zielvereinbarungen und SMART-Ziele.",
    slides: [
      {
        id: "pdl_gespraechsarten",
        title: "Gesprächsarten in der Führung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/0377e3b1b853aeac5ed4795512a6c24469505553ebeac1128fb8d06b954a9b95.png",
        explanation: "Mitarbeitergespräch: Regelmäßig (min. jährlich), strukturiert, vertraulich, dokumentiert. Ziele: Standortbestimmung, Erwartungen klären, Beziehung stärken. Zielvereinbarung mit SMART: Spezifisch, Messbar, Attraktiv, Realistisch, Terminiert. Entwicklungsgespräch: Fokus auf Stärken und Zukunft – PDL als Mentor/in!",
        keyPoints: [
          "MA-Gespräch: min. jährlich",
          "SMART-Ziele vereinbaren",
          "Entwicklung: Stärken & Zukunft",
          "PDL als Mentor/in"
        ]
      },
      {
        id: "pdl_strukturierte_fuehrung",
        title: "Strukturierte Gesprächsführung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/0377e3b1b853aeac5ed4795512a6c24469505553ebeac1128fb8d06b954a9b95.png",
        explanation: "Drei Phasen: Vorbereitung → Durchführung → Nachbereitung. Vorbereitung: Ziel klären, Ort/Zeit wählen (45-60 Min.), Fakten sammeln, MA informieren. Leitfaden: Eröffnung, Austausch, Rückblick, Vereinbarung, Abschluss. WWW-Feedback: Wahrnehmung – Wirkung – Wunsch. Mit Plan ins Gespräch, flexibel bleiben!",
        keyPoints: [
          "Vorbereiten → Führen → Reflektieren",
          "45-60 Minuten einplanen",
          "Leitfaden als Orientierung",
          "WWW: Wahrnehmung-Wirkung-Wunsch"
        ]
      }
    ]
  },
  // MODUL 29: Gesprächsführung – Steuerung & Beziehung
  29: {
    title: "Gesprächsführung – Steuerung & Beziehung",
    icon: "🎯",
    description: "Vier-Phasen-Modell, Dokumentation und Rollensimulation.",
    slides: [
      {
        id: "pdl_vier_phasen_modell",
        title: "Das Vier-Phasen-Modell",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/4e76275e6323763efa6552c44d0488e02ca7577a0d29d5ff38f62dc2256f4a3b.png",
        explanation: "Vorbereitung: Drei Säulen – Ziel definieren, Fakten sammeln, Haltung reflektieren. Phase 1 Einstieg: Vertrauen schaffen, Rahmen klären. Phase 2 Sachklärung: Fakten zusammentragen, aktiv zuhören. Phase 3 Lösungsentwicklung: Gemeinsam Optionen erarbeiten. Phase 4 Vereinbarung: Ziele festhalten, nächste Schritte planen.",
        keyPoints: [
          "3 Säulen: Ziel, Fakten, Haltung",
          "Einstieg: Vertrauen aufbauen",
          "Sachklärung: Aktiv zuhören",
          "Vereinbarung: Verbindlich abschließen"
        ]
      },
      {
        id: "pdl_dokumentation_simulation",
        title: "Dokumentation & Praxisübung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/4e76275e6323763efa6552c44d0488e02ca7577a0d29d5ff38f62dc2256f4a3b.png",
        explanation: "Dokumentation: Was (Datum, Anlass, Ziel, Inhalte, Vereinbarungen), Wie (neutral, kurz, datenschutzkonform), Warum (rechtliche Absicherung, Grundlage für Folgegespräche). Rollensimulation: PDL führt Gespräch, MA spielt Rolle, Beobachter gibt Feedback. Beobachten: Gesprächseinstieg, aktives Zuhören, Phasenstruktur, Vereinbarungsqualität. Gesprächsführung = Muskel, der trainiert werden muss!",
        keyPoints: [
          "Dokumentation: neutral, kurz, zeitnah",
          "Rechtliche Absicherung schaffen",
          "Rollensimulation zum Üben",
          "Training macht den Meister"
        ]
      }
    ]
  },
  // MODUL 30: Leistungsbewertung & Zielvereinbarungen
  30: {
    title: "Leistungsbewertung & Zielvereinbarungen",
    icon: "📊",
    description: "Strukturierte Bewertung und SMART-Ziele.",
    slides: [
      {
        id: "pdl_leistungsbewertung",
        title: "Strukturierte Leistungsbewertung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/d4119a47c88f0496289602378712b2f4a3303742a89df7c566104b8555043791.png",
        explanation: "Subjektive Einflüsse erkennen: Sympathie, selektive Wahrnehmung, Recency-Effekt. Objektivierungsstrategien: Beobachtungsnotizen, mehrere Perspektiven, einheitliche Kriterien. Kriterienbereiche: Fachkompetenz (Qualität, Standards), Sozialkompetenz (Kommunikation, Team), Selbstkompetenz (Zuverlässigkeit, Belastbarkeit). Bewertungsfehler vermeiden: Halo-Effekt, Milde-Fehler, zentrale Tendenz!",
        keyPoints: [
          "Subjektive Einflüsse erkennen",
          "Mehrperspektivische Bewertung",
          "Fach-, Sozial-, Selbstkompetenz",
          "Bewertungsfehler vermeiden"
        ]
      },
      {
        id: "pdl_smart_ziele",
        title: "SMART-Ziele formulieren",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/d4119a47c88f0496289602378712b2f4a3303742a89df7c566104b8555043791.png",
        explanation: "SMART: Spezifisch (konkret), Messbar (überprüfbar), Attraktiv (persönlich bedeutsam), Realistisch (erreichbar), Terminiert (mit Enddatum). Entwicklungsziele: Kompetenzaufbau, Weiterbildung. Leistungsziele: Messbare Ergebnisse, Standards. Tipp: Mindestens ein Entwicklungsziel pro Mitarbeitergespräch! Beteiligung schafft Ownership – gemeinsam erarbeitete Ziele werden häufiger erreicht.",
        keyPoints: [
          "SMART: Spezifisch, Messbar, Attraktiv, Realistisch, Terminiert",
          "Entwicklungs- vs. Leistungsziele",
          "Beteiligung schafft Motivation",
          "Schriftliche Vereinbarung treffen"
        ]
      }
    ]
  },
  // MODUL 31: Konfliktgespräche & Deeskalation
  31: {
    title: "Konfliktgespräche & Deeskalation",
    icon: "⚖️",
    description: "Konfliktdynamiken und Moderation.",
    slides: [
      {
        id: "pdl_konfliktdynamiken",
        title: "Konfliktdynamiken verstehen",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/7aebc0f9eed40a18da652a1b83c77221671969f96c170263b692a691ecda0218.png",
        explanation: "Konfliktursachen: Arbeitsbelastung (Überlastung, Personalmangel), Kommunikation (Missverständnisse, unklare Zuständigkeiten), Werte (verschiedene Pflegeverständnisse, Generationen). Eskalationsstufen nach Glasl: Win-Win (1-3), Win-Lose (4-6), Lose-Lose (7-9). Frühes Eingreifen ist entscheidend! Die PDL als Konfliktmoderator: Allparteilich, neutral, prozessverantwortlich.",
        keyPoints: [
          "Ursachen: Belastung, Kommunikation, Werte",
          "Glasl: 9 Eskalationsstufen",
          "Früh eingreifen = Win-Win möglich",
          "PDL = neutrale Moderatorin"
        ]
      },
      {
        id: "pdl_konfliktgespraech_struktur",
        title: "Vier Phasen des Konfliktgesprächs",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/7aebc0f9eed40a18da652a1b83c77221671969f96c170263b692a691ecda0218.png",
        explanation: "Phase 1 Sachverhalt klären: Fakten neutral, W-Fragen, Paraphrasieren. Phase 2 Emotionen anerkennen: Gefühle validieren, Ich-Botschaften fördern. Phase 3 Lösung erarbeiten: Interessen erfragen, Win-win-Perspektive. Phase 4 Verbindlichkeit herstellen: Schriftlich festhalten, Zuständigkeiten, Follow-up. Praxistipp: Schriftliche Zusammenfassung stärkt Commitment!",
        keyPoints: [
          "1. Sachverhalt neutral klären",
          "2. Emotionen anerkennen",
          "3. Win-win-Lösung erarbeiten",
          "4. Verbindlich abschließen"
        ]
      }
    ]
  },
  // MODUL 32: Kritikgespräche & Fehlverhalten
  32: {
    title: "Kritikgespräche & Fehlverhalten",
    icon: "💬",
    description: "Konstruktive Kritik und Widerstand.",
    slides: [
      {
        id: "pdl_konstruktive_kritik",
        title: "Vier Schritte konstruktiver Kritik",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/0fdf0254d17c46f23f91b2194435ba0dc18380c55e2c7bdd8fdbeb30b0419e67.png",
        explanation: "Schritt 1 Ich-Botschaften: Eigene Perspektive, nicht anklagen. Schritt 2 Konkrete Verhaltensbeschreibung: Nur Beobachtbares, keine Verallgemeinerungen. Schritt 3 Wirkung darstellen: Folgen für Team, Betrieb, Bewohner erklären. Schritt 4 Erwartung formulieren: Klar, realistisch, mit Zeitrahmen. Kritikgespräche sind ein Zeichen echter Führungskompetenz!",
        keyPoints: [
          "1. Ich-Botschaften verwenden",
          "2. Konkret statt pauschal",
          "3. Wirkung erklären",
          "4. Klare Erwartung formulieren"
        ]
      },
      {
        id: "pdl_fehlverhalten_widerstand",
        title: "Fehlverhalten & Widerstand",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/0fdf0254d17c46f23f91b2194435ba0dc18380c55e2c7bdd8fdbeb30b0419e67.png",
        explanation: "Typisches Fehlverhalten: Dokumentationsmängel, Pflichtverletzungen, Unpünktlichkeit, Hygieneverstöße. Rechtlicher Rahmen: Ermahnung (informell) → Abmahnung (formell) → Kündigung (Ultima Ratio). Widerstandsmuster: Rechtfertigung, Angriff, Verharmlosung, Schweigen, Schuldumkehr. Strategie: Anerkennen, aber Fokus halten. Situativ flexibel zwischen Empathie und Klarheit!",
        keyPoints: [
          "Fehlverhalten konkret benennen",
          "Ermahnung → Abmahnung → Kündigung",
          "Widerstand als Information verstehen",
          "Empathie UND Klarheit"
        ]
      }
    ]
  },
  // MODUL 33: Belastungsgespräche & Angehörige
  33: {
    title: "Belastungsgespräche & Angehörige",
    icon: "❤️",
    description: "Emotionale Belastung und Beschwerdemanagement.",
    slides: [
      {
        id: "pdl_belastungsgespraeche",
        title: "Gespräche bei emotionaler Belastung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/fe421295dd2ee02dfbcd10ffc4b98929424ca8933d132d0f4299cecb8739374f.png",
        explanation: "Belastungssignale erkennen: Verhaltensänderungen, Rückzug, gereizte Reaktionen. Überlastungsgespräch: Wertneutral ansprechen, zuhören, Lösungen erarbeiten. Rückkehrgespräch: Willkommen, Information, Belastungscheck. Psychische Belastung: Sensibel ansprechen, keine Diagnose, Unterstützung vermitteln (EAP, Betriebsarzt, Supervision).",
        keyPoints: [
          "Belastungssignale früh erkennen",
          "Wertneutral ansprechen",
          "Rückkehr: Willkommen + Check",
          "Professionelle Grenzen wahren"
        ]
      },
      {
        id: "pdl_angehoerige_beschwerde",
        title: "Angehörigenkommunikation & Beschwerden",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/fe421295dd2ee02dfbcd10ffc4b98929424ca8933d132d0f4299cecb8739374f.png",
        explanation: "Beschwerden als Chance: Unerfüllte Bedürfnisse erkennen, Vertrauen vertiefen. 5 Schritte: 1. Aktiv zuhören, 2. Verständnis zeigen, 3. Sachverhalt klären, 4. Transparent rückmelden, 5. Konsequenzen ziehen. Deeskalation: Ruhe, Tempo reduzieren, Pausen, gemeinsame Basis finden. Nie defensiv reagieren!",
        keyPoints: [
          "Beschwerden = Chance",
          "5 Schritte professionell",
          "Deeskalation: Ruhe übertragen",
          "Nie defensiv reagieren"
        ]
      }
    ]
  },
  // MODUL 34: Professionalisierung & Integration
  34: {
    title: "Professionalisierung & Integration",
    icon: "🎓",
    description: "Hochrisiko-Gespräche und Selbstfürsorge.",
    slides: [
      {
        id: "pdl_hochrisiko_gespraeche",
        title: "Hochrisiko-Gespräche meistern",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/ddd8cad8549af7c26263b80c8f01c0bdd47bc78a7e9d8a77e2945db6b6c83e4b.png",
        explanation: "Typische Hochrisiko-Gespräche: BEM-Vorgespräch (ab 42 Tage AU), nach Pflegefehler (Haftung!), Konflikt im Team, Angehörigenbeschwerde, Kündigungsabsicht, Überlastungsanzeige. BEM: Gesetzliche Pflicht, freiwillig für MA. Nach Pflegefehler: Fakten, Verantwortung, Lernen, Dokumentation. Widerstand als Information verstehen und produktiv wenden!",
        keyPoints: [
          "BEM ab 42 Tage AU Pflicht",
          "Pflegefehler: Fakten + Lernen",
          "Widerstand = Information",
          "4-Phasen-Modell anwenden"
        ]
      },
      {
        id: "pdl_integration_selbstfuersorge",
        title: "Integration & Selbstfürsorge",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/ddd8cad8549af7c26263b80c8f01c0bdd47bc78a7e9d8a77e2945db6b6c83e4b.png",
        explanation: "Gesprächsführung unter Zeitdruck: Vorbereitung in 5 Min (Ziel, Fakten, Haltung), klare Eröffnung, Verbindlichkeit auch im kurzen Gespräch. Selbstfürsorge: Mentale Regeneration, kollegiale Beratung, Grenzen setzen, Team als Ressource. Die 4 Kernprinzipien: 1. Struktur gibt Sicherheit, 2. Widerstand ist Information, 3. Sach- und Beziehungsebene, 4. Verbindlichkeit als Qualität.",
        keyPoints: [
          "Zeitdruck: Vorbereitung in 5 Min",
          "Selbstfürsorge nicht vergessen",
          "Kollegiale Beratung nutzen",
          "Professionalisierung = lebenslang"
        ]
      }
    ]
  },
  // MODUL 35: Rechtliche Grundlagen & Dokumentation
  35: {
    title: "Rechtliche Grundlagen & Dokumentation",
    icon: "⚖️",
    description: "Arbeitsrecht, Personalakte und Gesprächsprotokolle.",
    slides: [
      {
        id: "pdl_rechtliche_grundlagen",
        title: "Arbeitsrechtliche Grundlagen",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/a73e0cf5013a73386a3241d85ba459b14e0982bcb6d168cb00278a1e9f3355b2.png",
        explanation: "Dokumentationspflicht: Gespräche zu Fehlverhalten, Zielvereinbarungen, Kritik müssen dokumentiert werden. Personalakte: MA haben jederzeit Einsichtsrecht (§83 BetrVG). Abmahnung vs. Ermahnung: Ermahnung ist informell ohne rechtliche Wirkung. Abmahnung ist formell mit drei Funktionen: Rüge, Warnung, Dokumentation. MAV/Betriebsrat bei Maßnahmen mit personeller Konsequenz einbeziehen!",
        keyPoints: [
          "Dokumentationspflicht beachten",
          "MA haben Einsichtsrecht",
          "Abmahnung = formal + rechtlich",
          "MAV/BR frühzeitig einbeziehen"
        ]
      },
      {
        id: "pdl_gespraechsdokumentation",
        title: "Professionelle Gesprächsdokumentation",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/a73e0cf5013a73386a3241d85ba459b14e0982bcb6d168cb00278a1e9f3355b2.png",
        explanation: "Die 6 Pflichtbestandteile: 1. Gesprächsanlass (Warum?), 2. Sachverhalt (wertungsfrei), 3. Gesprächsziel (Was soll erreicht werden?), 4. Vereinbarungen (konkrete Maßnahmen), 5. Fristen (Umsetzungszeitraum), 6. Unterschriften (Verweigerung vermerken!). Die 5 häufigsten Fehler: Zu emotional, zu unklar, keine Vereinbarung, fehlende Nachverfolgung, Harmonieorientierung.",
        keyPoints: [
          "6 Pflichtbestandteile beachten",
          "Neutral und zeitnah dokumentieren",
          "Verweigerung vermerken",
          "Häufige Fehler vermeiden"
        ]
      }
    ]
  },
  // MODUL 36: Beschwerdemanagement & Angehörige
  36: {
    title: "Beschwerdemanagement & Angehörige",
    icon: "📋",
    description: "6-Stufen-Struktur und Deeskalation.",
    slides: [
      {
        id: "pdl_beschwerdemanagement",
        title: "Professionelles Beschwerdemanagement",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/924bf27b212e223d5906af2859005316f64ac11bec50c524e21315bb62fd9a5d.png",
        explanation: "Beschwerden sind ein Seismograph für Qualität! Sie verraten: Prozesslücken, Kommunikationsprobleme, unerfüllte Erwartungen. Risiken bei schlechtem Management: Imageschaden (Bewertungen!), Teambelastung, Haftung, Prüfungsrelevanz. Prüfer (MD/Heimaufsicht) erwarten: Schriftliches System, Rückmeldung an Beschwerdeführer, Integration ins QM.",
        keyPoints: [
          "Beschwerden = Qualitätssignal",
          "Risiken: Image, Haftung, Prüfung",
          "Dokumentiertes System Pflicht",
          "Rückmeldung an Beschwerdeführer"
        ]
      },
      {
        id: "pdl_6_stufen_deeskalation",
        title: "6-Stufen-Struktur & Deeskalation",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/924bf27b212e223d5906af2859005316f64ac11bec50c524e21315bb62fd9a5d.png",
        explanation: "Die 6 Stufen: 1. Zuhören ohne Verteidigung, 2. Emotion anerkennen, 3. Sachverhalt klären, 4. Transparenz herstellen, 5. Lösung anbieten, 6. Dokumentieren. Deeskalationstechniken: Ruhige Stimme, keine Rechtfertigung, Pausen zulassen (3-5 Sek.), klare Sprache, Gespräch strukturieren. Nie defensiv reagieren!",
        keyPoints: [
          "6 Stufen systematisch anwenden",
          "Zuerst Emotion anerkennen",
          "Pausen = Professionalität",
          "Ruhe überträgt sich"
        ]
      }
    ]
  },
  // MODUL 37: Selbstführung & innere Haltung
  37: {
    title: "Selbstführung & innere Haltung",
    icon: "🧘",
    description: "Stressregulation und emotionale Selbststeuerung.",
    slides: [
      {
        id: "pdl_haltung_fuehrung",
        title: "Haltung als Führungsinstrument",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/761cc49974a7f67e34b41583c98b14396975878e1b490f22a33e4d902bb66b81.png",
        explanation: "Autorität (Position/Macht) vs. Klarheit (transparente Kommunikation): Klarheit schafft Sicherheit, Autorität riskiert Angstklima. Nähe vs. Distanz: Zu viel Nähe gefährdet Entscheidungsfähigkeit, zu viel Distanz verliert Vertrauen. Balance: Empathie bewahren, Rolle beibehalten. Emotionale Selbststeuerung: Wahrnehmen → Benennen → Pause → Entscheiden → Vorbild sein.",
        keyPoints: [
          "Klarheit vor Autorität",
          "Balance zwischen Nähe und Distanz",
          "Emotionen: Pause vor Reaktion",
          "Ruhe als Ressource fürs Team"
        ]
      },
      {
        id: "pdl_stress_selbstregulation",
        title: "Stress erkennen & regulieren",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/761cc49974a7f67e34b41583c98b14396975878e1b490f22a33e4d902bb66b81.png",
        explanation: "Neurobiologie: Unter Stress wird Amygdala aktiviert (Kampf/Flucht), präfrontaler Kortex gehemmt (rationales Denken). Folge: Gespräche werden oberflächlicher, reaktiver. Trigger erkennen: Widersetzlichkeit, Unzuverlässigkeit, Kritik. Techniken: 4-7-8-Atmung, körperliche Erdung, mentale Distanzierung ('Was würde ich einer Kollegin raten?'), bewusste Gesprächspause.",
        keyPoints: [
          "Stress = biologische Reaktion",
          "Amygdala vs. Präfrontaler Kortex",
          "Trigger und Körpersignale erkennen",
          "4-7-8-Atmung zur Regulation"
        ]
      }
    ]
  },
  // MODUL 38: Praxisprüfung & Integration
  38: {
    title: "Praxisprüfung & Integration",
    icon: "🎯",
    description: "Simulation und Bewertungskriterien.",
    slides: [
      {
        id: "pdl_gespraechsarten_uebersicht",
        title: "Alle Gesprächsarten integriert",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/8765b8c4f025b888c0ce61ed1b3baeaad7f3cb48a29e49b234c4488d0bdc60ba.png",
        explanation: "Die 6 Gesprächsarten: Mitarbeitergespräch (Entwicklung, Bindung), Konfliktgespräch (Deeskalation, Lösung), Kritikgespräch (Verhalten ändern), Angehörigengespräch (Vertrauen, Kooperation), Überlastungsgespräch (Entlastung), Teambesprechung (Koordination). Gemeinsame Grundlagen: Klare Absicht, Steuerung statt Drift, Verbindlichkeit, Dokumentation.",
        keyPoints: [
          "6 Gesprächsarten beherrschen",
          "Klare Absicht vor jedem Gespräch",
          "Steuerung übernehmen",
          "Verbindlich abschließen"
        ]
      },
      {
        id: "pdl_bewertungskriterien",
        title: "Bewertungskriterien & Reflexion",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/8765b8c4f025b888c0ce61ed1b3baeaad7f3cb48a29e49b234c4488d0bdc60ba.png",
        explanation: "6 Bewertungskriterien: 1. Gesprächsstruktur (erkennbarer Aufbau), 2. Zieldefinition (realistisch, verständlich), 3. Emotionssteuerung (ruhig bleiben), 4. Verbindlichkeit (konkrete Vereinbarungen), 5. Dokumentationsfähigkeit, 6. Führungswirkung (souverän, präsent). Simulation: 20 Min., komplexes Szenario. Ziel: Vertrauen schaffen, nächste Schritte vereinbaren – nicht alle Probleme lösen!",
        keyPoints: [
          "6 Kriterien zur Selbstreflexion",
          "Struktur gibt Sicherheit",
          "Ziel: Vertrauen + nächste Schritte",
          "Übung macht den Meister"
        ]
      }
    ]
  },
  // MODUL 39: Gesunde Führung – Grundlagen
  39: {
    title: "Gesunde Führung – Grundlagen",
    icon: "💚",
    description: "Belastungsfaktoren und Salutogenese.",
    slides: [
      {
        id: "pdl_gesunde_fuehrung_bedeutung",
        title: "Bedeutung gesunder Führung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/156c224f50e4e22fbd6539ca62cd0339bc482293a993cde231681b27d4d60b1e.png",
        explanation: "Führung beeinflusst direkt: Krankenstand, Mitarbeiterzufriedenheit, Pflegequalität, Fluktuation. Strukturelle Ursachen für Krankenstand: Unterbesetzung, Schichtarbeit, mangelnde Erholung, emotionale Dauerbelastung. Teufelskreis Fachkräftemangel: Unterbesetzung → Überlastung → Krankenstand → mehr Unterbesetzung. Menschen verlassen nicht Unternehmen – sie verlassen Führungskräfte!",
        keyPoints: [
          "Führung = Hebel für Gesundheit",
          "Strukturelle Ursachen erkennen",
          "Teufelskreis durchbrechen",
          "Bindung durch Wertschätzung"
        ]
      },
      {
        id: "pdl_salutogenese_modelle",
        title: "Modelle gesunder Führung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/156c224f50e4e22fbd6539ca62cd0339bc482293a993cde231681b27d4d60b1e.png",
        explanation: "3 Modelle: 1. Ressourcenorientierung: Stärken stärken statt Defizite verwalten. 2. Salutogenese (Antonovsky): Kohärenzgefühl aus Verstehbarkeit (Welt erklärbar), Handhabbarkeit (Ressourcen vorhanden), Sinnhaftigkeit (Arbeit bedeutsam). 3. Wertschätzung: Aktiviert Dopamin/Oxytocin, senkt Cortisol, schützt vor Burnout. Konkret wertschätzen: Leistung benennen, zeitnah, authentisch!",
        keyPoints: [
          "Stärken stärken",
          "Kohärenzgefühl fördern",
          "Wertschätzung = Schutzfaktor",
          "Kleine Schritte, große Wirkung"
        ]
      }
    ]
  },
  // MODUL 40: Verantwortung & Gefährdungsbeurteilung
  40: {
    title: "Verantwortung & Gefährdungsbeurteilung",
    icon: "⚠️",
    description: "Vier Säulen der Verantwortung und Frühwarnsignale.",
    slides: [
      {
        id: "pdl_vier_saeulen",
        title: "Die vier Säulen der Leitungsverantwortung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/e9d19db36470fabe0e67942934c7367457673a35630ee4c284ad67858a27aee8.png",
        explanation: "1. Fürsorgepflicht: Schutz der physischen und psychischen Unversehrtheit. 2. Präventionsauftrag: Handeln vor Eintreten von Schäden. 3. Gefährdungsbeurteilung: Gesetzlich vorgeschrieben (ArbSchG §5), regelmäßig durchführen und dokumentieren. 4. Überlastungsanzeigen: Verpflichtung zur Reaktion und Dokumentation. Die PDL ist Gestalterin von Arbeitsbedingungen, NICHT Therapeutin!",
        keyPoints: [
          "4 Säulen der Verantwortung",
          "ArbSchG §5: Gefährdungsbeurteilung Pflicht",
          "PDL = Gestalterin, nicht Therapeutin",
          "Dokumentation schützt"
        ]
      },
      {
        id: "pdl_fruehwarnsignale",
        title: "Frühwarnsignale erkennen",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/e9d19db36470fabe0e67942934c7367457673a35630ee4c284ad67858a27aee8.png",
        explanation: "Bei Mitarbeitenden: Häufige Kurzzeiterkrankungen, Reizbarkeit, Leistungsabfall, Rückzug, Zynismus. Bei sich selbst: Dauerstress, Entscheidungsmüdigkeit, emotionale Abstumpfung. Typische Belastungsindikatoren: Arbeitsverdichtung (zu wenig Zeit, keine Pausen), emotionale Belastung (Kontakt mit Leidenden), ungünstige Arbeitszeiten, soziale Spannungen. Überlastung kündigt sich an!",
        keyPoints: [
          "MA: Krankmeldungen, Reizbarkeit, Rückzug",
          "Selbst: Erschöpfung, Entscheidungsmüde",
          "Belastung: Zeit, Emotion, Schichten",
          "Frühwarnsignale wahrnehmen!"
        ]
      }
    ]
  },
  // MODUL 41: Resilienz & Selbstführung
  41: {
    title: "Resilienz & Selbstführung",
    icon: "🧘",
    description: "Fünf Schlüsselfaktoren und gesunde Grenzen.",
    slides: [
      {
        id: "pdl_resilienz_faktoren",
        title: "Die fünf Schlüsselfaktoren der Resilienz",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/d3cdae4b944cae2918678627b0487dbd0bdbd4781f3095f441a0064b1c7cdc76.png",
        explanation: "Resilienz ist dynamisch und erlernbar! Die 5 Faktoren: 1. Optimismus (zuversichtliche Grundhaltung), 2. Lösungsorientierung (Fokus auf Möglichkeiten), 3. Selbstwirksamkeit (Überzeugung, Einfluss zu haben), 4. Netzwerkorientierung (soziale Unterstützung), 5. Zukunftsplanung (aktive Gestaltung). Resiliente Führung wirkt stabilisierend: Ruhe, Handlungsfähigkeit, Vorbildfunktion.",
        keyPoints: [
          "Resilienz = erlernbar",
          "5 Faktoren: Optimismus bis Planung",
          "Ruhe ausstrahlen = Team stabilisieren",
          "Vorbild im Umgang mit Fehlern"
        ]
      },
      {
        id: "pdl_grenzen_setzen",
        title: "Grenzen erkennen und setzen",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/d3cdae4b944cae2918678627b0487dbd0bdbd4781f3095f441a0064b1c7cdc76.png",
        explanation: "Warum Grenzen schwerfallen: Haltung des Dienens, Angst vor Konflikten, Perfektionismus. Was fehlende Grenzen kosten: Erschöpfung, Burnout, verminderte Entscheidungsqualität. Das wertschätzende Nein: Anerkennung + Grenze + Alternative. Beispiel: 'Ich verstehe, dass das wichtig ist. Im Moment kann ich das nicht übernehmen. Können wir Dienstag einen Termin machen?' Grenzen setzen = Führungsaufgabe!",
        keyPoints: [
          "Helfer-Syndrom erkennen",
          "Fehlende Grenzen = Burnout-Risiko",
          "Wertschätzendes Nein üben",
          "Grenzen = professionell, nicht schwach"
        ]
      }
    ]
  },
  // MODUL 42: Teamgesundheit fördern
  42: {
    title: "Teamgesundheit fördern",
    icon: "👥",
    description: "Teamklima, Instrumente und Überlastung.",
    slides: [
      {
        id: "pdl_teamklima_faktoren",
        title: "Teamklima als Gesundheitsfaktor",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/39b1e605c6d8af4c357810189fef9da4a0ceec4a1c64921a6f05f5e9f9983716.png",
        explanation: "Gutes Teamklima → geringerer Krankenstand → bessere Pflegequalität. Die 5 Schlüsselfaktoren: 1. Kommunikation (offen, wertschätzend), 2. Transparenz (Entscheidungen erklären), 3. Fairness (gleiche Maßstäbe), 4. Beteiligung (in Entscheidungen einbeziehen), 5. Konfliktkultur (konstruktiv lösen). 6 bewährte Instrumente: Realistische Dienstplanung, transparente Information, Fallbesprechungen, Supervision, Anerkennungskultur, SBAR-Übergaben.",
        keyPoints: [
          "5 Faktoren: Kommunikation bis Konflikt",
          "6 Instrumente für Gesundheit",
          "SBAR: Situation-Background-Assessment-Recommendation",
          "Führung gestaltet Teamklima"
        ]
      },
      {
        id: "pdl_ueberlastung_umgang",
        title: "Umgang mit Überlastung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/39b1e605c6d8af4c357810189fef9da4a0ceec4a1c64921a6f05f5e9f9983716.png",
        explanation: "Überlastungsanzeige: Formales Instrument, schützt MA haftungsrechtlich, verpflichtet zur Handlung. Vorgehensweise: 1. Ruhig annehmen, 2. Analysieren und dokumentieren, 3. Sofortmaßnahmen, 4. Träger informieren. Prioritätenhierarchie bei Personalmangel: 1. Sicherheit (lebenserhaltend, nicht verhandelbar), 2. Grundpflege, 3. Dokumentation, 4. Zusatzleistungen (verschiebbar). Führung zeigt sich, wenn es schwierig wird!",
        keyPoints: [
          "Überlastungsanzeige = Schutz + Pflicht",
          "4 Schritte der Bearbeitung",
          "Prioritäten: Sicherheit zuerst",
          "Führung in der Krise zeigen"
        ]
      }
    ]
  },
  // MODUL 43: Burnout-Prävention
  43: {
    title: "Burnout-Prävention & Führungskultur",
    icon: "🔥",
    description: "Maslach-Modell, drei Ebenen und Führungsleitbild.",
    slides: [
      {
        id: "pdl_burnout_verstehen",
        title: "Burnout verstehen",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/a36f777f098de288409d14e9a9b689ce216ad8a82568da609c85632623e48614.png",
        explanation: "Burnout ≠ normale Erschöpfung! Bei Burnout bringt Erholung keine Verbesserung. Das Maslach-Modell: 1. Emotionale Erschöpfung (ausgebrannt), 2. Depersonalisation (Zynismus, Distanz), 3. Reduzierte Leistungsfähigkeit (Ineffektivität). Warum Führungskräfte betroffen: Sandwich-Position, Helfer-Persönlichkeit, mangelnde Unterstützung, ständige Verfügbarkeit. Burnout ist ein systemisches Signal, kein persönliches Versagen!",
        keyPoints: [
          "Burnout ≠ normale Erschöpfung",
          "Maslach: 3 Dimensionen",
          "Führungskräfte besonders gefährdet",
          "Systemisches Signal, nicht Versagen"
        ]
      },
      {
        id: "pdl_praevention_ebenen",
        title: "Drei Ebenen der Prävention",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/a36f777f098de288409d14e9a9b689ce216ad8a82568da609c85632623e48614.png",
        explanation: "1. Individuelle Ebene: Selbstreflexion, Zeitmanagement (Eisenhower), Regeneration (Schlaf, Bewegung, Kontakte). 2. Teambezogene Ebene: Klare Zuständigkeiten, Beteiligung, transparente Kommunikation. 3. Organisationale Ebene: Strukturqualität, Führungsleitlinien, BGM. Die 4 Dimensionen gesunder Führung: Schutzfaktor, Qualitätsfaktor, Wirtschaftsfaktor, Kulturfaktor. Prävention beginnt bei Ihnen!",
        keyPoints: [
          "3 Ebenen: Ich, Team, Organisation",
          "4 Dimensionen gesunder Führung",
          "Prävention beginnt bei mir",
          "Jeden Tag neu entscheiden"
        ]
      }
    ]
  },
  // MODUL 44: Von der Maßnahme zur Strategie
  44: {
    title: "Von der Maßnahme zur Strategie",
    icon: "📈",
    description: "5-Punkte-Konzept und nachhaltige Verankerung.",
    slides: [
      {
        id: "pdl_5_punkte_konzept",
        title: "Das 5-Punkte-Konzept",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/9cbe0252c671c4d685303362bf5c9d0eeed850b6ed9523e64ae8610c208dbb00.png",
        explanation: "Einzelmaßnahmen verpuffen – systemische Verankerung wirkt! Das 5-Punkte-Konzept: 1. Analyse (Bestandsaufnahme mit Daten), 2. Zieldefinition (SMART-Ziele), 3. Maßnahmenplanung (Quick Wins + Strukturveränderungen), 4. Verantwortlichkeiten (Wer? Mit welchen Ressourcen?), 5. Evaluation (Messzeitpunkte, Kennzahlen). Quick Wins schaffen Glaubwürdigkeit: Dienstplan-Check, Teambesprechung, Präsenz zeigen.",
        keyPoints: [
          "Einzelmaßnahmen verpuffen",
          "5 Schritte: Analyse bis Evaluation",
          "Quick Wins = Glaubwürdigkeit",
          "SMART-Ziele formulieren"
        ]
      },
      {
        id: "pdl_nachhaltige_verankerung",
        title: "Nachhaltige Verankerung",
        image: "https://static.prod-images.emergentagent.com/jobs/cf2e2187-eb0b-43f4-8cd2-ecb1968211d9/images/9cbe0252c671c4d685303362bf5c9d0eeed850b6ed9523e64ae8610c208dbb00.png",
        explanation: "Methoden zur Verankerung: 1. Routinen etablieren (regelmäßige Ankerpunkte), 2. Führungskräfte qualifizieren (Fortbildung, Supervision), 3. Kennzahlen nutzen (lebendige Steuerungsgrößen). Verbindung zum Leitbild: Gesunde Führung als Teil des normativen Rahmens, Signal der Ernsthaftigkeit, Verbindung zu QM. Strukturen analysieren, nicht Menschen bewerten! Nachhaltigkeit entsteht durch Verankerung in Kultur.",
        keyPoints: [
          "Routinen etablieren",
          "Führungskräfte qualifizieren",
          "Im Leitbild verankern",
          "Strukturen, nicht Menschen bewerten"
        ]
      }
    ]
  },
  // MODUL 45: Dienstplanung und Arbeitsorganisation
  45: {
    title: "Dienstplanung und Arbeitsorganisation",
    icon: "📅",
    description: "Belastungssteuerung, Arbeitsverdichtung und Delegation.",
    slides: [
      {
        id: "pdl_dienstplanung",
        title: "Gesundheitsfördernde Dienstplanung",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/4080e4c915dd11158aa8bd972523c0ee4f77fbdcd326e113a1ae463a1c215c51.png",
        explanation: "Die Wahl der Schichtmodelle wirkt sich direkt auf die Regeneration aus! Chronobiologie zeigt: Nach Nachtschicht mindestens 11 Stunden Ruhe nötig. Empfehlungen: Vorwärtsrotierende Schichten (Tag→Abend→Nacht), maximal 2-3 Nachtschichten hintereinander, regelmäßige freie Wochenenden. Teams mit gesundheitsfördernden Schichtmodellen haben bis zu 30% weniger Fehlzeiten!",
        keyPoints: [
          "11 Stunden Ruhe nach Nachtschicht",
          "Vorwärtsrotierende Schichten",
          "Max. 2-3 Nächte hintereinander",
          "30% weniger Fehlzeiten möglich"
        ]
      },
      {
        id: "pdl_workflow_optimierung",
        title: "Arbeitsverdichtung reduzieren",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/9ceaf590f4ee9d8dc380fc6041c7e692d9ab32e4c74075d36dbc3f1abcafbf43.png",
        explanation: "Pflegekräfte verbringen 30-40% ihrer Zeit mit Dokumentation! Typische Probleme: Doppelstrukturen (gleiche Info mehrfach erfasst), unnötige Wege, Kommunikationsbrüche, Schnittstellenprobleme. Maßnahmen: Überflüssige Dokumentationsfelder eliminieren, strukturierte Vorlagen nutzen, Dokumentation am Ort der Pflege. Das Eisenhower-Prinzip hilft: Dringend+Wichtig sofort, Wichtig terminieren, Dringend delegieren, Unwichtiges eliminieren.",
        keyPoints: [
          "30-40% Zeit für Dokumentation",
          "Doppelstrukturen vermeiden",
          "Eisenhower-Prinzip anwenden",
          "Kompetenzorientiert delegieren"
        ]
      }
    ]
  },
  // MODUL 46: Kommunikation als Gesundheitsinstrument
  46: {
    title: "Kommunikation als Gesundheitsinstrument",
    icon: "💬",
    description: "Wertschätzung, Konfliktprävention und Teambesprechungen.",
    slides: [
      {
        id: "pdl_wertschaetzung",
        title: "Wertschätzung als Stresspuffer",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/a4760c6641ed8395e77f5082f6673edbf5639dd8ce0eebcff3f816eda366b392.png",
        explanation: "Wertschätzung ist ein nachgewiesener Schutzfaktor gegen Burnout! Auf physiologischer Ebene: Aktiviert Belohnungssystem (Dopamin, Oxytocin), dämpft Stressachsen, weniger körperliche Beschwerden. Glaubwürdig ist: Konkrete Anerkennung (spezifische Handlung loben), konstruktives Feedback, Transparenz bei Entscheidungen, echte Partizipation. Pauschales Lob wie 'Ihr macht das toll' wirkt nicht!",
        keyPoints: [
          "Aktiviert Belohnungssystem",
          "Dämpft Stressachsen",
          "Konkret und spezifisch loben",
          "Transparenz schafft Vertrauen"
        ]
      },
      {
        id: "pdl_konfliktpraevention",
        title: "Konfliktprävention und Teambesprechungen",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/b7f55a9f4874941288ac4ca590396334dac8a17479c54b87a483b3a7777ef756.png",
        explanation: "Frühwarnsignale erkennen: Cliquenbildung, häufige Kurzerkrankungen, Schweigen/Sarkasmus, Beschwerden über Kollegen, Spannung in Übergaben. Die 4 Säulen der Konfliktprävention: 1. Frühzeitige Klärung, 2. Transparente Kommunikation, 3. Klare Verantwortlichkeiten, 4. Regelmäßige Teambesprechungen. Struktur: Einstieg (5 Min), Information (10 Min), Thema (20 Min), Partizipation (10 Min), Abschluss (5 Min).",
        keyPoints: [
          "Frühwarnsignale beachten",
          "4 Säulen der Prävention",
          "Strukturierte Teambesprechungen",
          "Alle 4-6 Wochen"
        ]
      }
    ]
  },
  // MODUL 47: Personalmangel und Krisenkommunikation
  47: {
    title: "Personalmangel und Krisenkommunikation",
    icon: "🆘",
    description: "Professionell führen trotz Mangel und in Krisen.",
    slides: [
      {
        id: "pdl_krisenkommunikation",
        title: "Krisenkommunikation",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/c0e3569bdde5174c125af5acd7c77b71d46f911793b434675f5acb7aa4b29d6b.png",
        explanation: "Die 4 Säulen der Krisenkommunikation: 1. Klare Ansagen (konkrete Handlungsanweisungen), 2. Transparenz (ehrlich über Situation informieren), 3. Keine Schuldzuweisungen (lösungsorientiert), 4. Orientierung geben (nächste Schritte aufzeigen). Prioritäten bei Personalmangel: Stufe 1 Sicherheit (nicht verhandelbar), Stufe 2 Grundpflege, Stufe 3 Dokumentation, Stufe 4 Zusatzleistungen (verschiebbar).",
        keyPoints: [
          "4 Säulen der Krisenkommunikation",
          "Sicherheit = Stufe 1",
          "Keine Schuldzuweisungen",
          "Orientierung geben"
        ]
      },
      {
        id: "pdl_selbstschutz",
        title: "Eigene Belastungsgrenze erkennen",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/9d53b950b6c843ce8f9cc5931c0725defb9e3671919580c3a2fee4d821abf72e.png",
        explanation: "Warnsignale erkennen: Kognitiv (Konzentrationsprobleme, Vergesslichkeit), Emotional (Gereiztheit, Erschöpfung, Zynismus), Körperlich (Schlafstörungen, Kopfschmerzen), Verhalten (Rückzug, Aufschieben). PDL-Rollenkonflikte: Teamvertreter vs. Träger, Fachkraft vs. Managerin, Ansprechpartnerin vs. Teamschützerin. Strategien: Rollenabgrenzung, Supervision nutzen, Austausch mit anderen PDLs.",
        keyPoints: [
          "Warnsignale früh erkennen",
          "Rollenkonflikte sind normal",
          "Supervision nutzen",
          "Grenzen kommunizieren"
        ]
      }
    ]
  },
  // MODUL 48: Nachhaltige Implementierung
  48: {
    title: "Nachhaltige Implementierung",
    icon: "🎯",
    description: "Gesundheitskonzept finalisieren und präsentieren.",
    slides: [
      {
        id: "pdl_konzept_praesentation",
        title: "Das Gesundheitskonzept präsentieren",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/899cf9bd2d24f905d306c67895a7119b92b3bb434718717d76034c7a93e63edc.png",
        explanation: "Die 5 Säulen eines Gesundheitskonzepts: 1. Strukturiertes Konzept (SMART-Ziele), 2. Konkrete Maßnahmen (Wer? Was? Bis wann?), 3. Zeitplan (Meilensteine, Puffer), 4. Evaluationskriterien (quantitativ + qualitativ), 5. Kommunikationsstrategie (vor, während, nach). Wirksamkeitsmatrix nutzen: Quick Wins (hohe Wirkung, geringer Aufwand) zuerst umsetzen!",
        keyPoints: [
          "5 Säulen des Konzepts",
          "SMART-Ziele formulieren",
          "Quick Wins zuerst",
          "Kommunikation planen"
        ]
      },
      {
        id: "pdl_evaluation",
        title: "Transfer und Evaluation",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/9844772df6525a6ace983ba7782d5e1a8b6c7de5bfb4a40721bfec44a2471673.png",
        explanation: "4 Fragen für den Transfer: 1. Welche Maßnahme setze ich um? 2. Welche Ressourcen brauche ich? 3. Welche Widerstände erwarte ich? 4. Wie messe ich Erfolg? Die 4 Phasen des Widerstands: Ablehnung → Widerstand → Exploration → Integration. Erfolg messbar machen: Baseline etablieren, Zeitpunkte festlegen (3/6/12 Monate), Ergebnisse transparent kommunizieren.",
        keyPoints: [
          "4 Transfer-Fragen",
          "4 Phasen des Widerstands",
          "Baseline etablieren",
          "Transparent kommunizieren"
        ]
      }
    ]
  },
  // MODUL 49: Sicherheitskultur
  49: {
    title: "Sicherheitskultur",
    icon: "🛡️",
    description: "Qualität, MD-Prüfungen und CIRS.",
    slides: [
      {
        id: "pdl_sicherheitskultur",
        title: "Die 4 Elemente der Sicherheitskultur",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/6d365a61f469573b9a8407dc30f2757dc682a69563002998f81b04164597c802.png",
        explanation: "Sicherheitskultur = systematische Risikoprävention, nicht nur Fehlervermeidung! Die 4 Elemente: 1. Offene Kommunikation (ohne Angst vor Sanktionen), 2. Transparente Fehlerbearbeitung (CIRS, Fallbesprechungen), 3. Klare Verantwortlichkeiten (dokumentierte Zuständigkeiten), 4. Strukturierte Prozesse (standardisierte Abläufe). Der MD prüft heute Systemlogik, nicht nur Einzelfälle!",
        keyPoints: [
          "4 tragende Elemente",
          "Systematische Prävention",
          "CIRS nutzen",
          "MD prüft Systemlogik"
        ]
      },
      {
        id: "pdl_qualitaetsmanagement",
        title: "MD-Prüfungen und Qualitätsindikatoren",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/b343b35d3635fd2f11d0ec9aa01d4f2ad4913b07a8de1d7fbacba60a810c49a6.png",
        explanation: "Die 5 MD-Prüfdimensionen: 1. Strukturqualität (Personal, Ausstattung), 2. Prozessqualität (Standards, Schnittstellen), 3. Ergebnisqualität (Qualitätsindikatoren), 4. Dokumentationsqualität (Vollständigkeit, Plausibilität), 5. Risikomanagement (systematische Bewertung). Qualitätsindikatoren: Dekubitus, Gewichtsverlust, Stürze, FEM, Bewohnerzufriedenheit, Krankenhauseinweisungen. CIRS-Prozess: Melden → Analysieren → Maßnahmen → Rückmelden.",
        keyPoints: [
          "5 MD-Prüfdimensionen",
          "6 Qualitätsindikatoren",
          "CIRS = Verantwortung",
          "PDL als Kulturgestalterin"
        ]
      }
    ]
  },
  // MODUL 50: Risikomanagement als Führungsinstrument
  50: {
    title: "Risikomanagement als Führungsinstrument",
    icon: "⚠️",
    description: "Der Risikomanagement-Zyklus und pflegesensitive Risiken.",
    slides: [
      {
        id: "pdl_risikomanagement_zyklus",
        title: "Der Risikomanagement-Zyklus",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/fe065ce8f6050cc6de32a92cbd3bf23e07becee201903ead34ba05053bb3e62d.png",
        explanation: "Risikomanagement ist ein systematischer Prozess mit 6 Phasen: 1. Identifikation (Risiken systematisch erkennen), 2. Bewertung (nach Schwere und Eintrittswahrscheinlichkeit), 3. Planung (individuelle Maßnahmen entwickeln), 4. Umsetzung (Team informieren, konsequent durchführen), 5. Evaluation (Wirksamkeit prüfen), 6. Anpassung (bei Unwirksamkeit neu planen). Der Zyklus ist kontinuierlich und lebendig!",
        keyPoints: [
          "6 Phasen des Zyklus",
          "Systematische Erfassung",
          "Kontinuierlicher Prozess",
          "MD-relevante Dokumentation"
        ]
      },
      {
        id: "pdl_pflegesensitive_risiken",
        title: "Pflegesensitive Risiken",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/fe065ce8f6050cc6de32a92cbd3bf23e07becee201903ead34ba05053bb3e62d.png",
        explanation: "Pflegesensitive Risiken sind Gefährdungen, die aus dem Pflegeprozess entstehen oder durch Pflege beeinflusst werden. Die wichtigsten: Dekubitus (Braden-Skala), Sturz (Morse-Skala), Mangelernährung (MNA), Dehydration (Trinkprotokoll), Kontrakturen (Mobilitätsassessment). Der MD prüft: Systematische Erfassung, validierte Instrumente, angemessene Maßnahmen, korrekte Anwendung, nachvollziehbare Dokumentation.",
        keyPoints: [
          "5 Hauptrisikobereiche",
          "Validierte Instrumente nutzen",
          "Fachliche Begründung wichtig",
          "Individuelle Faktoren beachten"
        ]
      }
    ]
  },
  // MODUL 51: Dokumentation als Beweisführung
  51: {
    title: "Dokumentation als Beweisführung",
    icon: "📝",
    description: "Die vier Funktionen und das Dokumentationsaudit.",
    slides: [
      {
        id: "pdl_dokumentation_funktionen",
        title: "Die vier Funktionen der Dokumentation",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/169f2363a2dd8eb1a42f4378dadf30dd10b50c20287b3e51786b22616e5539ed.png",
        explanation: "Das Leitprinzip: Was nicht dokumentiert ist, gilt als nicht getan! Die 4 Funktionen: 1. Pflegefachlich (Kontinuität, Koordination, Transparenz), 2. Rechtlich (einziger Nachweis bei Streitigkeiten), 3. Prüfungsrelevant (MD bewertet nach Dokumentation), 4. Haftungsrechtlich (Beweislastumkehr bei Lücken). Die 5 häufigsten MD-Mängel: Keine Individualisierung, fehlende Evaluation, Maßnahmen ohne Zielbezug, unklare Verantwortlichkeiten, unvollständige Risikoeinschätzungen.",
        keyPoints: [
          "4 Funktionen verstehen",
          "Leitprinzip beachten",
          "5 häufigste MD-Mängel",
          "Individualisierung kritisch"
        ]
      },
      {
        id: "pdl_dokumentationsaudit",
        title: "Das Dokumentationsaudit",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/169f2363a2dd8eb1a42f4378dadf30dd10b50c20287b3e51786b22616e5539ed.png",
        explanation: "5 Schritte zum systematischen Audit: 1. Planen (Zeit, Umfang, Verantwortliche), 2. Checkliste anwenden (strukturiert prüfen), 3. Ergebnisse auswerten (Muster erkennen), 4. Feedback geben (konstruktiv, lösungsorientiert), 5. Nachprüfung (Maßnahmen kontrollieren). Plausibilitätskriterien: Widerspruchsfreiheit, Aktualität, Vollständigkeit, logische Verbindung. Vierteljährlicher Rhythmus empfohlen!",
        keyPoints: [
          "5 Audit-Schritte",
          "4 Plausibilitätskriterien",
          "Vierteljährlich durchführen",
          "Muster, nicht Schuldige suchen"
        ]
      }
    ]
  },
  // MODUL 52: Fehlerkultur und CIRS
  52: {
    title: "Fehlerkultur und CIRS",
    icon: "🔄",
    description: "Verstehen statt Sanktionieren und lernende Organisation.",
    slides: [
      {
        id: "pdl_fehlerarten",
        title: "Drei Fehlerarten verstehen",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/fcfd29238175d4b9c6296709b547db86d93cdae8440ac8704659f9aa5f6eaf64.png",
        explanation: "1. Aktive Fehler: Direkte Handlungsfehler durch Pflegende (oft fälschlich nur Einzelpersonen zugeschrieben). 2. Latente Systemfehler: Verborgene strukturelle Schwächen (Personalmangel, unklare Zuständigkeiten, veraltete Listen). 3. Organisationale Fehler: Fehler auf Führungsebene mit größtem Präventionspotenzial. Lernkultur fragt: 'Was ist passiert und warum?' statt 'Wer ist schuld?' – fördert offene Kommunikation und kontinuierliche Verbesserung.",
        keyPoints: [
          "3 Fehlerarten unterscheiden",
          "Systemisch denken",
          "Lern- statt Schuldkultur",
          "Strukturen gestalten"
        ]
      },
      {
        id: "pdl_cirs_system",
        title: "CIRS implementieren",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/fcfd29238175d4b9c6296709b547db86d93cdae8440ac8704659f9aa5f6eaf64.png",
        explanation: "CIRS = Critical Incident Reporting System: Anonymes Meldesystem für kritische Ereignisse und Beinahe-Unfälle. Anonymität ist der Kern! Nur so werden Mitarbeitende melden. Der CIRS-Prozess: 1. Anonym melden, 2. Systemisch analysieren, 3. Maßnahmen ableiten, 4. Rückmelden. Vertrauenskultur braucht: Sanktionsfreiheit, sichtbare Reaktionen, Vorbildfunktion der Führung. Reihenfolge bei Fehlern: Sicherheit → Analysieren → Kommunizieren → Maßnahmen.",
        keyPoints: [
          "Anonymität ist Kern",
          "4 CIRS-Schritte",
          "Vertrauenskultur aufbauen",
          "Richtige Reihenfolge"
        ]
      }
    ]
  },
  // MODUL 53: MD-Prüfung und Vorbereitungskonzept
  53: {
    title: "MD-Prüfung und Vorbereitungskonzept",
    icon: "✅",
    description: "Das 5-Bausteine-Konzept für nachhaltige Qualität.",
    slides: [
      {
        id: "pdl_md_pruefung",
        title: "Die MD-Prüfung verstehen",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/7c6303ef0dcce28f8051b14a977f2d3828616a1ac3f22c9adcd14feb802c4b61.png",
        explanation: "Die 5 Phasen der MD-Prüfung: 1. Dokumentensichtung, 2. Bewohnerinterviews, 3. Mitarbeitendenbefragung, 4. Begehung, 5. Abschlussgespräch. Neue Schwerpunkte: Risikobewertung, Ergebnisqualität (nicht nur Prozesse!), Individualität der Pflege, interne Qualitätskontrolle, Umgang mit Personalmangel. Prinzip: Was dokumentiert ist, muss gelebte Realität sein – Diskrepanzen sind häufige Kritikpunkte!",
        keyPoints: [
          "5 Prüfungsphasen",
          "5 neue Schwerpunkte",
          "Dokumentation = Realität",
          "Diskrepanzen vermeiden"
        ]
      },
      {
        id: "pdl_5_bausteine",
        title: "Das 5-Bausteine-Konzept",
        image: "https://static.prod-images.emergentagent.com/jobs/8836ab3d-3f8c-48a5-b80a-ab94d6f0231e/images/7c6303ef0dcce28f8051b14a977f2d3828616a1ac3f22c9adcd14feb802c4b61.png",
        explanation: "Die 5 Bausteine: 1. Interne Auditstruktur (Auditplan, Checklisten, Maßnahmenpläne), 2. Verantwortlichkeitsmatrix (klare Zuständigkeiten, Vertretungen), 3. Dokumentationsprüfung (Vollständigkeit, Aktualität, Konsistenz), 4. Schulungskonzept (Pflichtschulungen, Einarbeitung), 5. Maßnahmenplan bei Beanstandungen (erfassen, analysieren, umsetzen, prüfen, verankern). Kernstrategie: Nicht für die Prüfung arbeiten – strukturell sauber aufstellen! Sicherheitskultur ist Führungsqualität!",
        keyPoints: [
          "5 Bausteine umsetzen",
          "Proaktiv statt reaktiv",
          "Qualität im Alltag leben",
          "Führungsqualität zeigen"
        ]
      }
    ]
  }
};

// ===================== PDL TEIL 2 VISUAL CONTENT =====================
// Bilder für den 23-Modul-Kurs "PDL-Weiterbildung Teil 2: Abrechnung & Dokumentation"

const pdlTeil2VisualContent = {
  // MODUL 1: Einführung - Wirtschaftliche Verlustquellen
  1: {
    title: "Wirtschaftliche Verlustquellen erkennen",
    icon: "💰",
    description: "Warum viele Pflegedienste Geld verlieren und wie Sie Verlustquellen systematisch identifizieren.",
    slides: [
      {
        id: "pdl2_verlustquellen_intro",
        title: "Die fünf häufigsten Problembereiche",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/da9ebaefc3ba3e76ee20498ddc5ada41a7f192672e78849288050b07897dffd9.png",
        explanation: "Wirtschaftliche Verluste bleiben oft unbemerkt. Die fünf Hauptprobleme: 1) Suboptimale Abrechnung - kleine Fehler summieren sich. 2) Fehlende Pflegegrad-Überprüfung - Budget bleibt konstant trotz steigendem Aufwand. 3) Ungenutzte Zusatzleistungen - direkte Umsatzeinbußen. 4) Dokumentations-Abrechnungs-Lücken - Rückforderungsrisiko. 5) Fehlende Kennzahlenauswertung - Verluste bleiben unsichtbar.",
        keyPoints: [
          "Suboptimale Abrechnung",
          "Fehlende Pflegegrad-Überprüfung",
          "Ungenutzte Zusatzleistungen",
          "Dokumentations-Abrechnungs-Lücken"
        ]
      },
      {
        id: "pdl2_verlustquellen_analyse",
        title: "Potenzial: 400.000€ jährlich",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/da9ebaefc3ba3e76ee20498ddc5ada41a7f192672e78849288050b07897dffd9.png",
        explanation: "Durch systematische Optimierung aller zehn Verlustquellen können jährlich über 400.000 Euro zusätzlicher Umsatz generiert werden! Die PDL muss zwei Perspektiven vereinen: Pflegefachliche Qualität UND wirtschaftliche Effizienz. Gute Pflege und wirtschaftlicher Erfolg bedingen einander.",
        keyPoints: [
          "Zehn größte Verlustquellen identifizieren",
          "Systematische Verlustquellen-Analyse",
          "Pflegefachlich + wirtschaftlich denken",
          "Potenzial: 400.000€ pro Jahr"
        ]
      }
    ]
  },
  // MODUL 2: Leistungskombination & Pflegegrad-Management
  2: {
    title: "Leistungskombination & Pflegegrad",
    icon: "📊",
    description: "Optimale Leistungskombinationen und systematisches Pflegegrad-Management.",
    slides: [
      {
        id: "pdl2_leistungskombination",
        title: "Falsche vs. Optimale Kombination",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/608cf46641426e8864aed46c1501ed4ae8f4c1193ac94eae4cb3b94d01c6adab.png",
        explanation: "Beispielrechnung: Einzelleistungen (24,50€/Tag) vs. Kombinationsleistung (36,00€/Tag) = 11,50€ Differenz täglich = 345€/Monat = 4.140€/Jahr PRO KLIENT! Prüfen Sie systematisch: Wäre eine Kombinationslösung wirtschaftlicher? Wird Zeitvergütung korrekt genutzt? Gibt es alternative Abrechnungsmodelle?",
        keyPoints: [
          "Kombinationsleistungen prüfen",
          "11,50€ Differenz pro Tag möglich",
          "4.140€ mehr pro Klient pro Jahr",
          "Zeitvergütung optimieren"
        ]
      },
      {
        id: "pdl2_pflegegrad_management",
        title: "Pflegegrad-Höherstufung: 7.668€/Jahr",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/608cf46641426e8864aed46c1501ed4ae8f4c1193ac94eae4cb3b94d01c6adab.png",
        explanation: "Unüberprüfter Pflegegrad = wirtschaftlicher Verlust! Beispiel: Höherstufung von PG 2 (724€) auf PG 3 (1.363€) = 639€ mehr pro Monat = 7.668€ pro Jahr! Führen Sie eine monatliche Pflegegrad-Ampel: Grün = passend, Gelb = beobachten, Rot = Antrag vorbereiten.",
        keyPoints: [
          "Quartalsweise Überprüfung",
          "PG 2 → PG 3: +639€/Monat",
          "Pflegegrad-Ampel führen",
          "Dokumentation zielgerichtet vorbereiten"
        ]
      }
    ]
  },
  // MODUL 3: §37-Beratung & Zusatzleistungen
  3: {
    title: "§37-Beratung & Zusatzleistungen",
    icon: "📋",
    description: "Beratungsbesuche nach §37 und systematische Nutzung von Zusatzleistungen.",
    slides: [
      {
        id: "pdl2_beratung",
        title: "Beratungsbesuche systematisch nutzen",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/081d312e8523e06ee3ace82addd0c0137f36e8384994b3353c50f8090937bde8.png",
        explanation: "Die drei häufigsten Probleme: 1) Unregelmäßige Terminierung - Besuche werden vergessen. 2) Unvollständige Dokumentation - Rückfragen der Kassen. 3) Fehlende Nachverfolgung - Potenziale bleiben verborgen. Lösung: Automatisches Erinnerungssystem mit Termin, Dokumentation und Nachverfolgung. Bei 50 Klienten mit 3 Besuchen/Jahr: 4.500€ Jahresumsatz!",
        keyPoints: [
          "Automatisches Erinnerungssystem",
          "4 Wochen vor Fälligkeit erinnern",
          "Standardisierte Protokollvorlage",
          "4.500€ Jahresumsatz bei 50 Klienten"
        ]
      },
      {
        id: "pdl2_zusatzleistungen",
        title: "45.000€ ungenutztes Potenzial",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/081d312e8523e06ee3ace82addd0c0137f36e8384994b3353c50f8090937bde8.png",
        explanation: "Der Entlastungsbetrag von 125€/Monat bleibt bei vielen Klienten ungenutzt - direkter Umsatzverlust! Bei 30 Klienten: 30 × 125€ × 12 = 45.000€ Jahrespotenzial! Führen Sie eine monatliche Zusatzleistungs-Analyse für jeden Klienten durch.",
        keyPoints: [
          "Entlastungsbetrag: 125€/Monat",
          "Bei 30 Klienten: 45.000€/Jahr",
          "Monatliche Zusatzleistungs-Analyse",
          "Klienten aktiv beraten"
        ]
      }
    ]
  },
  // MODUL 4-10: Weitere Verlustquellen
  4: {
    title: "Fahrtzeiten & Entlastungsleistungen",
    icon: "🚗",
    description: "Korrekte Fahrzeitenberechnung und Nutzung von Entlastungsleistungen.",
    slides: [
      {
        id: "pdl2_fahrtzeiten",
        title: "62.400€ Verlust durch Fahrtzeiten",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/3b031eb8b24491c44b0f0a803bb2728164d556f3725f87f7eda266e99104f2e8.png",
        explanation: "Nicht erfasste Fahrtzeiten kosten bares Geld! 10 Min. pro Tour × 20 Touren/Woche = 800 Min./Monat = 13 Stunden × 40€ = 520€/Monat pro Mitarbeiter! Bei 10 Mitarbeitern: 62.400€ jährlicher Verlust. Lösung: Digitale Tourenplanung mit GPS-gestützter Erfassung.",
        keyPoints: [
          "13 Stunden/Monat nicht erfasst",
          "520€ Verlust pro Mitarbeiter/Monat",
          "62.400€ Jahresverlust bei 10 MA",
          "Digitale Tourenplanung nutzen"
        ]
      }
    ]
  },
  5: {
    title: "Verhinderungspflege & Pauschalen",
    icon: "💼",
    description: "Verhinderungspflege optimal nutzen und Pauschalen korrekt abrechnen.",
    slides: [
      {
        id: "pdl2_verhinderungspflege",
        title: "2.418€ Budget pro Klient",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/46ace8285dc9ae426172d8734647ef2522a27ad3defb1210f1ea544b925f6742.png",
        explanation: "Verhinderungspflege: 1.612€ + 50% Kurzzeitpflege (806€) = 2.418€ pro Jahr! Oft bleibt dieses Budget ungenutzt. Bei 40 Klienten mit 50% Nutzung: 32.240€ Jahrespotenzial = 2.687€ monatlicher Zusatzumsatz! Wichtig: Bis zu 18 Monate rückwirkend abrechnbar.",
        keyPoints: [
          "Verhinderungspflege: 1.612€/Jahr",
          "+ Kurzzeitpflege: 2.418€ gesamt",
          "18 Monate rückwirkend möglich",
          "32.240€ Jahrespotenzial bei 40 Klienten"
        ]
      }
    ]
  },
  6: {
    title: "Behandlungspflege optimieren",
    icon: "💉",
    description: "Behandlungspflege korrekt abrechnen und Investitionskostenzuschläge beantragen.",
    slides: [
      {
        id: "pdl2_behandlungspflege",
        title: "86.400€ durch Behandlungspflege",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/1bc6d569c6dda346ab30fb2e1f1dd9ac7f6ebdfe93a89d6109abe952b31e0fa8.png",
        explanation: "Behandlungspflege ist deutlich besser vergütet als Grundpflege! Bei 30 Klienten mit 2 zusätzlichen Leistungen/Tag à 4€: 240€ täglich = 7.200€ monatlich = 86.400€ jährlicher Zusatzumsatz! Regelmäßiger Austausch mit Ärzten ist essentiell.",
        keyPoints: [
          "Behandlungspflege > Grundpflege",
          "86.400€ Zusatzumsatz möglich",
          "Ärztliche Verordnungen konsequent einholen",
          "Monatliches Controlling einführen"
        ]
      }
    ]
  },
  7: {
    title: "Wirtschaftliche Kennzahlen",
    icon: "📈",
    description: "Umsatz pro Klient und Produktivstunde verstehen und steuern.",
    slides: [
      {
        id: "pdl2_kennzahlen",
        title: "Umsatz pro Klient: >1.200€",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/6225b4354024dd6ef25563f5712648d7589e5578a122b14ad910cfe379c9faf7.png",
        explanation: "Die wichtigsten KPIs: 1) Umsatz pro Klient (Ziel: >1.200€). 2) Personalkostenquote (Ziel: <70%, Warnschwelle: >75%). 3) Umsatz/Produktivstunde (Ziel: >150% der Personalkosten). 4) Entlastungsbetrag-Nutzung (Ziel: >70%). Erstellen Sie ein monatliches Dashboard!",
        keyPoints: [
          "Umsatz/Klient: Ziel >1.200€",
          "Personalkostenquote: <70%",
          "Produktivstunde: >150% Personalkosten",
          "Monatliches KPI-Dashboard"
        ]
      }
    ]
  },
  8: {
    title: "Pflegegrad-Mix & Abgleich",
    icon: "⚖️",
    description: "Pflegegrad-Mix analysieren und Dokumentation mit Abrechnung abgleichen.",
    slides: [
      {
        id: "pdl2_pflegegrad_mix",
        title: "Idealer Pflegegrad-Mix",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/af5b0f8bca637ac16413cecb6f13ecd07d9288217e16323bae608393b74995f3.png",
        explanation: "Idealer Mix (stationär): PG 1: 0-5%, PG 2: 15-25%, PG 3: 30-40%, PG 4: 20-30%, PG 5: 10-20%. Warnsignale: PG 2 über 35%, PG 3-4 unter 40% zusammen. Monatliche Stichprobe bei 10% der Klienten durchführen. Was nicht dokumentiert ist, gilt als nicht getan!",
        keyPoints: [
          "PG 3-4 sollten dominieren",
          "PG 2 über 35% = Warnsignal",
          "Monatliche 10%-Stichprobe",
          "Dokumentation = Nachweis"
        ]
      }
    ]
  },
  9: {
    title: "Rückforderungen vermeiden",
    icon: "🛡️",
    description: "Systematische Prävention von Rückforderungen.",
    slides: [
      {
        id: "pdl2_rueckforderungen",
        title: "Die drei häufigsten Gründe",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/bccaa2ff2b1aed163fd4f921355e2d025cc603a09a62e68b7085cb5172c62197.png",
        explanation: "Häufigste Rückforderungsgründe: 1) Fehlende Unterschrift - tägliche Kontrolle! 2) Maßnahme nicht dokumentiert - Echtzeitdokumentation! 3) Abgelaufene Verordnung - 4 Wochen Vorlauf-Erinnerung! Etablieren Sie ein mehrstufiges Kontrollsystem: täglich (Team), wöchentlich (PDL), monatlich (GF).",
        keyPoints: [
          "Fehlende Unterschrift vermeiden",
          "Echtzeitdokumentation einführen",
          "Verordnungen 4 Wochen vor Ablauf prüfen",
          "Mehrstufiges Kontrollsystem"
        ]
      }
    ]
  },
  10: {
    title: "KI für Abrechnungsoptimierung",
    icon: "🤖",
    description: "KI-Tools wie ChatGPT für Potenzialanalyse und Leistungsoptimierung.",
    slides: [
      {
        id: "pdl2_ki_tools",
        title: "KI sicher nutzen",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/c1ec0b1d63e2090d7a603de93241af4192a76c69380a1534585cec3252b99f09.png",
        explanation: "KI-Tools können helfen bei: Potenzialanalyse, Leistungsoptimierung, Verhandlungsvorbereitung. WICHTIG: Strenge Anonymisierung! Niemals echte Namen, Diagnosen oder komplette Datensätze eingeben. Nur anonymisierte Zahlen und Durchschnittswerte. Die fachliche Verantwortung bleibt bei Ihnen!",
        keyPoints: [
          "Niemals echte Klientennamen",
          "Nur anonymisierte Daten nutzen",
          "KI-Ergebnisse validieren",
          "Fachliche Verantwortung behalten"
        ]
      }
    ]
  },
  11: {
    title: "KI für Tourenplanung",
    icon: "🗺️",
    description: "KI-gestützte Tourenplanung und Personalkosten-Optimierung.",
    slides: [
      {
        id: "pdl2_ki_touren",
        title: "Bis zu 20% mehr Produktivzeit",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/3dd3913b91e072a5aec25b0fd8b8c8bd023247c8fb88bb822e4c038e0f2dd7e4.png",
        explanation: "KI-optimierte Tourenplanung kann bis zu 20% mehr produktive Zeit generieren! Geringere Kraftstoffkosten, höhere Mitarbeiterzufriedenheit, mehr Kapazität für neue Klienten. Nutzen Sie KI auch für strategische Personalplanung und Verhandlungsvorbereitung mit Krankenkassen.",
        keyPoints: [
          "20% mehr produktive Zeit",
          "Geringere Kraftstoffkosten",
          "Strategische Personalplanung",
          "Verhandlungsvorbereitung"
        ]
      }
    ]
  },
  12: {
    title: "Neue Erlösquellen",
    icon: "💎",
    description: "Zusatzleistungen und Spezialisierung als Erlösquellen.",
    slides: [
      {
        id: "pdl2_erloesquellen",
        title: "Spezialisierung: Premium-Erlöse",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/62468a4fc93e0c1b7d14c74e4f4c2c8a8d465fd6022d6af803bde15aeaeabb4a.png",
        explanation: "Lukrative Spezialisierungen: Intensivpflege/Beatmung (8.000-15.000€/Monat pro Klient!), Palliativversorgung (höhere Vergütung), Wundmanagement (Zusatzabrechnung), Demenzpflege (Premium-Positionierung). Vorteile: 20-50% höhere Vergütungssätze, weniger Preiswettbewerb, bessere Mitarbeiterbindung.",
        keyPoints: [
          "Intensivpflege: 8.000-15.000€/Klient",
          "20-50% höhere Vergütungssätze",
          "Weniger Preiswettbewerb",
          "Bessere Mitarbeiterbindung"
        ]
      }
    ]
  },
  13: {
    title: "Digitalisierung als Erlöstreiber",
    icon: "💻",
    description: "Digitale Lösungen zur Steigerung der wirtschaftlichen Effizienz.",
    slides: [
      {
        id: "pdl2_digitalisierung",
        title: "ROI nach 2 Monaten",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/ab51bbe47be23298cca37a936d61c42b1af1388b49bc5a81e3c1483ce6ec406a.png",
        explanation: "Digitalisierung rechnet sich schnell! Beispiel: Investment 5.000€ einmalig + 500€/Monat, Zeitersparnis 60h/Monat, Zusatzumsatz 2.700€/Monat = ROI nach 2 Monaten! Jahresgewinn: 26.400€. Mobile Pflegedokumentation spart 30 Min. pro Mitarbeiter pro Tag.",
        keyPoints: [
          "ROI nach 2 Monaten",
          "26.400€ Jahresgewinn",
          "30 Min./Tag/Mitarbeiter sparen",
          "Vollständige Leistungserfassung"
        ]
      }
    ]
  },
  // BEREICH B: QPR & DOKUMENTATION
  14: {
    title: "QPR ab 2026: Einführung",
    icon: "📜",
    description: "Die Qualitätsprüfungs-Richtlinien und neue Dokumentationspflichten.",
    slides: [
      {
        id: "pdl2_qpr_intro",
        title: "Neue Vorgaben ab 1. Juni 2026",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/b98fb93e0db7379e23d2238ff5e66c0dc604b3ffdd1cae1e90215abc9bf339e9.png",
        explanation: "QPR = Qualitätsprüfungs-Richtlinien. Ab 1. Juni 2026 gelten neue Dokumentationsvorgaben! Ziele: Bürokratie reduzieren, Pflegequalität messen, Ergebnisqualität bewerten. Paradigmenwechsel: Früher wurde geprüft, ob VIEL dokumentiert wurde. Heute: ob Pflege WIRKSAM ist. Fehlt Dokumentation, gilt Maßnahme als nicht durchgeführt!",
        keyPoints: [
          "Neue Vorgaben ab 1. Juni 2026",
          "Ergebnisorientierung statt Quantität",
          "Strukturmodell als Standard",
          "Fehlende Doku = nicht durchgeführt"
        ]
      }
    ]
  },
  15: {
    title: "SIS & Maßnahmenplanung",
    icon: "📝",
    description: "Die Strukturierte Informationssammlung und Maßnahmenplanung.",
    slides: [
      {
        id: "pdl2_sis",
        title: "Die sechs Themenfelder",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/ee6e8c5e03bada9deef18cfe0955b889823cd56231da9d64fd031f8f28b71c3b.png",
        explanation: "Das Strukturmodell hat 4 Elemente: SIS, Maßnahmenplanung, Berichteblatt, Evaluation. Die SIS umfasst 6 Themenfelder: 1) Kognition/Kommunikation, 2) Mobilität, 3) Krankheitsbezogene Anforderungen, 4) Selbstversorgung, 5) Soziale Beziehungen, 6) Wohnen/Häuslichkeit. Plus Risikoeinschätzung für Sturz, Dekubitus, Ernährung, Schmerz.",
        keyPoints: [
          "4 Elemente: SIS, Maßnahmen, Bericht, Evaluation",
          "6 Themenfelder in der SIS",
          "Risikoeinschätzung obligatorisch",
          "Individuelle Maßnahmenplanung"
        ]
      }
    ]
  },
  16: {
    title: "Berichteblatt & Evaluation",
    icon: "📊",
    description: "Das Berichteblatt und die Evaluation der Pflegemaßnahmen.",
    slides: [
      {
        id: "pdl2_berichteblatt",
        title: "Nur Abweichungen dokumentieren",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/13bc62d496eb1abe78810704597a4fdb0173818c723331d78ecbbad23e50a05e.png",
        explanation: "Das Berichteblatt dokumentiert nur Abweichungen vom Plan! Evaluation erfolgt prozessbegleitend und anlassbezogen. Wichtig: Alle Themenfelder und Risiken in der Evaluation abdecken. Das Baukastenprinzip erlaubt Routineabläufe als Standard, nur Besonderes wird extra dokumentiert.",
        keyPoints: [
          "Nur Abweichungen dokumentieren",
          "Evaluation prozessbegleitend",
          "Alle Themenfelder abdecken",
          "Baukastenprinzip nutzen"
        ]
      }
    ]
  },
  17: {
    title: "Risikoeinschätzung",
    icon: "⚠️",
    description: "Die neue QPR-Anforderung zur Risikoeinschätzung.",
    slides: [
      {
        id: "pdl2_risiko",
        title: "Fachlichkeit vor Skalen",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/15ecaf5f446eb4fa1cc3d231a3b6ab605ec204ab5476e24a0117211e068a76e6.png",
        explanation: "Die QPR prüft: Wurden alle relevanten Risiken identifiziert? Gibt es präventive Maßnahmen? Der Fokus liegt auf fachlicher Einschätzung, nicht auf Skalen! Die vier Hauptrisiken: Sturz, Dekubitus, Mangelernährung, Schmerz. Regelmäßige Überprüfung bei Veränderungen.",
        keyPoints: [
          "Fachliche Einschätzung wichtiger als Skalen",
          "4 Hauptrisiken: Sturz, Dekubitus, Ernährung, Schmerz",
          "Präventive Maßnahmen dokumentieren",
          "Regelmäßige Überprüfung"
        ]
      }
    ]
  },
  18: {
    title: "Individuelle Pflegeplanung",
    icon: "👤",
    description: "Individuelle Pflegeplanung nach den neuen QPR-Anforderungen.",
    slides: [
      {
        id: "pdl2_pflegeplanung",
        title: "Keine Standardtexte!",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/ecb34e0e75980264531401a3dd1ca9e7ce89126d698014d2a5213b342e563e9b.png",
        explanation: "Die QPR prüft: Spiegelt die Maßnahmenplanung die individuelle Situation? Keine Standardtexte! Bezug zur SIS herstellen. Ressourcen und Gewohnheiten berücksichtigen. Die Planung muss nachvollziehbar begründet sein und regelmäßig aktualisiert werden.",
        keyPoints: [
          "Individuelle Situation abbilden",
          "Keine Standardtexte verwenden",
          "Ressourcen berücksichtigen",
          "Regelmäßige Aktualisierung"
        ]
      }
    ]
  },
  19: {
    title: "Nachvollziehbarkeit",
    icon: "🔍",
    description: "Nachvollziehbarkeit und Ergebnisqualität in der Dokumentation.",
    slides: [
      {
        id: "pdl2_nachvollziehbarkeit",
        title: "Der rote Faden",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/52ba19104d6e627abbf8c92c336c771975a238161bd4d5190748d92ce982282a.png",
        explanation: "Die QPR prüft den 'roten Faden': Ist der Pflegeprozess lückenlos nachvollziehbar? Von der Einschätzung über die Planung zur Durchführung und Evaluation. Ergebnisqualität zeigt sich in stabilen oder verbesserten Indikatoren. Dokumentieren Sie Veränderungen zeitnah!",
        keyPoints: [
          "Roter Faden durch alle Dokumente",
          "Pflegeprozess lückenlos",
          "Ergebnisqualität messbar machen",
          "Veränderungen zeitnah dokumentieren"
        ]
      }
    ]
  },
  20: {
    title: "Digitale Dokumentation",
    icon: "📱",
    description: "Digitalisierung der Pflegedokumentation.",
    slides: [
      {
        id: "pdl2_digital_doku",
        title: "Vorteile digitaler Systeme",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/5a3653281ee04a3b607fa50ce1a9456f74e02e250c3d86f96cd1297fb7f06a61.png",
        explanation: "Digitale Dokumentation bietet: Zeitersparnis durch Vorlagen, Lesbarkeit, Vollständigkeitsprüfung, Auswertungsmöglichkeiten. Wichtig bei der Auswahl: Strukturmodell-Kompatibilität, Mobilfähigkeit, Schnittstellen, Schulungsangebot. Rechtliche Anforderungen beachten: Authentizität, Integrität, Verfügbarkeit.",
        keyPoints: [
          "Zeitersparnis durch Vorlagen",
          "Vollständigkeitsprüfung automatisch",
          "Mobil dokumentieren",
          "Rechtliche Anforderungen beachten"
        ]
      }
    ]
  },
  21: {
    title: "Rechtssichere Dokumentation",
    icon: "⚖️",
    description: "Kriterien und Beispiele für rechtssichere Dokumentation.",
    slides: [
      {
        id: "pdl2_rechtssicher",
        title: "Die 7 Kriterien",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/f45574fb0f26f2a6d7e1b24a3e8f8e1d87cc89e36a4c9ae1fbf4b1e12a10bdb5.png",
        explanation: "Rechtssichere Dokumentation: 1) Zeitnah - direkt nach Maßnahme, 2) Vollständig - alle relevanten Infos, 3) Richtig - faktisch korrekt, 4) Nachvollziehbar - logisch aufgebaut, 5) Fälschungssicher - keine nachträglichen Änderungen, 6) Lesbar - eindeutig entzifferbar, 7) Unterschrieben - mit Handzeichen versehen.",
        keyPoints: [
          "Zeitnah dokumentieren",
          "Vollständig und richtig",
          "Nachvollziehbar und lesbar",
          "Fälschungssicher und unterschrieben"
        ]
      }
    ]
  },
  22: {
    title: "Dokumentation als Führungsaufgabe",
    icon: "👔",
    description: "Dokumentationsqualität als Führungsverantwortung der PDL.",
    slides: [
      {
        id: "pdl2_fuehrung_doku",
        title: "Stichprobenprüfung einführen",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/a3a8bb799461b67f155a8d66ea6b98c9cb53a393f07b97afb064762c3e23db.png",
        explanation: "Die PDL trägt die Gesamtverantwortung für Dokumentationsqualität! Aufgaben: Stichprobenprüfung etablieren, Schulungen organisieren, Qualitätszirkel moderieren, Standards definieren. Monatliche Stichprobe: 10-15% der Dokumentationen prüfen, Ergebnisse mit Team besprechen.",
        keyPoints: [
          "Gesamtverantwortung der PDL",
          "Monatliche 10-15% Stichprobe",
          "Regelmäßige Schulungen",
          "Qualitätszirkel etablieren"
        ]
      }
    ]
  },
  23: {
    title: "Prüfungsvorbereitung & Fazit",
    icon: "🎯",
    description: "Zusammenfassung und Prüfungsvorbereitung für Teil 2.",
    slides: [
      {
        id: "pdl2_fazit",
        title: "Die nächsten Schritte",
        image: "https://static.prod-images.emergentagent.com/jobs/670ff0a7-e02b-419e-be4c-6c445767653a/images/dda0f4e51e02fc95c36a9a4f6fd62f0e0c4df26ba3295eb2b80c3f18a4c50e49.png",
        explanation: "Herzlichen Glückwunsch zum Abschluss von Teil 2! Ihre nächsten Schritte: 1) Bestandsaufnahme - Wo stehen Sie heute? 2) Priorisierung - Was muss zuerst verbessert werden? 3) Maßnahmenplan - Wer macht was bis wann? 4) Schulung - Team vorbereiten. 5) Umsetzung - Schritt für Schritt implementieren.",
        keyPoints: [
          "Bestandsaufnahme durchführen",
          "Prioritäten setzen",
          "Maßnahmenplan erstellen",
          "Team schulen und umsetzen"
        ]
      }
    ]
  }
};

// ===================== WUNDEXPERTE VISUAL CONTENT =====================
const wundexperteVisualContent = {
  1: {
    title: "Anatomie und Physiologie der Haut",
    icon: "🔬",
    description: "Aufbau der Haut mit den drei Schichten: Epidermis, Dermis und Subcutis.",
    slides: [
      {
        id: "we_mod1_haut",
        title: "Die drei Hautschichten",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/c5ab5eeba3962c26edf917ab2229987f524eca4ef6827f37c0beb038832cb019.png",
        explanation: "Die Haut besteht aus drei Hauptschichten: Die Epidermis (Oberhaut) ist die äußerste Schutzschicht. Die Dermis (Lederhaut) enthält Blutgefäße, Nerven und Haarfollikel. Die Subcutis (Unterhaut) besteht aus Fettgewebe und dient als Energiespeicher und Wärmeisolierung.",
        keyPoints: [
          "Epidermis: Schutzfunktion, Zellerneuerung alle 28 Tage",
          "Dermis: Blutgefäße, Nervenendigungen, Kollagenfasern",
          "Subcutis: Fettgewebe, Wärmeisolierung",
          "Hautanhangsgebilde: Haare, Talg- und Schweißdrüsen"
        ]
      }
    ]
  },
  2: {
    title: "Wundheilungsprozesse",
    icon: "🩹",
    description: "Die drei Phasen der Wundheilung und Einflussfaktoren.",
    slides: [
      {
        id: "we_mod2_heilung",
        title: "Phasen der Wundheilung",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/7f9c5e5965aa5d9afb5f5def029d29f1b4582095a52b1d4f581f981219251a5a.png",
        explanation: "Die Wundheilung verläuft in drei Phasen: Exsudationsphase (Tag 0-4) mit Entzündung und Reinigung, Granulationsphase (Tag 4-21) mit Neubildung von Gewebe, Epithelisierungsphase (ab Tag 21) mit Hautneubildung. Bei chronischen Wunden stagniert dieser Prozess.",
        keyPoints: [
          "Exsudationsphase: Blutstillung, Entzündung, Reinigung",
          "Granulationsphase: Neue Blutgefäße, Bindegewebe",
          "Epithelisierung: Hautneubildung vom Wundrand",
          "Chronische Wunden: Stagnation in einer Phase"
        ]
      }
    ]
  },
  3: {
    title: "Wundanamnese und Dokumentation",
    icon: "📋",
    description: "MEASURE-Konzept und rechtssichere Wunddokumentation.",
    slides: [
      {
        id: "we_mod3_doku",
        title: "Systematische Wunddokumentation",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/94c588c493ac49579a761850d869e1f86f6763b9dafaa226ff5a6006dca453d5.png",
        explanation: "Das MEASURE-Konzept ist der Standard für systematische Wundbeurteilung: Measure (Messen), Exudate (Exsudat), Appearance (Aussehen), Suffering (Schmerz), Undermining (Unterminierung), Re-evaluation (Reevaluation), Edge (Wundrand). Dokumentation ist rechtlich verbindlich!",
        keyPoints: [
          "MEASURE: Systematische Wundbeurteilung",
          "Fotodokumentation mit Maßstab und Datum",
          "Was nicht dokumentiert ist, gilt als nicht durchgeführt",
          "10 Jahre Aufbewahrungspflicht"
        ]
      }
    ]
  },
  4: {
    title: "Wundarten und -ursachen",
    icon: "🔍",
    description: "Akute und chronische Wunden, TIME-Konzept.",
    slides: [
      {
        id: "we_mod4_arten",
        title: "Chronische vs. Akute Wunden",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/07a63dc16b25e47801ed09a49ba4f1053a17f471cc0229890c1ad52f346106a8.png",
        explanation: "Akute Wunden heilen in ca. 4-12 Wochen ab. Chronische Wunden zeigen keine Heilungstendenz trotz adäquater Therapie. Das TIME-Konzept hilft bei der Behandlung: Tissue (Gewebe), Inflammation/Infection, Moisture Balance, Edge.",
        keyPoints: [
          "Akute Wunden: Schnitt, Riss, Schürfwunden",
          "Chronische Wunden: Ulcus cruris, Dekubitus, DFS",
          "TIME-Konzept für strukturierte Therapie",
          "Ursachenforschung ist entscheidend"
        ]
      }
    ]
  },
  5: {
    title: "Wundbehandlung und -therapie",
    icon: "💊",
    description: "Moderne Wundauflagen und phasengerechte Versorgung.",
    slides: [
      {
        id: "we_mod5_therapie",
        title: "Moderne Wundauflagen",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/3ceceb2d953a0f39a35808083a09bd83a39985bb9bf5d84298e86a2e15f4fa21.png",
        explanation: "Moderne Wundauflagen unterstützen die feuchte Wundheilung. Schaumverbände für mittlere Exsudation, Hydrokolloide für leicht nässende Wunden, Alginate für stark exsudierende Wunden, Hydrogele für trockene Wunden. Die Auswahl erfolgt phasengerecht!",
        keyPoints: [
          "Schaumverbände: Mittlere Exsudation",
          "Hydrokolloide: Leicht nässende Wunden",
          "Alginate: 20-fache Saugfähigkeit",
          "Hydrogele: Feuchtigkeitsspendend für trockene Wunden"
        ]
      }
    ]
  },
  6: {
    title: "Ulcus cruris - Diagnostik und Therapie",
    icon: "🦵",
    description: "ABI-Messung und Kompressionstherapie.",
    slides: [
      {
        id: "we_mod6_ulcus",
        title: "Venöses vs. Arterielles Ulcus",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/bcbb742dfffb9a06264c8acc9a4845143ca1584482d918fed8331abb6e2b21a0.png",
        explanation: "Der ABI (Knöchel-Arm-Index) ist entscheidend für die Therapiewahl! Normal: 0,9-1,3. Bei ABI unter 0,5 ist Kompression kontraindiziert (kritische Ischämie). Das venöse Ulkus wird mit Kompressionstherapie behandelt, das arterielle erfordert Revaskularisation.",
        keyPoints: [
          "ABI-Messung vor jeder Kompression!",
          "Venöses Ulkus: Kompression = Goldstandard",
          "Arterielles Ulkus: KEINE Kompression bei ABI < 0,5",
          "Mischulkus: Angepasste Therapie"
        ]
      }
    ]
  },
  7: {
    title: "Diabetisches Fußsyndrom",
    icon: "🦶",
    description: "Wagner-Klassifikation und die 5 Säulen der DFS-Behandlung.",
    slides: [
      {
        id: "we_mod7_dfs",
        title: "Diabetisches Fußsyndrom",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/b73b99fcdaf985980f5e034412c4b43ea575d1c2783156f26f046ae7d8ff1ea4.png",
        explanation: "Das DFS entsteht durch Neuropathie und Angiopathie. Die 5 Säulen der Behandlung: 1) Stoffwechseloptimierung, 2) Druckentlastung (WICHTIGSTE Maßnahme!), 3) Infektionsbehandlung, 4) Gefäßsanierung, 5) Lokale Wundbehandlung. Prävention durch tägliche Fußinspektion!",
        keyPoints: [
          "Neuropathie: Verlust der Schmerzwahrnehmung",
          "Druckentlastung: Wichtigste Maßnahme!",
          "Wagner-Klassifikation: Grad 0-5",
          "Prävention: Tägliche Fußinspektion"
        ]
      }
    ]
  },
  8: {
    title: "Dekubitus-Prophylaxe und -Therapie",
    icon: "🛏️",
    description: "EPUAP-Klassifikation und evidenzbasierte Prävention.",
    slides: [
      {
        id: "we_mod8_dekubitus",
        title: "Dekubitus-Kategorien",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/378d51973be5e85ad29596f75dfa79aceea3010629e570a146cf7eb6844d315c.png",
        explanation: "Dekubitus entsteht durch anhaltenden Druck auf Knochen-Hautbrücken. EPUAP-Kategorien: I = Nicht wegdrückbare Rötung, II = Teilverlust der Haut, III = Vollständiger Hautverlust, IV = Freiliegender Knochen. Prävention durch Lagerung alle 2-4 Stunden!",
        keyPoints: [
          "Kategorie I: Nicht wegdrückbare Rötung",
          "Kategorie IV: Freiliegender Knochen",
          "Lagerung alle 2-4 Stunden",
          "30°-Schräglagerung bevorzugen"
        ]
      }
    ]
  },
  9: {
    title: "Wundinfektion und Biofilm",
    icon: "🦠",
    description: "Infektionszeichen erkennen und Biofilm-Management.",
    slides: [
      {
        id: "we_mod9_infektion",
        title: "Biofilm und Infektion",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/b5e3bbca9c3de18e6bf5ef52445354cd8f496469bb5c2689110713f133becf87.png",
        explanation: "80% aller chronischen Wunden haben Biofilm! Dieser regeneriert sich schnell und erhöht die Antibiotikaresistenz. Biofilm-Management erfordert mechanisches Débridement alle 48-72 Stunden, antimikrobielle Wundauflagen und antiseptische Spülung.",
        keyPoints: [
          "80% chronischer Wunden haben Biofilm",
          "Débridement alle 48-72 Stunden",
          "Antiseptika: PHMB, Octenidin, Silber",
          "5 Infektionszeichen: Rubor, Calor, Tumor, Dolor, Functio laesa"
        ]
      }
    ]
  },
  10: {
    title: "Wundschmerz-Management",
    icon: "💉",
    description: "Schmerzassessment und WHO-Stufenschema.",
    slides: [
      {
        id: "we_mod10_schmerz",
        title: "Schmerzmanagement",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/33fe4f299fef25ad34302f1f0b29a00d9a453ed1e313ef886cecdc5106f62aba.png",
        explanation: "80% der Wundpatienten haben Schmerzen! Schmerzerfassung mit NRS (0-10). WHO-Stufenschema: Stufe I = Nicht-Opioide, Stufe II = Schwache Opioide + Stufe I, Stufe III = Starke Opioide + Stufe I. Topische Analgesie 30-60 Minuten vor Verbandwechsel!",
        keyPoints: [
          "NRS: Numerische Rating-Skala 0-10",
          "WHO-Stufenschema beachten",
          "Analgetika 30-60 Min. vor Verbandwechsel",
          "Atraumatische Materialien verwenden"
        ]
      }
    ]
  },
  11: {
    title: "Rechtliche und ethische Aspekte",
    icon: "⚖️",
    description: "Dokumentationspflichten, Datenschutz und ethische Fragestellungen.",
    slides: [
      {
        id: "we_mod11_recht",
        title: "Recht und Ethik in der Wundversorgung",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/0295d07cecf6567bc25afb7eb46a25ccec7e9f6fd73eb4a2a30d6d8639d86f91.png",
        explanation: "Dokumentation ist rechtlich verbindlich - was nicht dokumentiert ist, gilt als nicht durchgeführt! Wundfotos sind hochsensible Daten nach DSGVO. Patientenautonomie respektieren, auch wenn Entscheidungen medizinisch nicht optimal erscheinen. Ethikkomitees können bei Dilemmata beraten.",
        keyPoints: [
          "DSGVO bei Wundfotografie beachten",
          "Informierte Einwilligung vor Fotos",
          "10 Jahre Aufbewahrungspflicht",
          "Patientenautonomie respektieren"
        ]
      }
    ]
  },
  12: {
    title: "Qualitätsmanagement",
    icon: "✅",
    description: "Standards, Zertifizierungen und PDCA-Zyklus.",
    slides: [
      {
        id: "we_mod12_qm",
        title: "Qualitätsmanagement in der Wundversorgung",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/5d8200339e92fe38ca9bc927c1abbfd7e87a42be47684a8698c2b0d005a33995.png",
        explanation: "PDCA-Zyklus für kontinuierliche Verbesserung: Plan (Planen), Do (Umsetzen), Check (Überprüfen), Act (Handeln). Wichtige externe Institutionen für Leitlinien: AWMF (national) und EWMA (europäisch). Interne Audits alle 3-6 Monate durchführen. Eine anerkannte Weiterbildung dient als Qualitätsnachweis.",
        keyPoints: [
          "PDCA: Plan-Do-Check-Act",
          "AWMF und EWMA als Leitlinien-Geber",
          "Interne Audits alle 3-6 Monate",
          "Anerkannte Weiterbildung als Qualitätsnachweis"
        ]
      }
    ]
  },
  13: {
    title: "Fallbeispiele und Prüfungsvorbereitung",
    icon: "🎓",
    description: "Praktische Fallstudien und Tipps für die Abschlussprüfung.",
    slides: [
      {
        id: "we_mod13_pruefung",
        title: "Fallbeispiele und Prüfung",
        image: "https://static.prod-images.emergentagent.com/jobs/604be80a-f5e7-4126-8d26-64fd51230b9d/images/6af6c6102110327caec23f3f5243c1ca529e3aad87e8aadb8f56870a2ac8e0b1.png",
        explanation: "Typische Fallbeispiele: Diabetisches Fußulkus (Druckentlastung!), Venöses Ulkus (Kompression!), Dekubitus (Lagerung!). Prüfungstipps: ABI immer vor Kompression messen, Kontraindikationen kennen, phasengerechte Versorgung begründen. Viel Erfolg bei Ihrer Abschlussprüfung!",
        keyPoints: [
          "DFS: Druckentlastung ist entscheidend",
          "Venöses Ulkus: Kompression = Goldstandard",
          "ABI vor JEDER Kompression!",
          "Viel Erfolg bei der Abschlussprüfung!"
        ]
      }
    ]
  }
};

// ===================== MAIN COMPONENT =====================

const ModuleVisualLearning = ({ moduleOrder = 1, courseId, authHeader = {}, onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showKeyPoints, setShowKeyPoints] = useState(false);
  const audioRef = useRef(null);

  // Select content based on course type - ONLY show content for courses that have it
  const isWundmanagementCourse = courseId === 'course-wundmanagement';
  const isLGCourse = courseId === 'course-behandlungspflege-lg';
  const isWundexperteCourse = courseId === 'course-weiterbildung-wundexperte';
  const isPflegefachkraefteCourse = courseId === 'course-wundmanagement-pflegefachkraefte';
  const isPDLCourse = courseId === 'course-pdl-weiterbildung-teil1';
  const isPDLTeil2Course = courseId === 'course-pdl-weiterbildung-teil2';
  
  // These courses have visual content
  const hasVisualContent = isWundmanagementCourse || isLGCourse || isPflegefachkraefteCourse || isPDLCourse || isPDLTeil2Course || isWundexperteCourse;
  
  // Select the correct content source - each course has its OWN visual content
  let contentSource = null;
  if (isLGCourse) {
    contentSource = lgVisualContent;
  } else if (isPflegefachkraefteCourse) {
    // Pflegefachkräfte has its OWN dedicated visual content
    contentSource = pflegefachkraefteVisualContent;
  } else if (isPDLCourse) {
    // PDL Teil 1 has its OWN dedicated visual content
    contentSource = pdlVisualContent;
  } else if (isPDLTeil2Course) {
    // PDL Teil 2 has its OWN dedicated visual content
    contentSource = pdlTeil2VisualContent;
  } else if (isWundexperteCourse) {
    // Wundexperte has its OWN dedicated visual content
    contentSource = wundexperteVisualContent;
  } else if (isWundmanagementCourse) {
    contentSource = moduleVisualContent;
  }
  
  const moduleContent = contentSource ? (contentSource[moduleOrder] || contentSource[1]) : null;
  const slides = moduleContent?.slides || [];
  const currentSlideData = slides[currentSlide];

  useEffect(() => {
    setCurrentSlide(0);
    setShowKeyPoints(false);
    setIsPlaying(false);
  }, [moduleOrder]);

  const speakText = async (text) => {
    if (isMuted || !text) return;
    
    setIsSpeaking(true);
    try {
      const response = await axios.post(
        `${API}/tts/base64`,
        { text, voice: "nova" },
        authHeader
      );
      
      const audioSrc = `data:audio/mpeg;base64,${response.data.audio}`;
      if (audioRef.current) {
        audioRef.current.src = audioSrc;
        await audioRef.current.play();
        
        audioRef.current.onended = () => {
          setIsSpeaking(false);
        };
      }
    } catch (error) {
      console.error("TTS error:", error);
      setIsSpeaking(false);
    }
  };

  const startExplanation = () => {
    setIsPlaying(true);
    if (currentSlideData) {
      speakText(currentSlideData.explanation);
    }
  };

  const nextSlide = () => {
    setShowKeyPoints(false);
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
      if (isPlaying && slides[currentSlide + 1]) {
        setTimeout(() => speakText(slides[currentSlide + 1].explanation), 500);
      }
    } else if (onComplete) {
      onComplete();
    }
  };

  const prevSlide = () => {
    setShowKeyPoints(false);
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  if (!currentSlideData) {
    return (
      <div className="bg-cream-100 rounded-2xl p-10 border border-cream-300 text-center">
        <p className="font-mono text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.24em', color: '#8F650F' }}>
          {hasVisualContent ? 'Inhalte werden geladen …' : 'Keine Bilder verfügbar'}
        </p>
        {!hasVisualContent && (
          <p className="font-display italic text-[15px] text-sand-600 mt-4 max-w-md mx-auto">
            Bildliche Darstellungen gibt es nur für die Kurse „Wundmanagement" und „Behandlungspflege LG1/LG2".
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-cream-100 rounded-2xl border border-cream-300 overflow-hidden" data-testid="module-visual-learning">
      {/* Header */}
      <div className="px-6 py-4 border-b border-cream-300">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-terra flex items-center gap-2">
              <span className="text-2xl">{moduleContent.icon}</span>
              {moduleContent.title}
            </h3>
            <p className="text-sm text-sand-500">
              Bild {currentSlide + 1} von {slides.length} • {moduleContent.description}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMuted(!isMuted)}
              className={`p-2 rounded-lg transition-colors ${
                isMuted ? 'bg-red-500/20 text-red-400' : 'bg-cream-300 text-sand-500 hover:text-terra'
              }`}
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row">
        {/* Left - Image */}
        <div className="flex-1 lg:w-2/3 p-6">
          <div className="relative bg-cream-100 rounded-xl overflow-hidden shadow-2xl">
            {!isPlaying && (
              <div 
                className="absolute inset-0 flex items-center justify-center bg-black/60 cursor-pointer group z-10"
                onClick={startExplanation}
              >
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-terra flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform mx-auto mb-4">
                    <Play className="w-10 h-10 text-sand-800 ml-1" fill="#0A0A0A" />
                  </div>
                  <p className="text-white text-lg font-medium">Erklärung starten</p>
                  <p className="text-sand-500 text-sm">Die Dozentin erklärt dir das Bild.</p>
                </div>
              </div>
            )}
            <img 
              src={currentSlideData.image} 
              alt={currentSlideData.title}
              className="w-full h-auto"
            />
          </div>
          
          {/* Image Title */}
          <div className="mt-4 text-center">
            <h4 className="text-xl font-bold text-terra">{currentSlideData.title}</h4>
          </div>
        </div>

        {/* Right — Die Dozentin erklärt */}
        <div className="lg:w-1/3 border-l border-cream-300 flex flex-col">
          {/* Erklärung — Die Dozentin */}
          <div className="flex-1 p-5 overflow-y-auto">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-full flex items-center justify-center font-display text-[15px] flex-shrink-0" style={{ backgroundColor: '#E07A5F', color: '#F5F0EA' }}>DH</span>
              <span className="font-mono text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.22em', color: '#8F650F' }}>Die Dozentin erklärt</span>
            </div>
            <div className="pl-4" style={{ borderLeft: '2px solid #D89232' }}>
              <p className={`text-sand-800 leading-relaxed text-sm ${isSpeaking ? 'animate-pulse' : ''}`}>
                {isPlaying ? currentSlideData.explanation : `Willkommen zu „${moduleContent.title}". Klicke auf Play, um zu starten.`}
              </p>
            </div>

            {/* Key Points */}
            {isPlaying && (
              <div className="mt-4">
                <button
                  onClick={() => setShowKeyPoints(!showKeyPoints)}
                  className="w-full flex items-center justify-between p-3 bg-cream-300 rounded-lg text-sand-500 hover:text-terra transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Kernpunkte anzeigen
                  </span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${showKeyPoints ? 'rotate-90' : ''}`} />
                </button>
                
                {showKeyPoints && (
                  <div className="mt-3 space-y-2">
                    {currentSlideData.keyPoints.map((point, idx) => (
                      <div 
                        key={idx}
                        className="flex items-start gap-3 p-3 bg-cream-200 rounded-lg"
                      >
                        <div className="w-6 h-6 rounded-full bg-terra text-sand-800 flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {idx + 1}
                        </div>
                        <p className="text-sm text-sand-500">{point}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Replay Button */}
            {isPlaying && (
              <button
                onClick={() => speakText(currentSlideData.explanation)}
                disabled={isSpeaking}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-cream-300 rounded-lg text-sand-500 hover:text-terra disabled:opacity-50 transition-colors"
              >
                <Volume2 className="w-4 h-4" />
                {isSpeaking ? 'Spricht...' : 'Erklärung wiederholen'}
              </button>
            )}
          </div>

          {/* Slide Dots */}
          <div className="p-4 border-t border-cream-300">
            <div className="flex items-center justify-center gap-2 mb-3">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentSlide(idx);
                    setShowKeyPoints(false);
                    if (isPlaying) {
                      setTimeout(() => speakText(slides[idx].explanation), 300);
                    }
                  }}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    idx === currentSlide ? 'bg-terra' : 'bg-cream-300 hover:bg-cream-300'
                  }`}
                />
              ))}
            </div>
            <p className="text-xs text-sand-500 text-center">
              Bild {currentSlide + 1} von {slides.length}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="px-6 py-4 border-t border-cream-300 flex items-center justify-between">
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="flex items-center gap-2 px-4 py-2 bg-cream-300 text-sand-500 rounded-lg hover:text-terra disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Zurück
        </button>

        <div className="flex items-center gap-1">
          {slides.map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx <= currentSlide ? 'bg-terra' : 'bg-cream-300'
              }`}
            />
          ))}
        </div>

        <button
          onClick={nextSlide}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-terra to-terra-dark text-sand-800 rounded-lg font-medium hover:opacity-90 transition-opacity"
        >
          {currentSlide === slides.length - 1 ? 'Abschließen' : 'Weiter'}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Module-specific hint */}
      <div className="px-6 pb-4">
        <div className="bg-terra/10 rounded-xl p-4 border border-terra/30">
          <div className="flex items-start gap-3">
            <BookOpen className="w-5 h-5 text-terra flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="font-semibold text-terra text-sm">Lernhinweis für Modul {moduleOrder}</h5>
              <p className="text-sand-500 text-xs mt-1">
                {isLGCourse ? (
                  // LG Course hints
                  <>
                    {moduleOrder === 1 && "Die Flüssigkeitsbilanz ist besonders bei herzkranken Patienten wichtig!"}
                    {moduleOrder === 2 && "Blutzuckerwerte immer dokumentieren - Hypo- und Hyperglykämie erkennen!"}
                    {moduleOrder === 3 && "Üben Sie die Injektionstechnik mit einer Übungspuppe!"}
                    {moduleOrder === 4 && "Achten Sie auf Zeichen der Mangelernährung bei älteren Patienten!"}
                    {moduleOrder === 5 && "Dekubitus-Prävention ist wichtiger als Behandlung!"}
                    {moduleOrder === 6 && "Die richtige Grundlage für das richtige Hautproblem wählen!"}
                    {moduleOrder === 7 && "Warme Hände fördern das Wohlbefinden des Patienten!"}
                    {moduleOrder === 8 && "Hygiene am Auge ist besonders wichtig - Infektionsrisiko!"}
                    {moduleOrder === 9 && "SP-Katheter täglich kontrollieren - Infektionszeichen früh erkennen!"}
                    {moduleOrder === 10 && "Intimsphäre wahren - Würde des Patienten beachten!"}
                    {moduleOrder === 11 && "Kompressionsstrümpfe morgens anlegen, bevor der Patient aufsteht!"}
                    {moduleOrder === 12 && "Nach Kortison-Inhalation immer Mund ausspülen!"}
                    {moduleOrder === 13 && "Wechselwirkungen sind bei älteren Patienten besonders häufig!"}
                    {moduleOrder === 14 && "Die 6-R-Regel ist Ihr bester Schutz vor Fehlern!"}
                    {moduleOrder === 15 && "In Notfällen: Ruhe bewahren und strukturiert handeln!"}
                    {moduleOrder === 16 && "Sie haben es geschafft - viel Erfolg bei der Prüfung!"}
                  </>
                ) : (
                  // Wundmanagement hints
                  <>
                    {moduleOrder === 1 && "Prägen Sie sich die Wundgrade ein - sie sind die Grundlage für alle weiteren Module!"}
                    {moduleOrder === 2 && "Merken Sie sich besonders § 630h BGB - Beweislastumkehr ist DAS Thema für Leitungskräfte!"}
                    {moduleOrder === 3 && "Das RACI-Modell sollten Sie für Ihre Einrichtung anpassen können."}
                    {moduleOrder === 4 && "Nutzen Sie die Checklisten für Ihre eigenen internen Audits!"}
                    {moduleOrder === 5 && "Berechnen Sie die Kosten für Ihre Einrichtung - Sie werden überrascht sein!"}
                    {moduleOrder === 6 && "Planen Sie nach diesem Modul Ihre erste Teamschulung!"}
                    {moduleOrder === 7 && "Prüfen Sie Ihre aktuelle Dokumentation gegen die DSGVO-Anforderungen!"}
                    {moduleOrder === 8 && "Jetzt sind Sie bereit, Ihr eigenes Wundmanagement-Konzept zu erstellen!"}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} />
    </div>
  );
};

export default ModuleVisualLearning;
